import { API_ENDPOINTS } from "./endpoints";
import { HttpClient } from "./http-client";
import { replaceConsoleError } from "../Utils/toastNotifications";

class Client {
  users = {
    list: () => HttpClient.get(API_ENDPOINTS.USERS_LIST), // Alias for consistency with other modules
    publicList: (input) => HttpClient.get(API_ENDPOINTS.USERS_LIST, input),
    login: (input) => HttpClient.post(API_ENDPOINTS.USERS_LOGIN, input),
    internalLogin: (input) =>
      HttpClient.post(API_ENDPOINTS.USERS_INTERNAL_LOGIN, input),
    updateGoogle: (input) =>
      HttpClient.post(API_ENDPOINTS.USERS_UPDATE_GOOGLE, input, {
        ignoreAuthError: true,
      }),
    dashboard: () => HttpClient.get(API_ENDPOINTS.USERS_DASHBOARD),
    register: (input) => HttpClient.post(API_ENDPOINTS.USERS_REGISTER, input),
    fetchUser: () => HttpClient.get(API_ENDPOINTS.FETCH_USER),
    userKPI: () => HttpClient.get(API_ENDPOINTS.USERS_KPI),
    userDetails: () => HttpClient.get(API_ENDPOINTS.USERS_DETAILS),
    userList: () => HttpClient.get(API_ENDPOINTS.USERS_LIST),

    access: (input) => HttpClient.post(API_ENDPOINTS.REQUEST_ACCESS_CRM, input),
    userAdminFetch: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.USER_ADMIN_FETCH, params),
    unifiedUserAdminFetch: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.ADMIN_USER_ADMIN_FETCH, params),

    editUserWithCredits: (input) =>
      HttpClient.post(API_ENDPOINTS.USER_ADMIN_EDIT, input),
    editCreatorWithCredits: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATOR_CREDITS_ADMIN_EDIT, input),

    fetchSharedWith: () => HttpClient.get(API_ENDPOINTS.SHARED_WITH_ADMIN),
    postClient: (input) => HttpClient.post(API_ENDPOINTS.CLIENTS_ADD, input),
    fetchClient: () => HttpClient.get(API_ENDPOINTS.CLIENTS),
    fetchZapFeed: (input) => HttpClient.get(API_ENDPOINTS.USERS_ZAPFEED, input),
    updateClient: (id, input) =>
      HttpClient.put(API_ENDPOINTS.UPDATE_CLIENT(id), input),
    fetchUserSettings: () =>
      HttpClient.get(API_ENDPOINTS.USER_SETTINGS, null, { accept404: true }),
    createUserSettings: (input) =>
      HttpClient.post(API_ENDPOINTS.USER_SETTINGS, input),
    updateUserSettings: (input) =>
      HttpClient.put(API_ENDPOINTS.USER_SETTINGS, input),

    forgotRequest: (input) =>
      HttpClient.post(API_ENDPOINTS.USERS_FORGOT, input),
    forgotAction: (input) =>
      HttpClient.post(API_ENDPOINTS.USERS_FORGOT_ACTION, input),
    updateUser: (userData) =>
      HttpClient.put(API_ENDPOINTS.USERS_DETAILS, userData),
    fetchPublicDetails: (userId) =>
      HttpClient.get(API_ENDPOINTS.FETCH_SPEC_USER(userId)),
    topUsers: () => HttpClient.get(API_ENDPOINTS.USERS_TOP_USERS),
    unilogin: (input) =>
      HttpClient.post(API_ENDPOINTS.UNI_LOGIN, input, {
        ignoreAuthError: true,
      }),
    uniloginV2: (input) =>
      HttpClient.post(API_ENDPOINTS.UNI_LOGINV2, input, {
        ignoreAuthError: true,
      }),
    linkGoogleLogin: (input) =>
      HttpClient.post(API_ENDPOINTS.LINK_GOOGLE_LOGIN, input, {
        ignoreAuthError: true,
      }),

    fetchMyTeam: () => HttpClient.get(API_ENDPOINTS.USERS_MYTEAM),
    fetchEmailSettings: () =>
      HttpClient.get(API_ENDPOINTS.EMAIL_SETTINGS, null, { accept404: true }),

    createEmailSettings: (input) =>
      HttpClient.post(API_ENDPOINTS.EMAIL_SETTINGS, input),

    updateEmailSettings: (input) =>
      HttpClient.put(API_ENDPOINTS.EMAIL_SETTINGS, input),
    fetchSupervised: () => HttpClient.get(API_ENDPOINTS.GET_SUPERVISED),
    fetchSupervisedCompanies: () =>
      HttpClient.get(API_ENDPOINTS.SUPERVISOR_COMPANIES),
    fetchSupervisedCreators: () =>
      HttpClient.get(API_ENDPOINTS.SUPERVISOR_CREATORS),
    fetchManagedCampaigns: () =>
      HttpClient.get(API_ENDPOINTS.SUPERVISOR_CAMPAIGNS),

    // Enhanced Supervisor Clients
    fetchSupervisorClients: (params) =>
      HttpClient.get(API_ENDPOINTS.SUPERVISOR_CLIENTS, params),
    toggleClientStar: (clientId) =>
      HttpClient.post(API_ENDPOINTS.TOGGLE_CLIENT_STAR(clientId)),
    toggleClientArchive: (clientId) =>
      HttpClient.post(API_ENDPOINTS.TOGGLE_CLIENT_ARCHIVE(clientId)),
    fetchSupervisorClientsArchive: (params) =>
      HttpClient.get(API_ENDPOINTS.ARCHIVED_CLIENTS, params),

    getClientDetails: (clientId) =>
      HttpClient.get(API_ENDPOINTS.GET_CLIENT_DETAILS(clientId)),

    fetchAllClients: () => HttpClient.get(API_ENDPOINTS.ALL_CLIENTS),
    getClientCampaigns: (clientId) =>
      HttpClient.get(API_ENDPOINTS.CLIENT_CAMPAIGNS(clientId)),
    getClientNotes: (clientId) =>
      HttpClient.get(API_ENDPOINTS.CLIENT_NOTES(clientId)),
    postClientNote: (clientId, payload) =>
      HttpClient.post(API_ENDPOINTS.CLIENT_NOTES(clientId), payload),
    updateClientNote: (noteId, payload) =>
      HttpClient.put(API_ENDPOINTS.CLIENT_NOTE(noteId), payload),
    deleteClientNote: (noteId) =>
      HttpClient.delete(API_ENDPOINTS.CLIENT_NOTE(noteId)),
    createClient: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATE_SUP_CLIENT, input),
    linkCreatorAccount: (input) =>
      HttpClient.post(API_ENDPOINTS.LINK_CREATOR_ACCOUNT, input),
    fetchGlobalUser: () => HttpClient.get(API_ENDPOINTS.GLOBAL_USER),
    deleteClient: (clientId) =>
      HttpClient.delete(API_ENDPOINTS.DELETE_SUP_CLIENT(clientId)),
    appleLogin: (input) =>
      HttpClient.post(API_ENDPOINTS.APPLE_LOGIN, input, {
        ignoreAuthError: true,
      }),
    appleLoginv2: (input) =>
      HttpClient.post(API_ENDPOINTS.APPLE_LOGIN2, input, {
        ignoreAuthError: true,
      }),

    confirm: (id) => HttpClient.post(API_ENDPOINTS.USER_CONFIRM(id)),
    deactivateUser: (userId) =>
      HttpClient.post(API_ENDPOINTS.USER_DEACTIVATE(userId)),

    deleteAccount: () => HttpClient.delete(API_ENDPOINTS.UNI_DELETE),

    // Notifications
    getNotifications: (params) =>
      HttpClient.get(API_ENDPOINTS.NOTIFICATIONS, params),
    markNotificationRead: (notificationId) =>
      HttpClient.put(API_ENDPOINTS.NOTIFICATION_READ(notificationId)),
    getUnreadCount: () =>
      HttpClient.get(API_ENDPOINTS.NOTIFICATIONS_UNREAD_COUNT),
    createNotification: (input) =>
      HttpClient.post(API_ENDPOINTS.POST_ALL_NOTIFICATIONS, input),

    // Device Tokens
    registerDeviceToken: (input) =>
      HttpClient.post(API_ENDPOINTS.DEVICE_TOKENS, input),

    shareClient: (clientId, data) =>
      HttpClient.post(API_ENDPOINTS.SHARE_CLIENT(clientId), data),
    unshareClient: (clientId, data) =>
      HttpClient.post(API_ENDPOINTS.UNSHARE_CLIENT(clientId), data),
    getClientSharingInfo: (clientId) =>
      HttpClient.get(API_ENDPOINTS.CLIENT_SHARING_INFO(clientId)),

    getCreatorNotes: (creatorId, params) =>
      HttpClient.get(API_ENDPOINTS.GET_CREATOR_NOTES(creatorId), params),
    postCreatorNote: (creatorId, payload) =>
      HttpClient.post(API_ENDPOINTS.POST_CREATOR_NOTE(creatorId), payload),
    updateCreatorNote: (noteId, payload) =>
      HttpClient.put(API_ENDPOINTS.UPDATE_CREATOR_NOTE(noteId), payload),
    deleteCreatorNote: (noteId) =>
      HttpClient.delete(API_ENDPOINTS.DELETE_CREATOR_NOTE(noteId)),
    getClientNoteThreads: (clientId) =>
      HttpClient.get(API_ENDPOINTS.CLIENT_NOTE_THREADS(clientId)),
    postClientNoteReply: (clientId, noteId, payload) =>
      HttpClient.post(
        API_ENDPOINTS.CLIENT_NOTE_REPLY(clientId, noteId),
        payload,
      ),

    getCreatorNoteThreads: (creatorId) =>
      HttpClient.get(API_ENDPOINTS.CREATOR_NOTE_THREADS(creatorId)),
    postCreatorNoteReply: (creatorId, noteId, payload) =>
      HttpClient.post(
        API_ENDPOINTS.CREATOR_NOTE_REPLY(creatorId, noteId),
        payload,
      ),
      
      updateClientSalesStage: (clientId, stage) =>
        HttpClient.put(
          API_ENDPOINTS.CLIENT_SALES_STAGE(clientId),
          { sales_stage: stage },
        ),
      
      getPipelineSummary: () =>
        HttpClient.get(API_ENDPOINTS.PIPELINE_SUMMARY),
  };

  twofa = {
    sendSMS: (payload, config = {}) =>
      HttpClient.post(API_ENDPOINTS.TWOFA_SMS_SEND, payload, config),
    verifySMS: (payload, config = {}) =>
      HttpClient.post(API_ENDPOINTS.TWOFA_SMS_VERIFY, payload, config),
    sendEmail: (payload = {}, config = {}) =>
      HttpClient.post(API_ENDPOINTS.TWOFA_EMAIL_SEND, payload, config),
    verifyEmail: (payload, config = {}) =>
      HttpClient.post(API_ENDPOINTS.TWOFA_EMAIL_VERIFY, payload, config),
    confirmVerification: (payload) =>
      HttpClient.post(API_ENDPOINTS.TWOFA_CONFIRM, payload),
    sendTransferOTP: (payload) =>
      HttpClient.post(API_ENDPOINTS.TWOFA_TRANSFER_SEND, payload),
  };

  campaigns = {
    launch: (input) => HttpClient.post(API_ENDPOINTS.LAUNCH_CAMPAIGN, input),

    list: () => {
      return HttpClient.get(API_ENDPOINTS.CAMPAIGNS);
    },
    details: (id) => HttpClient.get(API_ENDPOINTS.CAMPAIGNS_DETAILS(id)),
    share: (id, input) =>
      HttpClient.post(API_ENDPOINTS.CAMPAIGNS_SHARE(id), input),
    timeline_edit: (id, input) =>
      HttpClient.put(API_ENDPOINTS.CAMPAIGNS_TIMELINE(id), input),
    timeline_by_creator: (id, creatorId) =>
      HttpClient.get(
        API_ENDPOINTS.CAMPAIGNS_TIMELINE_BY_CREATOR(id, creatorId),
      ),
    listAdmin: () => HttpClient.get(API_ENDPOINTS.CAMPAIGNS_ADMIN),
    listKPI: () => HttpClient.get(API_ENDPOINTS.CAMPAIGNS_ADMIN_KPI),
    getAdminCollaborations: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.COLLABS_ADMIN, params),

    create: (input) => HttpClient.post(API_ENDPOINTS.CAMPAIGNS, input),
    copy: (input) => HttpClient.post(API_ENDPOINTS.CAMPAIGNS_COPY, input),
    fetch: (input) => HttpClient.get(API_ENDPOINTS.CAMPAIGNS_GET(input)),
    fetchReport: (input) =>
      HttpClient.get(API_ENDPOINTS.CAMPAIGNS_REPORT_GET(input)),
    update: (input) => HttpClient.post(API_ENDPOINTS.UPDATE_CAMPAIGN, input),
    updateCreatorList: (input) =>
      HttpClient.post(API_ENDPOINTS.UPDATE_CREATOR_LIST, input),
    updateCreatorStats: (input) =>
      HttpClient.post(API_ENDPOINTS.CAMPAIGN_UPDATE_CREATOR_STATS, input),
    updateManager: (input) =>
      HttpClient.post(API_ENDPOINTS.UPDATE_MANAGER, input),
    complete: (input) =>
      HttpClient.post(
        `${API_ENDPOINTS.COMPLETE_CAMPAIGN}/${input.campaignId || input.index}`,
        input.params || {},
      ),
    completeCreator: (campaignId, input) =>
      HttpClient.post(API_ENDPOINTS.COMPLETE_CREATOR(campaignId), input),
    completeVideo: (campaignId, input) =>
      HttpClient.post(API_ENDPOINTS.COMPLETE_VIDEO(campaignId), input),
    delete: (input) => HttpClient.post(API_ENDPOINTS.DELETE_CAMPAIGN, input),
    bulkArchive: (input) =>
      HttpClient.post(API_ENDPOINTS.BULK_ARCHIVE_CAMPAIGNS, input),
    checkRestrictions: (input) =>
      HttpClient.post(API_ENDPOINTS.CHECK_CAMPAIGN_RESTRICTIONS, input),
    editAdmin: (input) =>
      HttpClient.post(API_ENDPOINTS.EDIT_CAMPAIGN_ADMIN, input),
    deleteCampaignAdmin: (input) =>
      HttpClient.post(API_ENDPOINTS.DELETE_CAMPAIGN_ADMIN, input),
    fetchAdmin: (input) => HttpClient.get(API_ENDPOINTS.CAMPAIGNS_GET(input)),
    exportSheet: (input) => HttpClient.post(API_ENDPOINTS.EXPORT_SHEET, input),
    exportSheetCreators: (input) =>
      HttpClient.post(API_ENDPOINTS.EXPORT_SHEET_CREATORS, input),

    updateCampaignCreatorSpecs: (input) =>
      HttpClient.post(API_ENDPOINTS.UPDATE_CREATOR_CAMPAIGN_SPEC, input),
    updateMetaData: (input) =>
      HttpClient.post(API_ENDPOINTS.UPDATE_META_DATA, input),
    createSimilar: (input) =>
      HttpClient.post(API_ENDPOINTS.CAMPAIGNS_SIMILAR, input),
    addBonus: (id, input) =>
      HttpClient.post(API_ENDPOINTS.CAMPAIGNS_ADDBONUS(id), input),
    history: (campaignId) =>
      HttpClient.get(API_ENDPOINTS.CAMPAIGNS_HISTORY(campaignId)),
    client: (campaignId, data) =>
      HttpClient.post(API_ENDPOINTS.CAMPAIGNS_CLIENT(campaignId), data),

    // Fetch clients assigned to a campaign
    clientFetch: (campaignId) =>
      HttpClient.get(API_ENDPOINTS.CAMPAIGNS_CLIENT_FETCH(campaignId)),

    clientRemove: (campaignId, data) =>
      HttpClient.post(API_ENDPOINTS.CAMPAIGNS_CLIENT_REMOVE(campaignId), data),

    optout: (campaignId) =>
      HttpClient.post(API_ENDPOINTS.CAMPAIGNS_OPTOUT(campaignId)),
    getCreatorSpecs: ({ creator, campaignId }) =>
      HttpClient.get(API_ENDPOINTS.CAMPAIGN_HANDLER_SPECS, {
        creator,
        campaignId,
      }),
    handleNegotiationResponse: (campaignId, payload) =>
      HttpClient.post(API_ENDPOINTS.CREATOR_NEGOTIATION(campaignId), payload),

    resendOffer: (campaignId, creatorName) =>
      HttpClient.post(API_ENDPOINTS.RESEND_OFFER(campaignId), { creatorName }),
    creatorHistory: (campaignId, creatorId) =>
      HttpClient.get(
        API_ENDPOINTS.CAMPAIGNS_CREATOR_HISTORY(campaignId, creatorId),
      ),
    archive: (input) => HttpClient.post(API_ENDPOINTS.PROJECT_ARCHIVE, input),
    copyCatalog: (input) => HttpClient.post(API_ENDPOINTS.CATALOG_COPY, input),
    sendFeedback: (campaignId, payload) =>
      HttpClient.post(
        API_ENDPOINTS.CAMPAIGN_SEND_FEEDBACK(campaignId),
        payload,
      ),

    updatePricingVisibility: (campaignId, hidePricing) => {
      return HttpClient.put(
        API_ENDPOINTS.CAMPAIGNS_PRICING_VISIBILITY(campaignId),
        {
          hide_public_pricing: hidePricing,
        },
      ).then((res) => res.data);
    },

    updateClientCompany: (campaignId, payload) =>
      HttpClient.put(
        API_ENDPOINTS.CAMPAIGNS_CLIENT_COMPANY(campaignId),
        payload,
      ),

      getTracker: (campaignId) =>
  HttpClient.get(API_ENDPOINTS.CAMPAIGNS_TRACKER(campaignId), null, {
    accept404: true,
  }),

createTracker: (campaignId, data) =>
  HttpClient.post(API_ENDPOINTS.CAMPAIGNS_TRACKER(campaignId), data),

syncTracker: (campaignId) =>
  HttpClient.post(API_ENDPOINTS.CAMPAIGNS_TRACKER_SYNC(campaignId)),

setCampaignManager: (campaignId, payload) =>
  HttpClient.put(API_ENDPOINTS.CAMPAIGNS_SET_MANAGER(campaignId), payload),
  };

  payouts = {
    list: () => HttpClient.get(API_ENDPOINTS.PAYOUTS),
    create: (input) => HttpClient.post(API_ENDPOINTS.PAYOUTS, input),
    delete: (input) => HttpClient.post(API_ENDPOINTS.PAYOUT_DELETE, input),
    listAdmin: async () => {
      const response = await HttpClient.get(API_ENDPOINTS.PAYOUTS_ADMIN);
      return response.payouts.map((p) => ({
        ...p,
        payout_date: p.payout_date ? new Date(p.payout_date) : null,
      }));
    },
    deleteAdmin: (input) =>
      HttpClient.post(API_ENDPOINTS.DELETE_PAYOUTS_ADMIN, input),
    editAdmin: (input) =>
      HttpClient.post(API_ENDPOINTS.PAYOUTS_EDIT_ADMIN, input),
    createBatch: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATE_BATCH_PAYOUT, input),

    /* EXISTING single‑id fetch */
    get: (payoutId) => HttpClient.get(`${API_ENDPOINTS.PAYOUTS}/${payoutId}`),

    /* Source-aware fee preview — backend is the single source of truth.
       timing: 'instant' | 'net30' (default: backend infers from payout.blitzpay) */
    feePreview: (payoutId, timing) => {
      const qs = timing ? `?timing=${encodeURIComponent(timing)}` : "";
      return HttpClient.get(
        `${API_ENDPOINTS.PAYOUTS}/${payoutId}/fee_preview${qs}`,
      );
    },

    /* NEW helpers */
    byToken: (token) => HttpClient.get(API_ENDPOINTS.PAYOUTS_BY_TOKEN(token)),
    invite: (payload) =>
      HttpClient.post(API_ENDPOINTS.CREATE_INVITE_PAYOUT, payload),
    inviteEmail: (payload) =>
      HttpClient.post(API_ENDPOINTS.CREATE_INVITE_PAYOUT_EMAIL, payload),

    claim: (token, payload) =>
      HttpClient.post(API_ENDPOINTS.CONFIRM_INVITE_PAYOUT(token), payload),

    initiateStripeOAuth: (token) =>
      HttpClient.post(`/payouts/claim/${token}/stripe/initiate`),

    //for external accounts
    initiatePaypalOAuth: (token) =>
      HttpClient.post(`/payouts/claim/${token}/paypal/initiate`),

    claimFCSession: (token) =>
      HttpClient.post(`/payouts/claim/${token}/bank/fc-session`),

    claimFCLink: (token, payload) =>
      HttpClient.post(`/payouts/claim/${token}/bank/fc-link`, payload),

    claimManualBank: (token, payload) =>
      HttpClient.post(`/payouts/claim/${token}/bank/manual`, payload),

    getOnboardingStatus: (token, params = {}) =>
      HttpClient.get(`/payouts/claim/${token}/onboarding-status`, { params }),
  };

  creators = {
    list: (params = null) => HttpClient.get(API_ENDPOINTS.CREATORS, params),
    listPartners: (params = null) =>
      HttpClient.get(API_ENDPOINTS.CREATORS_PARTNERS, params),
    emails: () => HttpClient.get(API_ENDPOINTS.CREATOR_EMAILS),
    listKPI: () => HttpClient.get(API_ENDPOINTS.CREATORS_KPI),
    listManager: (agencyid) =>
      HttpClient.get(API_ENDPOINTS.CREATORS_MANAGER(agencyid)),
    add: (input) => HttpClient.post(API_ENDPOINTS.CREATOR_ADD, input),
    signup: (input) => HttpClient.post(API_ENDPOINTS.CREATOR_SIGNUP, input),
    update: (input) => HttpClient.put(API_ENDPOINTS.CREATOR_UPDATE, input),
    delete: (input) => HttpClient.post(API_ENDPOINTS.CREATOR_DELETE, input),
    softDelete: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATOR_SOFT_DELETE, input),
    adminFetch: () => HttpClient.get(API_ENDPOINTS.CREATOR_ADMIN_FETCH),
    adminEmails: () => HttpClient.get(API_ENDPOINTS.ADMIN_CREATOR_EMAILS),

    adminBrandEmailsList: async (query) => {
      const data = await HttpClient.get(
        API_ENDPOINTS.CREATORCONNECT_ADMIN_EMAIL,
        query,
      );
      return {
        ...data,
        emails: data.emails.map((email) => ({
          ...email,
          date_received: new Date(email.date_received),
        })),
      };
    },
    adminBrandEmail: async (email) => {
      const data = await HttpClient.get(
        API_ENDPOINTS.CREATORCONNECT_ADMIN_EMAIL_GET(email),
      );
      return {
        ...data,
        message: {
          ...data.message,
          date_received: new Date(data.message.date_received),
        },
      };
    },
    adminBrandKeywords: () =>
      HttpClient.get(API_ENDPOINTS.CREATORCONNECT_ADMIN_EMAIL_KEYWORDS),

    payout: (input) =>
      HttpClient.get(API_ENDPOINTS.CREATOR_PAYOUT_READ, { sheetId: input }),
    data: () => HttpClient.get(API_ENDPOINTS.CREATOR_DATA_READ),
    fetchDetails: (creatorId) =>
      HttpClient.get(API_ENDPOINTS.CREATORS_SPEC(creatorId)),
    fetchDetailsBatch: (creatorIds, options = {}) =>
      HttpClient.post(API_ENDPOINTS.CREATORS_BATCH, {
        creator_ids: creatorIds,
        ...options,
      }),
    fetchSimilar: (creatorId) =>
      HttpClient.get(`${API_ENDPOINTS.CREATORS_SPEC(creatorId)}/similar`),
    crmUpdate: (input) => HttpClient.post(API_ENDPOINTS.CRM_UPDATE, input),
    fetchAverageReview: (creatorId) =>
      HttpClient.get(API_ENDPOINTS.CREATOR_AVERAGE_REVIEW(creatorId)),
    postReview: (creatorId, input) =>
      HttpClient.post(API_ENDPOINTS.CREATOR_REVIEW(creatorId), input),
    search: (input) => HttpClient.post(API_ENDPOINTS.CREATOR_SEARCH, input),
    activityReport: (creatorId) =>
      HttpClient.get(API_ENDPOINTS.CREATOR_ACTIVITY(creatorId)),
    detectDuplicates: () => HttpClient.get(API_ENDPOINTS.CREATOR_DUPLICATES),
    mergeCreators: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATOR_MERGE, input),
    previewMerge: (creatorId) =>
      HttpClient.get(API_ENDPOINTS.CREATOR_MERGE_PREVIEW(creatorId)),
    previewNuke: (identifier) =>
      HttpClient.get(API_ENDPOINTS.CREATOR_NUKE_PREVIEW(identifier)),
    nukeCreator: (input) => HttpClient.post(API_ENDPOINTS.CREATOR_NUKE, input),
    getNukeHistory: () => HttpClient.get(API_ENDPOINTS.CREATOR_NUKE_HISTORY),
    searchForPayment: (query) =>
      HttpClient.post(API_ENDPOINTS.CREATOR_SEARCH_FOR_PAYMENT, { query }),
    getPaymentMethods: (creatorId) =>
      HttpClient.get(`/api/creators/${creatorId}/payment-methods`),

    getMarketplaceRanking: (creatorId) =>
      HttpClient.get(API_ENDPOINTS.CREATOR_MARKETPLACE_RANKING(creatorId)),
    getMarketplaceLeaderboard: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.CREATOR_MARKETPLACE_LEADERBOARD, params),

    admin: {
      updateBasic: (creatorId, input) =>
        HttpClient.put(
          API_ENDPOINTS.ADMIN_CREATOR_UPDATE_BASIC(creatorId),
          input,
        ),

      updateContact: (creatorId, input) =>
        HttpClient.put(
          API_ENDPOINTS.ADMIN_CREATOR_UPDATE_CONTACT(creatorId),
          input,
        ),

      updatePlatforms: (creatorId, input) =>
        HttpClient.put(
          API_ENDPOINTS.ADMIN_CREATOR_UPDATE_PLATFORMS(creatorId),
          input,
        ),

      updateDemographics: (creatorId, input) =>
        HttpClient.put(
          API_ENDPOINTS.ADMIN_CREATOR_UPDATE_DEMOGRAPHICS(creatorId),
          input,
        ),

      updateAdditional: (creatorId, input) =>
        HttpClient.put(
          API_ENDPOINTS.ADMIN_CREATOR_UPDATE_ADDITIONAL(creatorId),
          input,
        ),
    },
    vendorSignup: (input) =>
      HttpClient.post(API_ENDPOINTS.VENDOR_SIGNUP, input, {
        ignoreAuthError: true,
      }),
  };

  crm = {
    update: (input) => HttpClient.post(API_ENDPOINTS.CRM_UPDATE, input),
    update_contacts: (input) =>
      HttpClient.post(API_ENDPOINTS.CRM_UPDATE_CONTACTS, input),
    update_contact_status: (input) =>
      HttpClient.post(API_ENDPOINTS.CRM_UPDATE_CONTACT_STATUS, input),
    creator_update_status: (input) =>
      HttpClient.post(API_ENDPOINTS.CRM_CREATOR_UPDATE_STATUS, input),
    add: (input) => HttpClient.post(API_ENDPOINTS.CRM_ADD, input),
    addBulk: (contacts) =>
      HttpClient.post(API_ENDPOINTS.CRM_ADD_BULK, { contacts }),
    creator_creators: () => HttpClient.get(API_ENDPOINTS.CRM_CREATOR_CREATORS),
    creator_fetch: (creatorId) =>
      HttpClient.get(API_ENDPOINTS.CRM_CREATOR_SPEC(creatorId)),
    igdm_creators: () => HttpClient.get(API_ENDPOINTS.CRM_IGDM_CREATORS),
    contacts: () => HttpClient.get(API_ENDPOINTS.CRM_CONTACTS),

    // NEW: Lead Qualification Methods
    getQualifiedLeads: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.CRM_QUALIFIED_LEADS, params),
    getLeadScore: (contactId) =>
      HttpClient.get(API_ENDPOINTS.CRM_LEAD_SCORE(contactId)),
    getLeadStats: () => HttpClient.get(API_ENDPOINTS.CRM_LEAD_STATS),
    bulkUpdateScores: (input) =>
      HttpClient.post(API_ENDPOINTS.CRM_BULK_UPDATE_SCORES, input),

    prepareLeadConversion: (contactId) =>
      HttpClient.get(API_ENDPOINTS.CRM_PREPARE_CONVERSION(contactId)),

    convertLeadToClient: (contactId, data) =>
      HttpClient.post(API_ENDPOINTS.CRM_CONVERT_TO_CLIENT(contactId), data),
  };

  credx = {
    credits: (input) => HttpClient.get(API_ENDPOINTS.CREDX_CREDITS, input),
    subtract_credx: (input) =>
      HttpClient.post(API_ENDPOINTS.SUBTRACT_CREDITS, input),
  };

  twilio = {
    sendCreatorSms: (input) =>
      HttpClient.post(API_ENDPOINTS.TWILIO_SEND_CREATOR_SMS, input),
    fetch: () => HttpClient.get(API_ENDPOINTS.FETCH_MESSAGES),
  };

  creatorConnect = {
    login: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATORCONNECT_LOGIN, input, {
        ignoreAuthError: true,
      }),
    dashboard: () => HttpClient.get(API_ENDPOINTS.CREATORCONNECT_DASHBOARD),
    verify: (creatorName) =>
      HttpClient.get(
        `${API_ENDPOINTS.CREATORCONNECT_VERIFY}?creator=${creatorName}`,
      ),
    internallogin: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATORCONNECT_INTERNALLOGIN, input, {
        ignoreAuthError: true,
      }),
    connect: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATORCONNECT_ADD, input),
    reauth: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATORCONNECT_REAUTH, input),
    getReauth: (creator) =>
      HttpClient.get(API_ENDPOINTS.CREATORCONNECT_REAUTH, {
        username: creator,
      }),
    campaigns: () => HttpClient.get(API_ENDPOINTS.creatorConnectCampaigns),
    campaignUpdateStatus: (campaignId, input) =>
      HttpClient.post(
        API_ENDPOINTS.CREATORCONNECT_STATUS_CAMPAIGN(campaignId),
        input,
      ),
    payouts: () => HttpClient.get(API_ENDPOINTS.CREATORCONNECT_PAYOUTS),
    campaignUpdateTimeline: (id, creatorId, input) =>
      HttpClient.put(
        API_ENDPOINTS.CAMPAIGNS_TIMELINE_BY_CREATOR(id, creatorId),
        input,
      ),
    editCreator: (input) =>
      HttpClient.put(API_ENDPOINTS.CREATORCONNECT_EDIT, input),
    onboard: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATORCONNECT_ONBOARD, input),
    onboardWithService: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATORCONNECT_ONBOARD_SERVICE, input),
    editCreatorStripe: (input) =>
      HttpClient.put(API_ENDPOINTS.CREATORCONNECT_EDIT_STRIPE, input),
    postGoogle: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATORCONNECT_GOOGLE, input),
    unlinkGmail: () =>
      HttpClient.post(API_ENDPOINTS.CREATORCONNECT_GMAIL_UNLINK),
    toggleEmailAutomation: (enabled) =>
      HttpClient.post(API_ENDPOINTS.CREATORCONNECT_EMAIL_AUTOMATION_TOGGLE, {
        enabled,
      }),
    getCreator: () => HttpClient.get(API_ENDPOINTS.CREATORCONNECT_GET),
    getEmails: (query) =>
      HttpClient.get(API_ENDPOINTS.CREATORCONNECT_EMAILS, query),
    getInvoice: (id) =>
      HttpClient.get(API_ENDPOINTS.CREATORCONNECT_INVOICE(id)),
    getPartnerships: () =>
      HttpClient.get(API_ENDPOINTS.CREATORCONNECT_PARTNERSHIPS),
    getPartnershipSingle: (partnershipId) =>
      HttpClient.get(
        API_ENDPOINTS.CREATORCONNECT_PARTNERSHIPS_SINGLE(partnershipId),
      ),
    getContacts: () => HttpClient.get(API_ENDPOINTS.CREATORCONNECT_CONTACTS),
    getUsersByCompany: () =>
      HttpClient.get(API_ENDPOINTS.CREATORCONNECT_USERS_BY_COMPANY),
    getCredxCredits: (input) =>
      HttpClient.get(API_ENDPOINTS.CREATORCONNECT_CREDX_CREDITS, input),
    postCredxPitch: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATORCONNECT_CREDX_PITCH, input),
    postCredxSubstraction: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATORCONNECT_CREDX_SUBSTRACT, input),
    getZapFeed: (input) =>
      HttpClient.get(API_ENDPOINTS.CREATORCONNECT_ZAPFEED, input),
    commissions: () => HttpClient.get(API_ENDPOINTS.CREATORCONNECT_COMMISSIONS),
    getFinancials: () =>
      HttpClient.get(API_ENDPOINTS.CREATORCONNECT_FINANCIALS),
    getMisc: () => HttpClient.get(API_ENDPOINTS.CREATORCONNECT_MISC),
    postInvoice: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATORCONNECT_INVOICE_CREATE, input),
    updateCreatorFields: (campaignId, input) =>
      HttpClient.post(
        API_ENDPOINTS.CREATORCONNECT_UPDATE_FIELDS(campaignId),
        input,
      ),
    confirmPayment: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATORCONNECT_CONFIRM_PAYOUT, input),
    getAuthorizedSenders: () =>
      HttpClient.get(API_ENDPOINTS.CREATOR_AUTHORIZED),
    sendBulkEmail: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATOR_AUTHOR_SEND, input),
    fetchEmailBlasts: () =>
      HttpClient.get(API_ENDPOINTS.CREATOR_AUTHOR_SEND_FETCH),
    campaignNegotiate: (campaignId, input) =>
      HttpClient.post(
        API_ENDPOINTS.CREATORCONNECT_NEGOTIATE_CAMPAIGN(campaignId),
        input,
      ),
    fetchUserInfo: (email) =>
      HttpClient.get(`/creatorUsers/fetch_user_info?email=${email}`),
    getWallets: () => HttpClient.get(API_ENDPOINTS.CREATOR_WALLETS),
    createWallet: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATOR_WALLETS, input),
    updateWallet: (walletId, input) =>
      HttpClient.put(API_ENDPOINTS.CREATOR_WALLET_DETAIL(walletId), input),
    deleteWallet: (walletId) =>
      HttpClient.delete(API_ENDPOINTS.CREATOR_WALLET_DETAIL(walletId)),
    setPrimaryWallet: (walletId) =>
      HttpClient.post(API_ENDPOINTS.CREATOR_WALLET_SET_PRIMARY(walletId)),
    migrateLegacyPayments: () =>
      HttpClient.post(API_ENDPOINTS.CREATOR_MIGRATE_PAYMENTS),

    // Tax Information Methods
    getTaxInfo: () => HttpClient.get(API_ENDPOINTS.CREATOR_TAX_INFO),
    updateTaxInfo: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATOR_TAX_INFO, input),

    // PayPal OAuth - Login with PayPal for wallet verification
    paypalOAuth: {
      // Initiate PayPal OAuth flow - returns authorization URL
      initiate: (redirectPath) =>
        HttpClient.post(API_ENDPOINTS.CREATOR_PAYPAL_OAUTH_INITIATE, {
          redirect_path: redirectPath,
        }),
      // Check if creator has verified PayPal wallet
      getStatus: () =>
        HttpClient.get(API_ENDPOINTS.CREATOR_PAYPAL_OAUTH_STATUS),
      // Disconnect PayPal wallet
      disconnect: () =>
        HttpClient.post(API_ENDPOINTS.CREATOR_PAYPAL_OAUTH_DISCONNECT),
    },

    // Stripe OAuth - Stripe Connect for wallet verification
    stripeOAuth: {
      // Initiate Stripe OAuth flow - returns authorization URL
      initiate: (redirectPath) =>
        HttpClient.post(API_ENDPOINTS.CREATOR_STRIPE_OAUTH_INITIATE, {
          redirect_path: redirectPath,
        }),
      // Check if creator has verified Stripe wallet
      getStatus: () =>
        HttpClient.get(API_ENDPOINTS.CREATOR_STRIPE_OAUTH_STATUS),
      // Disconnect Stripe wallet
      disconnect: () =>
        HttpClient.post(API_ENDPOINTS.CREATOR_STRIPE_OAUTH_DISCONNECT),
    },

    // Payment Preferences - Auto-accept payment settings
    getPaymentPreferences: () =>
      HttpClient.get(API_ENDPOINTS.CREATOR_PAYMENT_PREFERENCES),
    updatePaymentPreferences: (input) =>
      HttpClient.put(API_ENDPOINTS.CREATOR_PAYMENT_PREFERENCES, input),

    // Claim Payout - Explicit destination selection for payouts
    claimPayout: (payoutId, destination, paymentMethod) =>
      HttpClient.post(API_ENDPOINTS.CREATOR_CLAIM_PAYOUT, {
        payout_id: payoutId,
        destination: destination, // 'balance' or 'withdraw'
        payment_method: paymentMethod || "BLITZPAY", // 'BLITZPAY' or 'NET30'
      }),

    getPaymentTracking: () =>
      HttpClient.get(API_ENDPOINTS.CREATORCONNECT_PAYMENT_TRACKING),
    fetchCampaignsToDo: () =>
      HttpClient.get(API_ENDPOINTS.CAMPAIGN_CREATOR_TODO),
    // Profile Management
    updateProfile: (input) =>
      HttpClient.put(API_ENDPOINTS.CREATOR_PROFILE, input),
    getStatus: () => HttpClient.get(API_ENDPOINTS.CREATOR_STATUS),

    // Platform Management (NEW - separated from edit)
    updatePlatforms: (input) =>
      HttpClient.put(API_ENDPOINTS.CREATOR_PLATFORMS, input),
    upgradeToPartner: () =>
      HttpClient.post(API_ENDPOINTS.CREATOR_PARTNER_UPGRADE),

    // Partner Settings
    updatePartnerSettings: (input) =>
      HttpClient.put(API_ENDPOINTS.CREATOR_PARTNER_SETTINGS, input),
    unlinkGmail: () =>
      HttpClient.post(API_ENDPOINTS.CREATORCONNECT_GMAIL_UNLINK),

    onboardPartner: (data) =>
      HttpClient.post(API_ENDPOINTS.CREATORCONNECT_ONBOARD_PARTNER, data),
  };

  invoices = {
    list: () => HttpClient.get(API_ENDPOINTS.INVOICES),
    fetch: (invoiceId) =>
      HttpClient.get(API_ENDPOINTS.INVOICE_FETCH(invoiceId)),
    fetchBatch: (invoiceIds) =>
      HttpClient.post(API_ENDPOINTS.INVOICES_BATCH, {
        invoice_ids: invoiceIds,
      }),
    getFilters: () => HttpClient.get(API_ENDPOINTS.INVOICES_FILTERS),
    listKPI: () => HttpClient.get(API_ENDPOINTS.INVOICE_KPI),

    delete: (input) => HttpClient.post(API_ENDPOINTS.DELETE_INVOICE, input),
    //Cleanup. If the response is not as expected, do not secondguess on the receiving end. Fix at the source!!!!
    listAdmin: async (params = {}) => {
      try {
        const response = await HttpClient.get(
          API_ENDPOINTS.INVOICES_ADMIN,
          params,
        );

        // Validate response structure
        if (!response || typeof response !== "object") {
          throw new Error("Invalid response format from server");
        }

        if (!Array.isArray(response.invoices)) {
          replaceConsoleError("Invoices response is not an array", response);
          throw new Error("Invalid invoices data format");
        }

        return {
          ...response,
          invoices: response.invoices.map((invoice) => ({
            ...invoice,
            created_at: invoice.created_at
              ? new Date(invoice.created_at)
              : null,
          })),
        };
      } catch (error) {
        replaceConsoleError("Error in listAdmin", error);
        throw error;
      }
    },

    editAdmin: (input) =>
      HttpClient.post(API_ENDPOINTS.EDIT_INVOICES_ADMIN, input),
    mergeBlitzPayAdmin: (input) =>
      HttpClient.post(API_ENDPOINTS.ADMIN_BLITZPAY_INVOICES_MERGE, input),
    dissolveBlitzPayMergeAdmin: (input) =>
      HttpClient.post(API_ENDPOINTS.ADMIN_BLITZPAY_INVOICES_MERGE_DISSOLVE, input),
    create: (input) => HttpClient.post(API_ENDPOINTS.CREATE_INVOICE, input),
    creatorList: () => HttpClient.get(API_ENDPOINTS.CREATOR_INVOICES),
    createPaypalPayment: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATE_PAYPAL, input),
    action: ({ invoiceId, action }) =>
      HttpClient.post(API_ENDPOINTS.INVOICE_ACTION(invoiceId), { action }),
    clientAction: ({ invoiceId, action }) =>
      HttpClient.post(API_ENDPOINTS.CLIENT_INVOICE_ACTION(invoiceId), {
        action,
      }),
    editInvoice: (input) => HttpClient.put(API_ENDPOINTS.EDIT_INVOICE, input),
    confirmInvoice: (payload) =>
      HttpClient.put(API_ENDPOINTS.INVOICE_CONFIRM, payload),
    unifiedAction: ({ invoiceId, action }) =>
      HttpClient.post(API_ENDPOINTS.INVOICE_ACTION_UNITY(invoiceId), {
        action,
      }),

    listCreatorAll: async () => {
      const response = await HttpClient.get(
        API_ENDPOINTS.CREATOR_INVOICES_ADMIN,
      );
      return response.invoices.map((invoice) => ({
        ...invoice,
        invoice_created_at: invoice.invoice_created_at
          ? new Date(invoice.invoice_created_at)
          : null,
      }));
    },
    pendingInvoices: (userId) =>
      HttpClient.get(API_ENDPOINTS.PENDING_INVOICES(userId)),
    fetchProjectInvoice: (invoiceId) =>
      HttpClient.get(API_ENDPOINTS.INVOICE_PROJECT_FETCH(invoiceId)),
    fetchPartnershipInvoice: (invoiceId) =>
      HttpClient.get(API_ENDPOINTS.INVOICE_PARTNERSHIP_FETCH(invoiceId)),

    getPaymentOptions: (invoiceId) =>
      HttpClient.get(API_ENDPOINTS.INVOICE_PAYMENT_OPTIONS(invoiceId)),

    payWithBalance: (invoiceId, data) =>
      HttpClient.post(API_ENDPOINTS.INVOICE_PAY_WITH_FUNDS, {
        invoiceId,
        payment_method: "balance",
        ...data,
      }),

    payWithCredit: (invoiceId, data) =>
      HttpClient.post(API_ENDPOINTS.INVOICE_PAY_WITH_FUNDS, {
        invoiceId,
        payment_method: "credit",
        ...data,
      }),

    /**
     * Get current payment status for an invoice
     * Includes balance applied, credit used, and remaining amount
     */
    getPaymentStatus: (invoiceId) =>
      HttpClient.get(API_ENDPOINTS.INVOICE_PAYMENT_STATUS(invoiceId)),

    updateExternalPO: (invoiceId, data) =>
      HttpClient.put(API_ENDPOINTS.INVOICE_EXTERNAL_PO(invoiceId), data),

    getExternalPO: (invoiceId, data) =>
      HttpClient.get(API_ENDPOINTS.INVOICE_EXTERNAL_PO(invoiceId), data),
  };

  invoiceSettings = {
    // User invoice settings
    get: () => HttpClient.get(API_ENDPOINTS.INVOICE_SETTINGS),
    create: (input) => HttpClient.post(API_ENDPOINTS.INVOICE_SETTINGS, input),
    update: (input) => HttpClient.put(API_ENDPOINTS.INVOICE_SETTINGS, input),
    getPublic: (invoiceId) =>
      HttpClient.get(`${API_ENDPOINTS.PUBLIC_INVOICE_SETTINGS}/${invoiceId}`),

    creator: {
      get: () => HttpClient.get(API_ENDPOINTS.CREATOR_INVOICE_SETTINGS),
      create: (input) =>
        HttpClient.post(API_ENDPOINTS.CREATOR_INVOICE_SETTINGS, input),
      update: (input) =>
        HttpClient.put(API_ENDPOINTS.CREATOR_INVOICE_SETTINGS, input),
    },
  };

  companies = {
    list: (params = {}) => HttpClient.get(API_ENDPOINTS.COMPANY, params),
    create: (input) => HttpClient.post(API_ENDPOINTS.COMPANY, input),
    delete: (input) => HttpClient.post(API_ENDPOINTS.DELETE_COMPANY, input),
    edit: (input) =>
      HttpClient.post(
        `${API_ENDPOINTS.COMPANY}/${input.companyId}`,
        input.params,
      ),
    listFetch: () => HttpClient.get(API_ENDPOINTS.FETCH_COMPANY_DATA),
    listUsers: () => HttpClient.get(API_ENDPOINTS.USERS_COMPANY),
    demoRegister: (input) =>
      HttpClient.post(API_ENDPOINTS.DEMO_REGISTER, input),
    invoice: (companyId, statusFilter) => {
      let url = API_ENDPOINTS.COMPANY_INVOICES(companyId);
      if (statusFilter) url += `?status=${statusFilter}`;
      return HttpClient.get(url);
    },
    getInvoiceSummaries: () =>
      HttpClient.get(API_ENDPOINTS.COMPANIES_INVOICE_SUMMARY),
    activityReport: (companyId) =>
      HttpClient.get(API_ENDPOINTS.COMPANY_ACTIVITY(companyId)),
    updateLegalEntity: (companyId, data) =>
      HttpClient.post(API_ENDPOINTS.UPDATE_LEGAL_ENTITY(companyId), data),

    // NEW: company users + files
    getUsers: (cid) => HttpClient.get(API_ENDPOINTS.COMPANY_USERS(cid)),
    getFiles: (cid) => HttpClient.get(API_ENDPOINTS.COMPANY_FILES(cid)),

    teams: {
      list: (cid) => HttpClient.get(API_ENDPOINTS.COMPANY_TEAMS(cid)),
      create: (cid, body) =>
        HttpClient.post(API_ENDPOINTS.COMPANY_TEAMS(cid), body),
      update: (cid, tid, body) =>
        HttpClient.put(API_ENDPOINTS.COMPANY_TEAM(cid, tid), body),
      delete: (cid, tid) =>
        HttpClient.delete(API_ENDPOINTS.COMPANY_TEAM(cid, tid)),
      assignUser: (cid, tid, userId) =>
        HttpClient.post(`${API_ENDPOINTS.COMPANY_TEAM(cid, tid)}/assign-user`, {
          user_id: userId,
        }),
      removeUser: (cid, tid, userId) =>
        HttpClient.post(`${API_ENDPOINTS.COMPANY_TEAM(cid, tid)}/remove-user`, {
          user_id: userId,
        }),
      allocateSpending: (cid, tid, body) =>
        HttpClient.post(
          `${API_ENDPOINTS.COMPANY_TEAM(cid, tid)}/allocate-credit`,
          body,
        ),
      useSpending: (cid, tid, body) =>
        HttpClient.post(
          `${API_ENDPOINTS.COMPANY_TEAM(cid, tid)}/use-credit`,
          body,
        ),
      releaseSpending: (cid, tid, body) =>
        HttpClient.post(
          `${API_ENDPOINTS.COMPANY_TEAM(cid, tid)}/release-credit`,
          body,
        ),
      rebalance: (cid, body) =>
        HttpClient.post(API_ENDPOINTS.COMPANY_REBALANCE_TEAMS(cid), body),
      getSpendingSummary: (cid) =>
        HttpClient.get(API_ENDPOINTS.COMPANY_TEAM_SPENDING_SUMMARY(cid)),
      getSpendingLedger: (cid) =>
        HttpClient.get(API_ENDPOINTS.COMPANY_TEAM_SPENDING_LEDGER(cid)),
      // NEW: team files
      getFiles: (tid) => HttpClient.get(API_ENDPOINTS.TEAM_FILES(tid)),
    },
  };

  partnerships = {
    // Core Operations
    list: () => HttpClient.get(API_ENDPOINTS.PARTNERSHIPS),
    book: (input) => HttpClient.post(API_ENDPOINTS.PARTNERSHIPS_PUBLIC, input),
    create: (input) =>
      HttpClient.post(API_ENDPOINTS.PARTNERSHIPS_CREATE, input),

    // FIXED: Now uses function endpoint with ID parameter
    fetchPartnership: async (id) => {
      const data = await HttpClient.get(API_ENDPOINTS.PARTNERSHIPS_FETCH(id));
      return {
        ...data,
        partnership: {
          ...data.partnership,
          finish_date: data.partnership.finish_date
            ? new Date(data.partnership.finish_date)
            : null,
          paid_date: data.partnership.paid_date
            ? new Date(data.partnership.paid_date)
            : null,
          proposal_date: data.partnership.proposal_date
            ? new Date(data.partnership.proposal_date)
            : null,
          invoice_date: data.partnership.invoice_date
            ? new Date(data.partnership.invoice_date)
            : null,
        },
      };
    },
    update: (id, input) =>
      HttpClient.put(API_ENDPOINTS.PARTNERSHIPS_UPDATE(id), input),

    // FIXED: Now uses function endpoint with ID parameter
    updateStatus: (id, input) =>
      HttpClient.put(API_ENDPOINTS.UPDATE_PARTNERSHIP_STATUS(id), input),

    // FIXED: Now uses function endpoint with ID parameter
    delete: (id) => HttpClient.delete(API_ENDPOINTS.DELETE_PARTNERSHIP(id)),

    // NEW: Unified partnership + deliverable creation
    createWithDeliverable: (input) =>
      HttpClient.post(
        API_ENDPOINTS.PARTNERSHIPS_CREATE_WITH_DELIVERABLE,
        input,
      ),

    // NEW: Refresh computed totals
    refreshTotals: (id) =>
      HttpClient.post(API_ENDPOINTS.PARTNERSHIPS_REFRESH_TOTALS(id)),

    // Existing methods (keep these)
    share: (id, input) =>
      HttpClient.post(API_ENDPOINTS.PARTNERSHIPS_SHARE(id), input),
    getAdminDetails: () => HttpClient.get(API_ENDPOINTS.ADMIN_PARTNERSHIPS),
    getDeliverables: (id, creatorId) =>
      HttpClient.get(API_ENDPOINTS.PARTNERSHIP_DELIVERABLES(id, creatorId)),
  };

  conversations = {
    createCampaign: (input) =>
      HttpClient.post(API_ENDPOINTS.CONVERSATIONS_CREATE_CAMPAIGN, input),
    createPartnership: (input) =>
      HttpClient.post(API_ENDPOINTS.CONVERSATIONS_CREATE_PARTNERSHIP, input),
    checkCampaign: (input) =>
      HttpClient.post(API_ENDPOINTS.CONVERSATIONS_CHECK_CAMPAIGN, input),
    checkUserToUser: (input) =>
      HttpClient.post(API_ENDPOINTS.CONVERSATIONS_CHECK_USER_TO_USER, input),
    checkUserToCreator: (input) =>
      HttpClient.post(API_ENDPOINTS.CONVERSATIONS_CHECK_USER_TO_CREATOR, input),

    getMessages: (conversationId, input) =>
      HttpClient.get(API_ENDPOINTS.MESSAGES(conversationId), input),
    getMetadata: (conversationId, input) =>
      HttpClient.get(API_ENDPOINTS.MESSAGES_META(conversationId), input),
    sendMessage: (id, input) =>
      HttpClient.post(API_ENDPOINTS.MESSAGES(id), input),
    deleteMessage: (id) => HttpClient.delete(API_ENDPOINTS.DELETE_MESSAGES(id)),
    fetchUserToUser: () =>
      HttpClient.get(API_ENDPOINTS.CONVERSATIONS_GET_USER_TO_USER),
    share: (id, input) =>
      HttpClient.post(
        API_ENDPOINTS.CONVERSATIONS_SHARE_USER_TO_USER(id),
        input,
      ),
    inviteByEmail: (id, input) =>
      HttpClient.post(
        API_ENDPOINTS.CONVERSATIONS_SHARE_BY_EMAIL_USER_TO_USER(id),
        input,
      ),
    fetchCreatorConversations: () =>
      HttpClient.get(API_ENDPOINTS.FETCH_CREATOR_CONVERSATIONS),
    getUnreadCountForCampaignCreator: (campaignId, creatorId) =>
      HttpClient.get(
        API_ENDPOINTS.CONVERSATIONS_UNREAD_COUNT_FOR_CAMPAIGN_CREATOR(
          campaignId,
          creatorId,
        ),
      ),
    updateConversationDetails: (conversationId, displayName) =>
      HttpClient.post(
        API_ENDPOINTS.CONVERSATIONS_UPDATE_NAME_USER_TO_USER(conversationId),
        { display_name: displayName },
      ),

    // New endpoints for public conversations
    getPublic: (conversationId) =>
      HttpClient.get(
        API_ENDPOINTS.CONVERSATIONS_PUBLIC_CONVERSATION(conversationId),
      ),
    join: (conversationId) =>
      HttpClient.post(
        API_ENDPOINTS.CONVERSATIONS_PUBLIC_CONVERSATION_JOIN(conversationId),
      ),
    checkCreatorToUser: (input) =>
      HttpClient.post(API_ENDPOINTS.CONVERSATIONS_GET_CREATOR_TO_USER, input),
    fetchAdmin: () => HttpClient.get(API_ENDPOINTS.CONVERSATIONS_GET_ADMIN),
    createConversation: (payload) =>
      HttpClient.post(API_ENDPOINTS.CONVERSATIONS_CREATE, payload),

    addReaction: (messageId, reactionType) =>
      HttpClient.post(`/conversations/messages/${messageId}/reactions`, {
        reaction_type: reactionType,
      }),

    removeReaction: (messageId, reactionType) =>
      HttpClient.delete(`/conversations/messages/${messageId}/reactions`, {
        data: { reaction_type: reactionType },
      }),

    getReactions: (messageId) =>
      HttpClient.get(`/conversations/messages/${messageId}/reactions`),

    addParticipant: (conversationId, data) =>
      HttpClient.post(
        API_ENDPOINTS.CONVERSATIONS_ADD_PARTICIPANT(conversationId),
        data,
      ),

    removeParticipant: (conversationId, data) =>
      HttpClient.post(
        API_ENDPOINTS.CONVERSATIONS_REMOVE_PARTICIPANT(conversationId),
        data,
      ),

    transferHost: (conversationId, data) =>
      HttpClient.post(
        API_ENDPOINTS.CONVERSATIONS_TRANSFER_HOST(conversationId),
        data,
      ),

    fetchOrganizedConversations: () =>
      HttpClient.get(API_ENDPOINTS.CONVERSATIONS_ORGANIZED),

    getConversationStatus: (conversationId) =>
      HttpClient.get(API_ENDPOINTS.CONVERSATIONS_STATUS(conversationId)),

    updateConversationStatus: (conversationId, status) =>
      HttpClient.post(
        API_ENDPOINTS.CONVERSATIONS_UPDATE_STATUS(conversationId),
        { status },
      ),

    acceptConversationRequest: (conversationId) =>
      HttpClient.post(API_ENDPOINTS.CONVERSATION_ACCEPT(conversationId)),

    bulkUpdateConversationStatus: (conversationIds, status) =>
      HttpClient.post(API_ENDPOINTS.CONVERSATIONS_BULK_UPDATE, {
        conversation_ids: conversationIds,
        status,
      }),
  };

  referrals = {
    // Core Operations
    create: (input) => HttpClient.post(API_ENDPOINTS.REFERRALS_CREATE, input),
    list: (params) => HttpClient.get(API_ENDPOINTS.REFERRALS_LIST, params),
    get: (id) => HttpClient.get(API_ENDPOINTS.REFERRALS_GET(id)),
    updateStatus: (id, input) =>
      HttpClient.put(API_ENDPOINTS.REFERRALS_UPDATE_STATUS(id), input),
    delete: (id) => HttpClient.delete(API_ENDPOINTS.REFERRALS_DELETE(id)),

    // Analytics
    getStats: () => HttpClient.get(API_ENDPOINTS.REFERRALS_STATS),
    getByUser: (userId) =>
      HttpClient.get(API_ENDPOINTS.REFERRALS_BY_USER(userId)),

    // Retroactive Assignment Support
    searchUsers: (query) =>
      HttpClient.get(API_ENDPOINTS.REFERRALS_SEARCH_USERS, { q: query }),
    getUserDetails: (identifier) =>
      HttpClient.get(API_ENDPOINTS.REFERRALS_USER_DETAILS(identifier)),
    validateAssignment: (input) =>
      HttpClient.post(API_ENDPOINTS.REFERRALS_VALIDATE_ASSIGNMENT, input),
    bulkCreate: (input) =>
      HttpClient.post(API_ENDPOINTS.REFERRALS_BULK_CREATE, input),
    getRetroactiveSummary: () =>
      HttpClient.get(API_ENDPOINTS.REFERRALS_RETROACTIVE_SUMMARY),
  };

  // Update commissions object to include referrals for backwards compatibility
  commissions = {
    list: () => HttpClient.get(API_ENDPOINTS.COMMISSIONS),
    getAll: () => HttpClient.get(API_ENDPOINTS.COMMISSIONS_All),

    // Referral operations (proxied to referrals)
    create: (input) => HttpClient.post(API_ENDPOINTS.REFERRALS_CREATE, input),
    getReferrals: (params) =>
      HttpClient.get(API_ENDPOINTS.REFERRALS_LIST, params),

    // ✅ FIXED: Added method aliases for component compatibility

    // Get eligible commissions (ready to claim)
    getEligible: () => HttpClient.get(API_ENDPOINTS.COMMISSIONS_ELIGIBLE),
    getEligibleCommissions: () =>
      HttpClient.get(API_ENDPOINTS.COMMISSIONS_ELIGIBLE), // NEW ALIAS

    // Get pending commissions (claimed but not yet processed)
    getPending: () => HttpClient.get(API_ENDPOINTS.COMMISSIONS_PENDING),
    getPendingCommissions: () =>
      HttpClient.get(API_ENDPOINTS.COMMISSIONS_PENDING), // NEW ALIAS

    // Get claimed/completed commissions
    getClaimed: () => HttpClient.get(API_ENDPOINTS.COMMISSIONS_CLAIMED),
    getClaimedCommissions: () =>
      HttpClient.get(API_ENDPOINTS.COMMISSIONS_CLAIMED), // NEW ALIAS

    // Get commission summary
    getSummary: () => HttpClient.get(API_ENDPOINTS.COMMISSIONS_SUMMARY),
    getCommissionSummary: () =>
      HttpClient.get(API_ENDPOINTS.COMMISSIONS_SUMMARY),

    // Claim a single commission
    claim: (params) => HttpClient.post(API_ENDPOINTS.COMMISSIONS_CLAIM, params),
    claimCommission: (params) =>
      HttpClient.post(API_ENDPOINTS.COMMISSIONS_CLAIM, params), // NEW ALIAS

    // Claim all eligible commissions
    claimAll: () => HttpClient.post(API_ENDPOINTS.COMMISSIONS_CLAIM_ALL),

    // Get commission details
    getDetail: (id) => HttpClient.get(API_ENDPOINTS.COMMISSIONS_DETAIL(id)),

    // Cancel a commission
    cancel: (id, reason) =>
      HttpClient.post(API_ENDPOINTS.COMMISSIONS_CANCEL(id), { reason }),

    // Get commissions by type
    getByType: (type) =>
      HttpClient.get(API_ENDPOINTS.COMMISSIONS_BY_TYPE(type)),

    // Get commissions by referral
    getByReferral: (referralId) =>
      HttpClient.get(API_ENDPOINTS.COMMISSIONS_BY_REFERRAL(referralId)),

    // Get commission history
    getHistory: (params) =>
      HttpClient.get(API_ENDPOINTS.COMMISSIONS_HISTORY, params),

    getReferralEligibleCommissions: (referralId) =>
      HttpClient.get(
        API_ENDPOINTS.GLOBAL_USER_REFERRAL_ELIGIBLE_COMMISSIONS(referralId),
      ),

    listAccessGrants: (params) =>
      HttpClient.get(API_ENDPOINTS.GLOBAL_USER_REFERRAL_ACCESS_LIST, params),
    getAccessGrant: (accessId) =>
      HttpClient.get(
        API_ENDPOINTS.GLOBAL_USER_REFERRAL_ACCESS_DETAIL(accessId),
      ),
    updateAccessGrant: (accessId, data) =>
      HttpClient.put(
        API_ENDPOINTS.GLOBAL_USER_REFERRAL_ACCESS_UPDATE(accessId),
        data,
      ),
    deleteAccessGrant: (accessId) =>
      HttpClient.delete(
        API_ENDPOINTS.GLOBAL_USER_REFERRAL_ACCESS_DELETE(accessId),
      ),
    getReferralAccessGrants: (referralId) =>
      HttpClient.get(
        API_ENDPOINTS.GLOBAL_USER_REFERRAL_SPECIFIC_ACCESS(referralId),
      ),
    getAccessRateBreakdown: (accessId) =>
      HttpClient.get(API_ENDPOINTS.COMMISSIONS_ACCESS_RATE_BREAKDOWN(accessId)),
    getCommissionTypes: () => HttpClient.get(API_ENDPOINTS.COMMISSIONS_TYPES),
    createAccessGrant: (data) =>
      HttpClient.post(API_ENDPOINTS.COMMISSIONS_ACCESS_CREATE, data),
    getMyCommissionAccess: () =>
      HttpClient.get(API_ENDPOINTS.COMMISSIONS_MY_ACCESS),

    // Admin operations
    admin: {
      list: (params) => HttpClient.get(API_ENDPOINTS.ADMIN_COMMISSIONS, params),
      get: (id) => HttpClient.get(API_ENDPOINTS.ADMIN_COMMISSIONS_DETAIL(id)),
      approve: (id) =>
        HttpClient.post(API_ENDPOINTS.ADMIN_COMMISSIONS_APPROVE(id)),
      getStats: () => HttpClient.get(API_ENDPOINTS.ADMIN_COMMISSIONS_STATS),
    },
  };

  deliverables = {
    // Core Operations
    list: () => HttpClient.get(API_ENDPOINTS.DELIVERABLES),
    create: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATE_DELIVERABLES, input),

    // FIXED: Now uses function endpoint with ID parameter
    update: (id, input) =>
      HttpClient.put(API_ENDPOINTS.UPDATE_DELIVERABLES(id), input),

    // FIXED: Now uses function endpoint with partnership ID parameter
    getByPartnership: (partnershipId) =>
      HttpClient.get(
        API_ENDPOINTS.GET_DELIVERABLE_PARTNERSHIPS(partnershipId),
        undefined,
        { accept404: true },
      ),

    // NEW: Create deliverable with partnership
    createWithPartnership: (input) =>
      HttpClient.post(
        API_ENDPOINTS.CREATE_DELIVERABLES_WITH_PARTNERSHIP,
        input,
      ),

    // NEW: Migration utility for orphaned deliverables
    migrateOrphaned: () =>
      HttpClient.post(API_ENDPOINTS.MIGRATE_ORPHANED_DELIVERABLES),
  };

  miscProjects = {
    list: () => HttpClient.get(API_ENDPOINTS.MISC_PROJECTS),
    create: (input) =>
      HttpClient.post(API_ENDPOINTS.MISC_PROJECTS_CREATE, input),
    // fetch: (id) => HttpClient.get(API_ENDPOINTS.PARTNERSHIPS_FETCH(id)),
    // updateStatus: (id, input) => HttpClient.put(`${API_ENDPOINTS.UPDATE_PARTNERSHIP_STATUS}/${id}/status`, input),
    // delete: (id) => HttpClient.delete(`${API_ENDPOINTS.DELETE_PARTNERSHIP}/${id}`),
  };

  files = {
    // Existing file methods
    post: (input) => HttpClient.post(API_ENDPOINTS.POST_FILES, input),
    fetch: () => HttpClient.get(API_ENDPOINTS.FETCH_FILES),
    fetchContracts: () => HttpClient.get(API_ENDPOINTS.FETCH_CONTRACTS),
    createAsset: (input) => HttpClient.post(API_ENDPOINTS.CREATE_ASSET, input),
    post_creator_pfp: (input) =>
      HttpClient.post(API_ENDPOINTS.post_creator_pfp, input),
    postCreatorFile: (input) =>
      HttpClient.post(API_ENDPOINTS.POST_FILES_CREATOR, input),
    createContract: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATE_CONTRACT, input),
    getContract: (id) =>
      HttpClient.get(API_ENDPOINTS.GET_CONTRACT.SPEC_CONTRACT(id)),
    updateContract: (id, data) =>
      HttpClient.put(API_ENDPOINTS.SPEC_CONTRACT(id), data),
    getFile: (id) => HttpClient.get(API_ENDPOINTS.SPEC_FILE(id)),
    updateFile: (id, data) =>
      HttpClient.post(API_ENDPOINTS.SPEC_FILE_UPDATE(id), data),

    // Enhanced AssetOrganizer methods with checklist support
    organizers: {
      // ========== BASIC ORGANIZER CRUD ==========
      // Get all organizers
      list: (params = {}) => HttpClient.get(API_ENDPOINTS.ORGANIZERS, params),

      // Create new organizer
      create: (input) => HttpClient.post(API_ENDPOINTS.ORGANIZERS, input),

      // Get specific organizer with files
      get: (id) => HttpClient.get(API_ENDPOINTS.ORGANIZER_DETAIL(id)),

      // Update organizer metadata
      update: (id, input) =>
        HttpClient.put(API_ENDPOINTS.ORGANIZER_DETAIL(id), input),

      // Delete organizer (soft delete)
      delete: (id) => HttpClient.delete(API_ENDPOINTS.ORGANIZER_DETAIL(id)),

      // ========== FILE MANAGEMENT ==========
      // Add single file to organizer
      addFile: (organizerId, input) =>
        HttpClient.post(API_ENDPOINTS.ORGANIZER_FILES(organizerId), input),

      // Add multiple existing files to organizer
      addExistingFiles: (organizerId, input) =>
        HttpClient.post(
          API_ENDPOINTS.ORGANIZER_ADD_EXISTING(organizerId),
          input,
        ),

      // Alternative method name for backward compatibility
      addMultipleFiles: (organizerId, input) =>
        HttpClient.post(
          API_ENDPOINTS.ORGANIZER_ADD_EXISTING(organizerId),
          input,
        ),

      // Remove file from organizer
      removeFile: (organizerId, fileId) =>
        HttpClient.delete(
          API_ENDPOINTS.ORGANIZER_FILE_DETAIL(organizerId, fileId),
        ),

      // Reorder files in organizer
      reorderFiles: (organizerId, input) =>
        HttpClient.put(API_ENDPOINTS.ORGANIZER_REORDER(organizerId), input),

      // Update file notes/tags in organizer
      updateFileNotes: (organizerId, fileId, input) =>
        HttpClient.put(
          API_ENDPOINTS.ORGANIZER_FILE_NOTES(organizerId, fileId),
          input,
        ),

      // Get available organizer types
      getTypes: () => HttpClient.get(API_ENDPOINTS.ORGANIZER_TYPES),

      // ========== NEW: CHECKLIST MANAGEMENT ==========
      // Get checklist for organizer
      getChecklist: (organizerId, params = {}) =>
        HttpClient.get(API_ENDPOINTS.ORGANIZER_CHECKLIST(organizerId), params),

      // Create checklist item
      createChecklistItem: (organizerId, input) =>
        HttpClient.post(API_ENDPOINTS.ORGANIZER_CHECKLIST(organizerId), input),

      // Update checklist item
      updateChecklistItem: (organizerId, itemId, input) =>
        HttpClient.put(
          API_ENDPOINTS.ORGANIZER_CHECKLIST_ITEM(organizerId, itemId),
          input,
        ),

      // Delete checklist item
      deleteChecklistItem: (organizerId, itemId) =>
        HttpClient.delete(
          API_ENDPOINTS.ORGANIZER_CHECKLIST_ITEM(organizerId, itemId),
        ),

      // Fulfill checklist item with file
      fulfillChecklistItem: (organizerId, itemId, input) =>
        HttpClient.post(
          API_ENDPOINTS.ORGANIZER_CHECKLIST_FULFILL(organizerId, itemId),
          input,
        ),

      // Mark checklist item as incomplete
      unfulfillChecklistItem: (organizerId, itemId) =>
        HttpClient.post(
          API_ENDPOINTS.ORGANIZER_CHECKLIST_UNFULFILL(organizerId, itemId),
        ),

      // Reorder checklist items
      reorderChecklistItems: (organizerId, input) =>
        HttpClient.put(
          API_ENDPOINTS.ORGANIZER_CHECKLIST_REORDER(organizerId),
          input,
        ),

      // Create multiple checklist items at once
      createBulkChecklistItems: (organizerId, input) =>
        HttpClient.post(
          API_ENDPOINTS.ORGANIZER_CHECKLIST_BULK(organizerId),
          input,
        ),

      // Get checklist templates
      getChecklistTemplates: (organizerId) =>
        HttpClient.get(
          API_ENDPOINTS.ORGANIZER_CHECKLIST_TEMPLATES(organizerId),
        ),

      shareOrganizer: (organizerId, input) =>
        HttpClient.post(API_ENDPOINTS.ORGANIZER_SHARE(organizerId), input),

      // Get shared organizers for current user
      getSharedOrganizers: (params = {}) =>
        HttpClient.get(API_ENDPOINTS.ORGANIZERS_SHARED, params),

      // Get shares for a specific organizer
      getOrganizerShares: (organizerId) =>
        HttpClient.get(API_ENDPOINTS.ORGANIZER_SHARES(organizerId)),

      // Update share permissions
      updateOrganizerShare: (organizerId, shareId, input) =>
        HttpClient.put(
          API_ENDPOINTS.ORGANIZER_SHARE_DETAIL(organizerId, shareId),
          input,
        ),

      // Remove a share
      removeOrganizerShare: (organizerId, shareId) =>
        HttpClient.delete(
          API_ENDPOINTS.ORGANIZER_SHARE_DETAIL(organizerId, shareId),
        ),

      createPublicLink: (organizerId) =>
        HttpClient.post(
          API_ENDPOINTS.ORGANIZER_CREATE_PUBLIC_LINK(organizerId),
        ),

      // Revoke public access
      revokePublicLink: (organizerId) =>
        HttpClient.post(API_ENDPOINTS.ORGANIZER_REVOKE_PUBLIC(organizerId)),

      // Public access methods (no auth required)
      getPublicOrganizer: (organizerId, token) =>
        HttpClient.get(
          `${API_ENDPOINTS.ORGANIZER_PUBLIC_ACCESS(organizerId)}?token=${token}`,
          null,
          { ignoreAuthError: true },
        ),

      uploadToPublicOrganizer: (organizerId, token, data) =>
        HttpClient.post(
          `${API_ENDPOINTS.ORGANIZER_PUBLIC_UPLOAD(organizerId)}?token=${token}`,
          { ...data, token },
          { ignoreAuthError: true },
        ),

      fulfillPublicChecklistItem: (organizerId, itemId, token, data) =>
        HttpClient.post(
          `${API_ENDPOINTS.ORGANIZER_PUBLIC_CHECKLIST_FULFILL(organizerId, itemId)}?token=${token}`,
          { ...data, token },
          { ignoreAuthError: true },
        ),

      getPublicChecklist: (organizerId, token) =>
        HttpClient.get(
          `${API_ENDPOINTS.ORGANIZER_PUBLIC_CHECKLIST(organizerId)}?token=${token}`,
          null,
          { ignoreAuthError: true },
        ),
      uploadToOrganizer: (organizerId, data) =>
        HttpClient.post(API_ENDPOINTS.ORGANIZER_UPLOAD(organizerId), data),

      // NEW: Upload and fulfill checklist item
      uploadToChecklistItem: (organizerId, itemId, data) =>
        HttpClient.post(
          API_ENDPOINTS.ORGANIZER_CHECKLIST_UPLOAD(organizerId, itemId),
          data,
        ),

      getConversation: (organizerId) =>
        HttpClient.get(
          `${API_ENDPOINTS.ORGANIZERS}/${organizerId}/conversation`,
        ),

      createConversation: (organizerId) =>
        HttpClient.post(
          `${API_ENDPOINTS.ORGANIZERS}/${organizerId}/conversation`,
        ),

      getConversationParticipants: (organizerId) =>
        HttpClient.get(
          `${API_ENDPOINTS.ORGANIZERS}/${organizerId}/conversation/participants`,
        ),
    },
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // API/index.js  — trackers OBJECT  (complete replacement)
  //
  // Find this block:
  //
  //   trackers = {
  //     create: (input) => HttpClient.post(API_ENDPOINTS.CREATE_TRACKERS, input),
  //     fetch: () => HttpClient.get(API_ENDPOINTS.FETCH_TRACKERS),
  //     update: (trackerId, input) =>
  //       HttpClient.put(`${API_ENDPOINTS.UPDATE_TRACKERS}/${trackerId}`, input),
  //     exportSheet: (input) =>
  //       HttpClient.post(API_ENDPOINTS.EXPORT_SHEET_TRACKER, input),
  //   };
  //
  // Replace with the block below:
  // ─────────────────────────────────────────────────────────────────────────────

  trackers = {
    /** POST /trackers/  — create a new tracker (auth required) */
    create: (input) => HttpClient.post(API_ENDPOINTS.CREATE_TRACKERS, input),

    /** GET /trackers/  — list all trackers for the signed-in user (auth required) */
    fetch: () => HttpClient.get(API_ENDPOINTS.FETCH_TRACKERS),

    /**
     * GET /trackers/:id  — single tracker, owner-only (auth required).
     * Used by tracker.jsx after mutations to refresh the detail overlay.
     */
    get: (trackerId) => HttpClient.get(API_ENDPOINTS.FETCH_TRACKER(trackerId)),

    /**
     * GET /trackers/public/:id  — single tracker, NO auth.
     * Used by TrackerPublicPage (/tracker/:trackerId).
     * ignoreAuthError: true prevents HttpClient from redirecting
     * unauthenticated visitors to the login page.
     */
    getPublic: (trackerId) =>
      HttpClient.get(API_ENDPOINTS.FETCH_TRACKER_PUBLIC(trackerId), null, {
        ignoreAuthError: true,
      }),

    /** PUT /trackers/:id  — update a tracker (auth required) */
    update: (trackerId, input) =>
      HttpClient.put(API_ENDPOINTS.UPDATE_TRACKERS(trackerId), input),

    stop: (trackerId) =>
      HttpClient.post(API_ENDPOINTS.STOP_TRACKER(trackerId), {}),

    delete: (trackerId) =>
      HttpClient.delete(API_ENDPOINTS.DELETE_TRACKER(trackerId)),

    exportSheet: (trackerId) =>
      HttpClient.post(API_ENDPOINTS.EXPORT_SHEET_TRACKER(trackerId)),

    getHistory: (trackerId) =>
      HttpClient.get(API_ENDPOINTS.GET_TRACKER_HISTORY(trackerId)),

    getHistoryPublic: (trackerId) =>
    HttpClient.get(API_ENDPOINTS.GET_TRACKER_HISTORY_PUBLIC(trackerId), null, {
      ignoreAuthError: true,
    }),

    // Sharing
  getShares: (trackerId) =>
    HttpClient.get(API_ENDPOINTS.TRACKER_SHARES(trackerId)),
  share: (trackerId, input) =>
    HttpClient.post(API_ENDPOINTS.TRACKER_SHARES(trackerId), input),
  updateShare: (trackerId, shareUserId, input) =>
    HttpClient.patch(API_ENDPOINTS.TRACKER_SHARE_USER(trackerId, shareUserId), input),
  removeShare: (trackerId, shareUserId) =>
    HttpClient.delete(API_ENDPOINTS.TRACKER_SHARE_USER(trackerId, shareUserId)),
  };

  agency = {
    getCreator: async (creatorId) => {
      const data = await HttpClient.get(
        API_ENDPOINTS.AGENCY_CREATOR(creatorId),
      );
      return {
        ...data,
        campaigns: data.campaigns.map((campaign) => ({
          ...campaign,
          ideal_duedate: new Date(campaign.ideal_duedate),
        })),
        conversations: data.conversations.map((conversation) => ({
          ...conversation,
          created_at: new Date(conversation.created_at),
        })),
        partnerships: data.partnerships.map((partnership) => ({
          ...partnership,
          finish_date: partnership.finish_date
            ? new Date(partnership.finish_date)
            : null,
          paid_date: partnership.paid_date
            ? new Date(partnership.paid_date)
            : null,
          proposal_date: partnership.proposal_date
            ? new Date(partnership.proposal_date)
            : null,
          invoice_date: partnership.invoice_date
            ? new Date(partnership.invoice_date)
            : null,
        })),
        payouts: data.payouts.map((payout) => ({
          ...payout,
          payout_date: new Date(payout.payout_date),
        })),
      };
    },

    getZapfeed: async (params) => {
      // Make sure params are passed as query parameters, not request body
      const data = await HttpClient.get(API_ENDPOINTS.AGENCY_ZAPFEED, params);
      return {
        ...data,
        activities:
          data.activities?.map((entry) => ({
            ...entry,
            activity_date: new Date(entry.activity_date),
          })) || [],
      };
    },

    editCreator: (creatorId, input) =>
      HttpClient.put(API_ENDPOINTS.AGENCY_CREATOR(creatorId), input),
    payouts: () => HttpClient.get(API_ENDPOINTS.AGENCY_PAYOUTS),
    dashboard: () => HttpClient.get(API_ENDPOINTS.AGENCY_DASHBOARD),

    campaignAccept: (campaignId, input) =>
      HttpClient.post(
        API_ENDPOINTS.AGENCY_CAMPAIGN_CREATOR_ACCEPT(campaignId),
        input,
      ),

    campaignUpdateFields: (campaignId, input) =>
      HttpClient.post(
        API_ENDPOINTS.AGENCY_CAMPAIGN_CREATOR_UPDATE_FIELDS(campaignId),
        input,
      ),

    approvePayment: (payoutId, input) =>
      HttpClient.post(API_ENDPOINTS.AGENCY_PAYOUT_APPROVE(payoutId), input),

    getRosterSettings: (agencyName) =>
      HttpClient.get(API_ENDPOINTS.AGENCY_ROSTER_SETTINGS(agencyName)),

    createRosterSettings: (agencyName, data) =>
      HttpClient.post(API_ENDPOINTS.AGENCY_ROSTER_SETTINGS(agencyName), data),

    updateRosterSettings: (agencyName, data) =>
      HttpClient.put(API_ENDPOINTS.AGENCY_ROSTER_SETTINGS(agencyName), data),

    getPublicRosterSettings: (agencyName) =>
      HttpClient.get(API_ENDPOINTS.PUBLIC_AGENCY_ROSTER_SETTINGS(agencyName)),

    campaignNegotiate: (campaignId, input) =>
      HttpClient.post(
        API_ENDPOINTS.AGENCY_CAMPAIGN_NEGOTIATE(campaignId),
        input,
      ),

    getAllCollaborations: () =>
      HttpClient.get(API_ENDPOINTS.AGENCY_ALL_COLLABS),

    getCreatorActivity: async (creatorId, params) => {
      const data = await HttpClient.get(
        API_ENDPOINTS.AGENCY_CREATOR_ACTIVITY(creatorId),
        params,
      );
      return {
        ...data,
        activities:
          data.activities?.map((entry) => ({
            ...entry,
            activity_date: entry.activity_date
              ? new Date(entry.activity_date)
              : null,
          })) || [],
      };
    },
    claimInvoiceForCreator: async (invoiceId) => {
      return await HttpClient.post(
        API_ENDPOINTS.AGENCY_CLAIM_INVOICE(invoiceId),
      );
    },

    getClaimableInvoices: () =>
      HttpClient.get(API_ENDPOINTS.AGENCY_CLAIMABLE_INVOICES), // ADD THIS
  };

  social = {
    follow: (data) => HttpClient.post(API_ENDPOINTS.SOCIAL_FOLLOW, data),
    unfollow: (username) =>
      HttpClient.delete(`${API_ENDPOINTS.SOCIAL_UNFOLLOW}/${username}`),
    getFollowers: (username) =>
      HttpClient.get(`${API_ENDPOINTS.FETCH_FOLLOWERS}/${username}`),
    getFollowing: (username) =>
      HttpClient.get(`${API_ENDPOINTS.FETCH_FOLLOWING}/${username}`),
    isFollowing: (username) =>
      HttpClient.get(`${API_ENDPOINTS.SOCIAL_IS_FOLLOWING}/${username}`),
  };

  // Add this to your existing Client class
  emailCampaigns = {
    // List campaigns with optional filters
    list: (filters = {}) => {
      const params = new URLSearchParams(filters).toString();
      return HttpClient.get(
        `${API_ENDPOINTS.ADMIN_CAMPAIGNS}${params ? `?${params}` : ""}`,
      );
    },
    // Get single campaign details
    get: (campaignId) => {
      return HttpClient.get(API_ENDPOINTS.ADMIN_CAMPAIGN_DETAIL(campaignId));
    },
    // Create new campaign
    create: (data) => {
      return HttpClient.post(API_ENDPOINTS.ADMIN_CAMPAIGNS, data);
    },
    // Update campaign
    update: (campaignId, data) => {
      return HttpClient.put(
        API_ENDPOINTS.ADMIN_CAMPAIGN_DETAIL(campaignId),
        data,
      );
    },
    // Delete campaign
    delete: (campaignId) => {
      return HttpClient.delete(API_ENDPOINTS.ADMIN_CAMPAIGN_DETAIL(campaignId));
    },
    // Get campaign analytics
    getAnalytics: (campaignId) => {
      return HttpClient.get(API_ENDPOINTS.ADMIN_CAMPAIGN_ANALYTICS(campaignId));
    },
    // Get campaign recipients
    getRecipients: (campaignId, filters = {}) => {
      const params = new URLSearchParams(filters).toString();
      return HttpClient.get(
        `${API_ENDPOINTS.ADMIN_CAMPAIGN_RECIPIENTS(campaignId)}${params ? `?${params}` : ""}`,
      );
    },
    // Send test email
    sendTest: (campaignId, data) => {
      return HttpClient.post(
        API_ENDPOINTS.ADMIN_CAMPAIGN_TEST(campaignId),
        data,
      );
    },
    // Get templates
    getTemplates: () => {
      return HttpClient.get(API_ENDPOINTS.ADMIN_CAMPAIGNS_TEMPLATES);
    },
    // Create from template
    createFromTemplate: (templateId, overrides = {}) => {
      return HttpClient.post(
        API_ENDPOINTS.ADMIN_CAMPAIGNS_TEMPLATE(templateId),
        overrides,
      );
    },
    // Get campaign types
    getCampaignTypes: () => {
      return HttpClient.get(API_ENDPOINTS.ADMIN_CAMPAIGNS_TYPES);
    },
    // Get audience types
    getAudienceTypes: () => {
      return HttpClient.get(API_ENDPOINTS.ADMIN_CAMPAIGNS_AUDIENCE_TYPES);
    },
  };

  castingCalls = {
    // Brand/User Routes
    list: async (params = {}) => {
      const response = await HttpClient.get(
        API_ENDPOINTS.CASTING_CALLS_LIST,
        params,
      );
      return {
        ...response,
        casting_calls: response.casting_calls.map((call) => ({
          ...call,
          date_created: call.date_created ? new Date(call.date_created) : null,
          date_updated: call.date_updated ? new Date(call.date_updated) : null,
        })),
      };
    },

    create: (input) =>
      HttpClient.post(API_ENDPOINTS.CASTING_CALLS_POST_NEW, input),

    getById: async (id) => {
      const response = await HttpClient.get(
        API_ENDPOINTS.CASTING_CALL_DETAIL(id),
      );
      return {
        ...response,
        date_created: response.date_created
          ? new Date(response.date_created)
          : null,
        date_updated: response.date_updated
          ? new Date(response.date_updated)
          : null,
      };
    },

    invite: (id, input) =>
      HttpClient.post(API_ENDPOINTS.CASTING_CALL_INVITE(id), input),
    select: (id, input) =>
      HttpClient.post(API_ENDPOINTS.CASTING_CALL_SELECT(id), input),

    updateStatus: (id, input) =>
      HttpClient.put(API_ENDPOINTS.CASTING_CALL_UPDATE_STATUS(id), input),

    // Creator Routes
    creatorBrowse: async (params = {}) => {
      const response = await HttpClient.get(
        API_ENDPOINTS.CASTING_CALLS_CREATOR_BROWSE,
        params,
      );
      return {
        ...response,
        casting_calls: response.casting_calls.map((call) => ({
          ...call,
          date_created: call.date_created ? new Date(call.date_created) : null,
          date_updated: call.date_updated ? new Date(call.date_updated) : null,
        })),
      };
    },

    creatorApply: (id) =>
      HttpClient.post(API_ENDPOINTS.CASTING_CALLS_CREATOR_APPLY(id)),

    // General User Routes
    generalBrowse: async (params = {}) => {
      const response = await HttpClient.get(
        API_ENDPOINTS.CASTING_CALLS_GENERAL_BROWSE,
        params,
      );
      return {
        ...response,
        casting_calls: response.casting_calls.map((call) => ({
          ...call,
          date_created: call.date_created ? new Date(call.date_created) : null,
          date_updated: call.date_updated ? new Date(call.date_updated) : null,
        })),
      };
    },

    generalApply: (id) =>
      HttpClient.post(API_ENDPOINTS.CASTING_CALLS_GENERAL_APPLY(id)),

    // Public Routes
    getPublic: (slug) =>
      HttpClient.get(API_ENDPOINTS.CASTING_CALL_PUBLIC(slug), null, {
        ignoreAuthError: true,
      }),

    // Utility
    getWorkTypes: () => HttpClient.get(API_ENDPOINTS.CASTING_CALLS_WORK_TYPES),

    // Legacy (backwards compatibility)
    postNew: (input) =>
      HttpClient.post(API_ENDPOINTS.CASTING_CALLS_POST_NEW, input),
    creatorFetch: async (input) => {
      const response = await HttpClient.get(
        API_ENDPOINTS.creatorCastingCalls,
        input,
      );
      return {
        ...response,
        casting_calls: response.casting_calls.map((call) => ({
          ...call,
          date_created: call.date_created ? new Date(call.date_created) : null,
          date_updated: call.date_updated ? new Date(call.date_updated) : null,
        })),
      };
    },
    // In the castingCalls object, add this method:
    getPublicOrganizers: (slug) =>
      HttpClient.get(API_ENDPOINTS.CASTING_CALL_PUBLIC_ORGANIZERS(slug)),
    agencyApply: (castingCallId, data) =>
      HttpClient.post(
        API_ENDPOINTS.CASTING_CALLS_AGENCY_APPLY(castingCallId),
        data,
      ),

    // NEW: Check agency eligibility
    checkAgencyEligibility: (slug) =>
      HttpClient.get(API_ENDPOINTS.CASTING_CALLS_CHECK_AGENCY(slug)),

    getMyManaged: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.CASTING_CALLS_MY_MANAGED, params),
    // NEW: Management endpoints
    updateBudget: (id, data) =>
      HttpClient.put(API_ENDPOINTS.CASTING_CALL_UPDATE_BUDGET(id), data),

    assignPayout: (id, data) =>
      HttpClient.post(API_ENDPOINTS.CASTING_CALL_ASSIGN_PAYOUT(id), data),

    issuePayment: (id, data) =>
      HttpClient.post(API_ENDPOINTS.CASTING_CALL_ISSUE_PAYMENT(id), data),

    // Organizer management
    createOrganizer: (id, input) =>
      HttpClient.post(API_ENDPOINTS.CASTING_CALL_ORGANIZERS(id), input),

    assignOrganizerToApplicants: (castingCallId, organizerId, data) =>
      HttpClient.post(
        API_ENDPOINTS.CASTING_CALL_ASSIGN_ORGANIZER(castingCallId, organizerId),
        data,
      ),
    reject: (id, data) =>
      HttpClient.post(API_ENDPOINTS.CASTING_CALL_REJECT(id), data),
    assignPayout: (id, data) =>
      HttpClient.post(API_ENDPOINTS.CASTING_CALL_ASSIGN_PAYOUT(id), data),

    // NEW: Chat/conversation management
    checkConversation: (id, applicantId) =>
      HttpClient.get(
        `${API_ENDPOINTS.CASTING_CALL_CONVERSATIONS_CHECK(id)}?applicant_id=${applicantId}`,
      ),

    createConversation: (id, data) =>
      HttpClient.post(
        API_ENDPOINTS.CASTING_CALL_CONVERSATIONS_CREATE(id),
        data,
      ),

    getCreatorApplications: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.CASTING_CALLS_CREATOR_APPLICATIONS, params),

    validateBudget: (castingCallId, budget) =>
      HttpClient.post(
        API_ENDPOINTS.CASTING_CALL_BUDGET_VALIDATE(castingCallId),
        { budget },
      ),

    getBudgetStatus: (castingCallId) =>
      HttpClient.get(API_ENDPOINTS.CASTING_CALL_BUDGET_STATUS(castingCallId)),

    // Payout operations
    createPayout: (castingCallId, data) =>
      HttpClient.post(API_ENDPOINTS.CASTING_CALL_PAYOUTS(castingCallId), data),

    getPayouts: (castingCallId) =>
      HttpClient.get(API_ENDPOINTS.CASTING_CALL_PAYOUTS(castingCallId)),

    approvePayout: (payoutId) =>
      HttpClient.post(API_ENDPOINTS.CASTING_CALL_PAYOUT_APPROVE(payoutId)),

    cancelPayout: (payoutId) =>
      HttpClient.delete(API_ENDPOINTS.CASTING_CALL_PAYOUT_CANCEL(payoutId)),

    update: (id, data) =>
      HttpClient.put(API_ENDPOINTS.CASTING_CALL_UPDATE(id), data),
  };

  blocklist = {
    list: () => HttpClient.get(API_ENDPOINTS.BLOCKLIST),
    create: (input) => HttpClient.post(API_ENDPOINTS.BLOCKLIST, input),
    delete: (mail) => HttpClient.delete(API_ENDPOINTS.BLOCKED_CONTACT(mail)),
  };

  collections = {
    list: () => HttpClient.get(API_ENDPOINTS.collections),
    create: (input) => HttpClient.post(API_ENDPOINTS.collections_post, input),
    delete: (input) =>
      HttpClient.delete(API_ENDPOINTS.collections_delete(input)),
    popular: () => HttpClient.get(API_ENDPOINTS.collectionsPopular),
  };

  dealsIO = {
    create: (input) => HttpClient.post(API_ENDPOINTS.DEAL_IOS, input),
    list: () => HttpClient.get(API_ENDPOINTS.DEAL_IOS),
    fetch: (dealIOId) => HttpClient.get(API_ENDPOINTS.DEAL_IO_DETAIL(dealIOId)),
    update: (dealIOId, input) =>
      HttpClient.put(API_ENDPOINTS.DEAL_IO_DETAIL(dealIOId), input),
    delete: (dealIOId) =>
      HttpClient.delete(API_ENDPOINTS.DEAL_IO_DETAIL(dealIOId)),
    copy: (dealIOId) => HttpClient.post(API_ENDPOINTS.DEAL_IO_COPY(dealIOId)),
    share: (dealIOId, input) =>
      HttpClient.post(API_ENDPOINTS.DEAL_IO_SHARE(dealIOId), input), // New share functionality
    creatorFetch: () => HttpClient.get(API_ENDPOINTS.CREATOR_DEAL_FETCH),
    creatorSpecfetch: (dealIOId) =>
      HttpClient.get(API_ENDPOINTS.CREATOR_DEAL_IO_DETAIL(dealIOId)),
    creatorUpdate: (dealIOId, input) =>
      HttpClient.put(API_ENDPOINTS.CREATOR_DEAL_IO_DETAIL(dealIOId), input),
  };

  withdrawals = {
    // Admin methods (existing)
    adminList: async (input) => {
      const response = await HttpClient.get(
        API_ENDPOINTS.ADMIN_WITHDRAWALS,
        input,
      );
      return {
        ...response,
        withdrawals: response.withdrawals.map((wdw) => ({
          ...wdw,
          created_at: wdw.created_at ? new Date(wdw.created_at) : null,
          approved_at: wdw.approved_at ? new Date(wdw.approved_at) : null,
          payed_at: wdw.payed_at ? new Date(wdw.payed_at) : null,
        })),
      };
    },
    adminGet: async (id) => {
      const wdw = await HttpClient.get(
        API_ENDPOINTS.ADMIN_WITHDRAWALS_SPEC(id),
      );
      return {
        ...wdw,
        created_at: wdw.created_at ? new Date(wdw.created_at) : null,
        approved_at: wdw.approved_at ? new Date(wdw.approved_at) : null,
        payed_at: wdw.payed_at ? new Date(wdw.payed_at) : null,
      };
    },
    adminUpdate: (id, input) =>
      HttpClient.put(API_ENDPOINTS.ADMIN_WITHDRAWALS_SPEC(id), input),
    cancel: (id) => HttpClient.post(API_ENDPOINTS.ADMIN_CANCEL_WITHDRAWAL(id)),

    // Retry functionality
    retry: (id, options = {}) =>
      HttpClient.post(API_ENDPOINTS.ADMIN_WITHDRAWAL_RETRY(id), options),
    getRetryHistory: (id) =>
      HttpClient.get(API_ENDPOINTS.ADMIN_WITHDRAWAL_RETRY_HISTORY(id)),
    getRetryEligibility: (id) =>
      HttpClient.get(API_ENDPOINTS.ADMIN_WITHDRAWAL_RETRY_ELIGIBILITY(id)),
    processAutomated: (id, options = {}) =>
      HttpClient.post(
        API_ENDPOINTS.ADMIN_WITHDRAWAL_PROCESS_AUTOMATED(id),
        options,
      ),

    // Unified withdrawal methods (like invoices)
    request: (data) => HttpClient.post(API_ENDPOINTS.WITHDRAWAL_REQUEST, data),
    approve: (id) => HttpClient.post(API_ENDPOINTS.WITHDRAWAL_APPROVE(id)),
    process: (id, options = {}) =>
      HttpClient.post(API_ENDPOINTS.WITHDRAWAL_PROCESS(id), options, {
        ignoreAuthError: true,
      }),
    getStatus: (id) => HttpClient.get(API_ENDPOINTS.WITHDRAWAL_STATUS(id)),
    getDetails: (id) => HttpClient.get(API_ENDPOINTS.WITHDRAWAL_DETAILS(id)),
    list: (params) => HttpClient.get(API_ENDPOINTS.WITHDRAWAL_LIST, { params }),
    getBalance: (params) =>
      HttpClient.get(API_ENDPOINTS.WITHDRAWAL_BALANCE, params),
    getUserBalance: (userId) =>
      HttpClient.get(API_ENDPOINTS.USER_BALANCE(userId)),
    getPaymentServices: () =>
      HttpClient.get(API_ENDPOINTS.WITHDRAWAL_PAYMENT_SERVICES),
    batchProcess: (options = {}) =>
      HttpClient.post(API_ENDPOINTS.WITHDRAWAL_BATCH_PROCESS, options),
    convertMode: (id, mode, options = {}) => {
      const data = { mode, ...options };
      return HttpClient.post(API_ENDPOINTS.WITHDRAWAL_CONVERT_MODE(id), data);
    },
    convertToAutomated: (id, options = {}) =>
      HttpClient.post(
        API_ENDPOINTS.WITHDRAWAL_CONVERT_TO_AUTOMATED(id),
        options,
      ),
    convertToManual: (id, options = {}) =>
      HttpClient.post(API_ENDPOINTS.WITHDRAWAL_CONVERT_TO_MANUAL(id), options),
    getStats: () => HttpClient.get(API_ENDPOINTS.WITHDRAWAL_STATS),
    healthCheck: () => HttpClient.get(API_ENDPOINTS.WITHDRAWAL_HEALTH_CHECK),
    processAutomatedAdmin: (id, options = {}) =>
      HttpClient.post(API_ENDPOINTS.WITHDRAWAL_PROCESS(id), options, {
        ignoreAuthError: true,
      }),

    // User withdrawal methods (Phase 2.4)
    userRequest: (data) =>
      HttpClient.post(API_ENDPOINTS.USER_WITHDRAWAL_REQUEST, data),
    userList: (params) =>
      HttpClient.get(API_ENDPOINTS.USER_WITHDRAWAL_LIST, { params }),
    userGetDetails: (id) =>
      HttpClient.get(API_ENDPOINTS.USER_WITHDRAWAL_DETAILS(id)),
    userGetStatus: (id) =>
      HttpClient.get(API_ENDPOINTS.USER_WITHDRAWAL_STATUS(id)),
    userCancel: (id) =>
      HttpClient.post(API_ENDPOINTS.USER_WITHDRAWAL_CANCEL(id)),
    userGetHistory: (params) =>
      HttpClient.get(API_ENDPOINTS.USER_WITHDRAWAL_HISTORY, { params }),
    userGetLimits: () => HttpClient.get(API_ENDPOINTS.USER_WITHDRAWAL_LIMITS),
    userGetEligibility: () =>
      HttpClient.get(API_ENDPOINTS.USER_WITHDRAWAL_ELIGIBILITY),
    // Instant withdrawal (skip admin approval)
    userInstantRequest: (data) =>
      HttpClient.post(API_ENDPOINTS.USER_WITHDRAWAL_INSTANT, data),
    // Admin user balance method
    getAdminUserBalance: (userId) =>
      HttpClient.get(API_ENDPOINTS.ADMIN_USER_BALANCE(userId)),
    // Admin user balances dashboard
    getUserBalances: (params) =>
      HttpClient.get(API_ENDPOINTS.ADMIN_USER_BALANCES, params),
    getUserBalanceLedger: (userId, params) =>
      HttpClient.get(API_ENDPOINTS.ADMIN_USER_BALANCE_LEDGER(userId), params),
    getT2History: (params) =>
      HttpClient.get(API_ENDPOINTS.ADMIN_T2_HISTORY, params),
    zeroUserBalance: (globalUserId, data) => HttpClient.post(API_ENDPOINTS.ADMIN_USER_BALANCE_ZERO(globalUserId), data),
  };

  payoutResolver = {
  /**
   * Search approved / confirmed payouts for orphaned-payout attribution.
   * @param {{ search?: string, status?: string, page?: number, rows?: number }} params
   */
  searchPayouts: (params = {}) =>
    HttpClient.get(API_ENDPOINTS.ADMIN_RESOLVER_PAYOUTS, params),

  /**
   * Search GlobalUsers to find the target account to credit.
   * @param {{ search: string, limit?: number }} params
   */
  searchUsers: (params = {}) =>
    HttpClient.get(API_ENDPOINTS.ADMIN_RESOLVER_USERS, params),

  /**
   * Attribute selected payouts to a GlobalUser's balance and mark them paid.
   * @param {{ target_global_user_id: number, payout_ids: number[], reason?: string }} data
   */
  resolve: (data) =>
    HttpClient.post(API_ENDPOINTS.ADMIN_RESOLVER_RESOLVE, data),
};

  stripeACH = {
    // Onboard a Plaid-linked bank_ach wallet to Stripe as an external account (one-time setup)
    onboardBank: (data) =>
      HttpClient.post(API_ENDPOINTS.STRIPE_ACH_ONBOARD_BANK, data),
    // Check whether a bank account supports instant payouts
    checkInstantEligibility: (walletId) =>
      HttpClient.get(API_ENDPOINTS.STRIPE_ACH_CHECK_INSTANT, {
        wallet_id: walletId,
      }),
    // Get all bank_ach wallets for the current user (shows stripe_bank_onboarded flag)
    getBankAccounts: () =>
      HttpClient.get(API_ENDPOINTS.STRIPE_ACH_BANK_ACCOUNTS),
  };

  developer = {
    serviceList: async (parameters = null) => {
      const response = await HttpClient.get(
        API_ENDPOINTS.DEVELOPER_SERVICE,
        parameters,
      );
      return response.map((service) => ({
        ...service,
        date_created: service.date_created
          ? new Date(service.date_created)
          : null,
        date_updated: service.date_updated
          ? new Date(service.date_updated)
          : null,
        date_approved: service.date_approved
          ? new Date(service.date_approved)
          : null,
        date_approval_updated: service.date_approval_updated
          ? new Date(service.date_approval_updated)
          : null,
      }));
    },
    apikeyList: async () => {
      const response = await HttpClient.get(API_ENDPOINTS.DEVELOPER_KEY);
      return response.map((service) => ({
        ...service,
        date_created: service.date_created
          ? new Date(service.date_created)
          : null,
      }));
    },
    apikeyCreate: (input) =>
      HttpClient.post(API_ENDPOINTS.DEVELOPER_KEY, input),
    apikeyDelete: (id) =>
      HttpClient.delete(API_ENDPOINTS.DEVELOPER_KEY_SPEC(id)),
    serviceCreate: (input) =>
      HttpClient.post(API_ENDPOINTS.DEVELOPER_SERVICE, input),
    serviceUpdate: (id, input) =>
      HttpClient.put(API_ENDPOINTS.DEVELOPER_SERVICE_SPEC(id), input),
  };

  developerPublic = {
    subscriptionGet: (id) =>
      HttpClient.get(API_ENDPOINTS.DEVELOPER_SERVICE_SUBBED(id), null, {
        accept404: true,
      }),
    subscriptionPost: (id) =>
      HttpClient.post(API_ENDPOINTS.DEVELOPER_SERVICE_SUBBED(id)),
    subscriptionLog: (id) =>
      HttpClient.post(API_ENDPOINTS.DEVELOPER_SERVICE_LOG(id)),
    subscriptionUpdate: (id, input) =>
      HttpClient.post(API_ENDPOINTS.DEVELOPER_SERVICE_UPDATE(id), input),
    serviceGet: (id) =>
      HttpClient.get(API_ENDPOINTS.DEVELOPER_SERVICE_PUBLIC_SPEC(id)),
    serviceGetAll: (input) =>
      HttpClient.get(API_ENDPOINTS.DEVELOPER_SERVICE_PUBLIC, input),
  };

  bank = {
    // -------------------------------
    // PLAID (Existing)
    // -------------------------------
    /**
     * GET /plaid/create_link_token/creator
     * Retrieve a link_token for the front end to open Plaid Link (Creator).
     */
    getLinkTokenCreator: (username) =>
      HttpClient.get(
        `${API_ENDPOINTS.PLAID_CREATE_LINK_TOKEN_CREATOR}?username=${encodeURIComponent(username)}`,
      ),

    /**
     * GET /plaid/create_link_token/user
     * Retrieve a link_token for a user (not a creator).
     */
    getLinkTokenUser: () =>
      HttpClient.get(API_ENDPOINTS.PLAID_CREATE_LINK_TOKEN_USER),

    /**
     * POST /plaid/link/creator
     * Exchange a public_token for an access_token and link to a creator.
     * input: { public_token, entity_type, entity_id }
     */
    creatorLink: (input) =>
      HttpClient.post(API_ENDPOINTS.PLAID_LINK_CREATOR, input),

    /**
     * POST /plaid/link/user
     * Exchange a public_token for an access_token and link to a user.
     */
    userLink: (input) => HttpClient.post(API_ENDPOINTS.PLAID_LINK_USER, input),

    /**
     * GET /plaid/transactions
     * Fetch transactions for a given bank_account_id (and optional date range).
     */
    getTransactions: (params) =>
      HttpClient.get(API_ENDPOINTS.PLAID_TRANSACTIONS, { params }),

    /**
     * POST /plaid/send_transfer
     * Example of sending a transfer using Plaid's Transfer product.
     * input: { bank_account_id, amount, description? }
     */
    sendTransfer: (input) =>
      HttpClient.post(API_ENDPOINTS.PLAID_SEND_TRANSFER, input),

    // -------------------------------
    // PLAID ACH (NEW)
    // -------------------------------
    /**
     * POST /api/plaid/link-token
     * Create a Plaid Link token for bank account connection
     */
    createLinkToken: () => HttpClient.post(API_ENDPOINTS.PLAID_LINK_TOKEN),

    /**
     * POST /api/plaid/link-account
     * Link a bank account using Plaid Link
     * input: { public_token }
     */
    linkBankAccount: (input) =>
      HttpClient.post(API_ENDPOINTS.PLAID_LINK_ACCOUNT, input),

    /**
     * GET /api/plaid/bank-accounts
     * Get all bank accounts for the current user
     */
    getBankAccounts: () => HttpClient.get(API_ENDPOINTS.PLAID_BANK_ACCOUNTS),

    /**
     * POST /api/plaid/create-transfer
     * Create an ACH transfer
     * input: { bank_account_id, amount, transfer_type, description, ach_class }
     */
    createACHTransfer: (input) =>
      HttpClient.post(API_ENDPOINTS.PLAID_CREATE_TRANSFER, input),

    /**
     * GET /api/plaid/transfer-status/:transferId
     * Get the status of an ACH transfer
     */
    getTransferStatus: (transferId) =>
      HttpClient.get(API_ENDPOINTS.PLAID_TRANSFER_STATUS(transferId)),

    /**
     * POST /api/plaid/withdrawal/:withdrawalId/process-ach
     * Process a withdrawal using ACH transfer
     */
    processACHWithdrawal: (withdrawalId) =>
      HttpClient.post(API_ENDPOINTS.PLAID_WITHDRAWAL_PROCESS(withdrawalId)),

    /**
     * POST /api/plaid/deposit/:depositId/process-ach
     * Process a deposit using ACH transfer
     */
    processACHDeposit: (depositId) =>
      HttpClient.post(API_ENDPOINTS.PLAID_DEPOSIT_PROCESS(depositId)),

    /**
     * POST /api/plaid/batch-transfer
     * Process multiple ACH transfers in a batch
     * input: { transfers: [{ bank_account_id, amount, transfer_type, description }] }
     */
    processBatchTransfer: (input) =>
      HttpClient.post(API_ENDPOINTS.PLAID_BATCH_TRANSFER, input),

    /**
     * GET /api/plaid/health
     * Health check for Plaid integration
     */
    plaidHealth: () => HttpClient.get(API_ENDPOINTS.PLAID_HEALTH),

    // -------------------------------
    // STRIPE FINANCIAL CONNECTIONS
    // -------------------------------
    /** POST /api/stripe-fc/create-express-account — Create Express Connected Account */
    createExpressAccount: () =>
      HttpClient.post(API_ENDPOINTS.STRIPE_FC_CREATE_EXPRESS_ACCOUNT),

    /** POST /api/stripe-fc/express-onboarding-link — Get Stripe-hosted onboarding URL */
    getExpressOnboardingLink: (input) =>
      HttpClient.post(API_ENDPOINTS.STRIPE_FC_EXPRESS_ONBOARDING_LINK, input),

    /** GET /api/stripe-fc/account-status — Check Express account verification status */
    getAccountStatus: () =>
      HttpClient.get(API_ENDPOINTS.STRIPE_FC_ACCOUNT_STATUS),

    /** GET /api/stripe-fc/onboarding-status — Onboarding status + onboarding URL for ACH guard modal */
    getOnboardingStatus: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.STRIPE_FC_ONBOARDING_STATUS, { params }),

    /** POST /api/stripe-fc/create-session — Get client_secret for FC modal */
    createFCSession: () =>
      HttpClient.post(API_ENDPOINTS.STRIPE_FC_CREATE_SESSION),

    /** POST /api/stripe-fc/link-account — Link FC account to a GUWallet */
    linkFCAccount: (input) =>
      HttpClient.post(API_ENDPOINTS.STRIPE_FC_LINK_ACCOUNT, input),

    /** POST /api/stripe-fc/add-bank-to-connect — Add bank to Express account */
    addBankToConnect: (input) =>
      HttpClient.post(API_ENDPOINTS.STRIPE_FC_ADD_BANK_TO_CONNECT, input),

    /** POST /api/stripe-fc/create-platform-session — FC session without Connect */
    createPlatformFCSession: () =>
      HttpClient.post(API_ENDPOINTS.STRIPE_FC_CREATE_PLATFORM_SESSION),

    /** POST /api/stripe-fc/link-platform-account — Link FC bank to platform */
    linkPlatformAccount: (input) =>
      HttpClient.post(API_ENDPOINTS.STRIPE_FC_LINK_PLATFORM_ACCOUNT, input),

    /** POST /api/stripe-fc/manual-bank — Add bank via routing/account numbers */
    createManualBank: (input) =>
      HttpClient.post(API_ENDPOINTS.STRIPE_FC_MANUAL_BANK, input),

    /** POST /api/stripe-fc/reconcile-external-accounts — Pull banks linked during hosted onboarding into GUWallet */
    reconcileExternalAccounts: () =>
      HttpClient.post(API_ENDPOINTS.STRIPE_FC_RECONCILE_EXTERNAL),

    // =================== TRANSACTION LINKING API ===================

    // Transaction Linking Core
    createTransactionLink: (data) =>
      HttpClient.post(API_ENDPOINTS.TRANSACTION_LINKING_CREATE_LINK, data),
    linkDepositToWithdrawal: (data) =>
      HttpClient.post(
        API_ENDPOINTS.TRANSACTION_LINKING_DEPOSIT_WITHDRAWAL,
        data,
      ),
    getTransactionLinks: (transactionId) =>
      HttpClient.get(
        API_ENDPOINTS.TRANSACTION_LINKING_GET_LINKS(transactionId),
      ),
    getTransactionChain: (transactionId, maxDepth) =>
      HttpClient.get(
        API_ENDPOINTS.TRANSACTION_LINKING_GET_CHAIN(transactionId),
        { params: { max_depth: maxDepth } },
      ),
    reconcileTransactions: (params) =>
      HttpClient.get(API_ENDPOINTS.TRANSACTION_LINKING_RECONCILE, { params }),
    createBalanceTransfer: (data) =>
      HttpClient.post(API_ENDPOINTS.TRANSACTION_LINKING_BALANCE_TRANSFER, data),

    // Transaction Analytics
    getTransactionAnalyticsSummary: () =>
      HttpClient.get(API_ENDPOINTS.TRANSACTION_LINKING_ANALYTICS_SUMMARY),
    getTransactionFlowAnalytics: () =>
      HttpClient.get(API_ENDPOINTS.TRANSACTION_LINKING_ANALYTICS_FLOW),

    // Admin Transaction Linking
    bulkLinkTransactions: (data) =>
      HttpClient.post(API_ENDPOINTS.TRANSACTION_LINKING_BULK_LINK, data),
    validateTransactionLinks: (data) =>
      HttpClient.post(API_ENDPOINTS.TRANSACTION_LINKING_VALIDATE, data),

    // =================== DEPOSIT API ===================

    // Deposit Management
    createDeposit: (data) =>
      HttpClient.post(API_ENDPOINTS.DEPOSIT_CREATE, data),
    confirmDeposit: (depositId, data) =>
      HttpClient.post(API_ENDPOINTS.DEPOSIT_CONFIRM(depositId), data),
    getDeposits: (params) =>
      HttpClient.get(API_ENDPOINTS.DEPOSIT_LIST, { params }),

    // BlitzPay Merge
    mergeBlitzPayInvoices: (data) =>
      HttpClient.post(API_ENDPOINTS.BLITZPAY_INVOICES_MERGE, data),

    // -------------------------------
    // STRIPE (NEW)
    // -------------------------------
    /**
     * GET /creator/stripe/bank_accounts
     * Retrieve already-linked Stripe payment methods for a given entity.
     * input: { entity_type, entity_id }
     */
    getStripeBankAccounts: ({ entity_type, entity_id }) =>
      HttpClient.get(
        `${API_ENDPOINTS.STRIPE_GET_BANK_ACCOUNTS}?entity_type=${entity_type}&entity_id=${entity_id}`,
      ),

    /**
     * POST /creator/stripe/link
     * Attach a new Stripe PaymentMethod to a user/creator, store it in DB.
     * input: { entity_type, entity_id, stripe_customer_id, payment_method_id }
     */
    linkStripeBankAccount: (payload) =>
      HttpClient.post(API_ENDPOINTS.STRIPE_LINK, payload),
    createStripeSetupIntent: (stripe_customer_id) =>
      HttpClient.post(API_ENDPOINTS.STRIPE_CREATOR_SETUP_INTENT, {
        stripe_customer_id,
      }),

    // New checkout session methods
    createCheckoutSession: (paymentData) =>
      HttpClient.post(
        API_ENDPOINTS.STRIPE_CREATE_CHECKOUT_SESSION,
        paymentData,
      ),
    handleSuccess: (sessionData) =>
      HttpClient.post(API_ENDPOINTS.STRIPE_HANDLE_SUCCESS, sessionData),

    // -------------------------------
    // PAYPAL
    // -------------------------------
    /**
     * POST /services/paypal/create-order
     * Create a PayPal order for deposit
     * input: { amount }
     */
    createPayPalOrder: (input) =>
      HttpClient.post(API_ENDPOINTS.PAYPAL_CREATE_ORDER, input),

    /**
     * POST /services/paypal/capture-order
     * Capture a PayPal order after user approval
     * input: { order_id }
     */
    capturePayPalOrder: (input) =>
      HttpClient.post(API_ENDPOINTS.PAYPAL_CAPTURE_ORDER, input),

    /**
     * GET /services/paypal/status/:orderId
     * Check PayPal order status
     */
    getPayPalOrderStatus: (orderId) =>
      HttpClient.get(API_ENDPOINTS.PAYPAL_ORDER_STATUS(orderId)),

    /**
     * POST /services/paypal/create-payout
     * Create a PayPal payout for withdrawal
     * input: { amount, recipient_email }
     */
    createPayPalPayout: (input) =>
      HttpClient.post(API_ENDPOINTS.PAYPAL_CREATE_PAYOUT, input),

    /**
     * GET /services/paypal/payout-status/:batchId
     * Check PayPal payout status
     */
    getPayPalPayoutStatus: (batchId) =>
      HttpClient.get(API_ENDPOINTS.PAYPAL_PAYOUT_STATUS(batchId)),

    /**
     * GET /services/paypal/withdrawals
     * List PayPal withdrawals for current user
     */
    getPayPalWithdrawals: () =>
      HttpClient.get(API_ENDPOINTS.PAYPAL_WITHDRAWALS),
  };

  

  wise = {
    /** GET /api/wise/profile — profile status + auto-fill prefill data */
    getProfile: () => HttpClient.get(API_ENDPOINTS.WISE_PROFILE),
    /** POST /api/wise/profile — create one-time Wise customer profile */
    createProfile: (input) =>
      HttpClient.post(API_ENDPOINTS.WISE_PROFILE, input),
    /** GET /api/wise/account-requirements?source=USD&target=INR — per-country bank field schema */
    getAccountRequirements: (params) =>
      HttpClient.get(API_ENDPOINTS.WISE_ACCOUNT_REQUIREMENTS, params),
    /** POST /api/wise/account-requirements/refresh — re-fetch after refreshRequirementsOnChange field */
    refreshAccountRequirements: (input) =>
      HttpClient.post(API_ENDPOINTS.WISE_ACCOUNT_REQUIREMENTS_REFRESH, input),
    /** POST /api/wise/recipients — create Wise recipient + GUWallet */
    createRecipient: (input) =>
      HttpClient.post(API_ENDPOINTS.WISE_RECIPIENTS, input),
    /** GET /api/wise/recipients — list user's Wise-rail wallets */
    listRecipients: () => HttpClient.get(API_ENDPOINTS.WISE_RECIPIENTS),
    /** DELETE /api/wise/recipients/:id — soft-delete wallet + remove Wise recipient */
    deleteRecipient: (walletId) =>
      HttpClient.delete(API_ENDPOINTS.WISE_RECIPIENT_DETAIL(walletId)),
  };

  creditLedger = {
    fetch: (companyId) => HttpClient.get(API_ENDPOINTS.CREDIT_FETCH(companyId)),
    approve: (input) =>
      HttpClient.post(API_ENDPOINTS.BANK_CREDIT_REQUEST_APPROVE, input),
    postRequest: (input) =>
      HttpClient.post(API_ENDPOINTS.BANK_CREDIT_REQUEST, input),
    systemLiquidity: {
      get: () => HttpClient.get(API_ENDPOINTS.BANK_SYSTEM_LIQUID),
      update: (input) =>
        HttpClient.post(API_ENDPOINTS.BANK_SYSTEM_LIQUID, input),
    },
    debtInstrument: {
      // GET /bank/debt/instruments?company_id=#
      list: (params = {}) =>
        HttpClient.get(API_ENDPOINTS.BANK_DEBT_INSTRUMENTS, { params }),

      // GET /bank/debt/instruments/:id
      get: (id) => HttpClient.get(API_ENDPOINTS.BANK_DEBT_INSTRUMENT(id)),

      // POST /bank/debt/instruments
      create: (payload) =>
        HttpClient.post(API_ENDPOINTS.BANK_DEBT_INSTRUMENTS, payload),

      // PATCH /bank/debt/instruments/:id
      update: (id, payload) =>
        HttpClient.patch(API_ENDPOINTS.BANK_DEBT_INSTRUMENT(id), payload),
    },

    liquidityPools: {
      list: (params = {}) =>
        HttpClient.get(API_ENDPOINTS.CREDIT_LIQUIDITY_POOLS_LIST, { params }),

      get: (id) => HttpClient.get(API_ENDPOINTS.CREDIT_LIQUIDITY_POOL(id)),

      create: (data) =>
        HttpClient.post(API_ENDPOINTS.CREDIT_LIQUIDITY_POOLS_LIST, data),

      update: (id, data) =>
        HttpClient.put(API_ENDPOINTS.CREDIT_LIQUIDITY_POOL(id), data),

      delete: (id) =>
        HttpClient.delete(API_ENDPOINTS.CREDIT_LIQUIDITY_POOL(id)),
    },
  };

  balanceLedger = {
    // GET /balance/ledger/:user_id - Fetch balance ledger entries for a user
    fetch: (userId) => HttpClient.get(API_ENDPOINTS.BALANCE_LEDGER(userId)),

    // POST /bank/balance/request - Request balance operation
    postRequest: (input) =>
      HttpClient.post(API_ENDPOINTS.BALANCE_REQUEST, input),

    // POST /bank/balance/approve - Approve or deny a balance request
    approve: (input) => HttpClient.post(API_ENDPOINTS.BALANCE_APPROVE, input),

    // POST /bank/balance/operation - Perform direct balance operations
    operation: (input) =>
      HttpClient.post(API_ENDPOINTS.BALANCE_OPERATION, input),

    // NEW: GET /bank/balance/me - Get current authenticated user's balance
    fetchMyBalance: () => HttpClient.get(API_ENDPOINTS.BALANCE_ME),

    // NEW: POST /api/gu-balance/internal-transfer - Create internal transfer between users
    internalTransfer: (data) =>
      HttpClient.post(API_ENDPOINTS.GU_BALANCE_INTERNAL_TRANSFER, data),

    // POST /api/gu-balance/batch-transfer - Standard transfer service (gu_balance)
    batchTransfer: (data) =>
      HttpClient.post(API_ENDPOINTS.GU_BALANCE_BATCH_TRANSFER, data),

    // POST /api/gu-balance/t2a-batch-transfer - T2A service balance-funded batch (t2a, parallel)
    t2aBatchTransfer: (data) =>
      HttpClient.post(API_ENDPOINTS.GU_BALANCE_T2A_BATCH_TRANSFER, data),

    // External Transfers
    listExternalTransfers: (params) =>
      HttpClient.get(API_ENDPOINTS.GU_BALANCE_EXTERNAL_TRANSFERS, params),
    cancelExternalTransfer: (id) =>
      HttpClient.post(API_ENDPOINTS.GU_BALANCE_EXTERNAL_TRANSFER_CANCEL(id)),
    updateExternalTransfer: (id, data) =>
      HttpClient.patch(
        API_ENDPOINTS.GU_BALANCE_EXTERNAL_TRANSFER_UPDATE(id),
        data,
      ),
    claimExternalTransfer: (data) =>
      HttpClient.post(API_ENDPOINTS.GU_BALANCE_CLAIM_EXTERNAL_TRANSFER, data),

    // NEW: GET /bank/user/resolve/:userIdentifier - Debug/utility endpoint
    resolveUser: (userIdentifier) =>
      HttpClient.get(API_ENDPOINTS.USER_RESOLVE(userIdentifier)),

    // PayPal methods
    createPayPalOrder: (data) =>
      HttpClient.post(API_ENDPOINTS.PAYPAL_CREATE_ORDER, data),
    capturePayPalOrder: (data) =>
      HttpClient.post(API_ENDPOINTS.PAYPAL_CAPTURE_ORDER, data),
    getPayPalOrderStatus: (orderId) =>
      HttpClient.get(API_ENDPOINTS.PAYPAL_ORDER_STATUS(orderId)),
    createPayPalPayout: (data) =>
      HttpClient.post(API_ENDPOINTS.PAYPAL_CREATE_PAYOUT, data),
    getPayPalPayoutStatus: (batchId) =>
      HttpClient.get(API_ENDPOINTS.PAYPAL_PAYOUT_STATUS(batchId)),
    getPayPalWithdrawals: () =>
      HttpClient.get(API_ENDPOINTS.PAYPAL_WITHDRAWALS),

    // Stripe methods
    createStripeCheckoutSession: (data) =>
      HttpClient.post(API_ENDPOINTS.STRIPE_CREATE_CHECKOUT_SESSION, data),
    handleStripeSuccess: (data) =>
      HttpClient.post(API_ENDPOINTS.STRIPE_HANDLE_SUCCESS, data),
    createStripeRefund: (data) =>
      HttpClient.post(API_ENDPOINTS.STRIPE_REFUND, data),
    processStripeWithdrawal: (data) =>
      HttpClient.post(API_ENDPOINTS.STRIPE_PROCESS_WITHDRAWAL, data),

    t2aGift: (data) => HttpClient.post(API_ENDPOINTS.GU_BALANCE_T2A_GIFT, data),

    t2aGiftSuccess: (data) =>
  HttpClient.post(API_ENDPOINTS.GU_BALANCE_T2A_GIFT_SUCCESS, data),

    requestTransfer: (data) =>
  HttpClient.post(API_ENDPOINTS.GU_BALANCE_REQUEST_TRANSFER, data),

     getRequestByToken: (token) =>
      HttpClient.get(API_ENDPOINTS.GU_BALANCE_PAY_REQUEST(token), null, {
        ignoreAuthError: true,
      }),

    // POST /gu-balance/pay-request/:token — public, initiates PayPal/Stripe
    payRequestByToken: (token, data) =>
      HttpClient.post(API_ENDPOINTS.GU_BALANCE_PAY_REQUEST(token), data, {
        ignoreAuthError: true,
      }),
 
  };

  // Add this to your index file where you have other API clients
  surveys = {
    list: () => HttpClient.get(API_ENDPOINTS.LIST_SURVEYS),
    create: (input) => HttpClient.post(API_ENDPOINTS.CREATE_SURVEY, input),
    listPending: (userId) =>
      HttpClient.get(API_ENDPOINTS.LIST_PENDING_SURVEY(userId)),
    createResponse: (input) =>
      HttpClient.post(API_ENDPOINTS.CREATE_SURVEY_RESPONSE, input),
    update: (surveyId, input) =>
      HttpClient.put(API_ENDPOINTS.UPDATE_SURVEY(surveyId), input),
    delete: (surveyId) =>
      HttpClient.delete(API_ENDPOINTS.DELETE_SURVEY(surveyId)),
    getResponses: (surveyId) =>
      HttpClient.get(API_ENDPOINTS.GET_SURVEY_RESPONSES(surveyId)), // New method
  };

  blitzbolts = {
    list: () => HttpClient.get(API_ENDPOINTS.LIST_BLITZBOLTS),
    create: (input) => HttpClient.post(API_ENDPOINTS.CREATE_BLITZBOLT, input),
    listForUser: (userId, options = {}) => {
      const scope = options.scope === "public" ? "public" : "self";
      return HttpClient.get(API_ENDPOINTS.LIST_BLITZBOLTS_FOR_USER(userId), {
        scope,
      });
    },
    getAchievement: (userId, attainmentId) =>
      HttpClient.get(
        API_ENDPOINTS.GET_BLITZBOLT_ACHIEVEMENT(userId, attainmentId),
      ),
    shareUrl: (userId, attainmentId) =>
      `${window.location.origin}${API_ENDPOINTS.SHARE_BLITZBOLT(userId, attainmentId)}`,

    getLeaderboard: () => HttpClient.get(API_ENDPOINTS.BLITZBOLTS_LEADERBOARD),
  };

  // =================== NOTIFICATIONS (V2 - GLOBAL USER) ===================
  notifications = {
    // Default methods (V2 endpoints)
    list: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.NOTIFICATIONS_V2, params),
    listAll: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.NOTIFICATIONS_V2, params),
    listMinimal: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.NOTIFICATIONS_V2_MINIMAL, params),
    getCounts: () => HttpClient.get(API_ENDPOINTS.NOTIFICATIONS_V2_COUNTS),
    getUnreadCount: () =>
      HttpClient.get(API_ENDPOINTS.NOTIFICATIONS_V2_UNREAD_COUNT),
    markAsRead: (notificationId) =>
      HttpClient.put(API_ENDPOINTS.NOTIFICATION_V2_READ(notificationId)),
    markRead: (notificationId) =>
      HttpClient.put(API_ENDPOINTS.NOTIFICATION_V2_READ(notificationId)),
    markAllRead: () =>
      HttpClient.put(API_ENDPOINTS.NOTIFICATIONS_V2_MARK_ALL_READ),
    markSourceRead: (source) =>
      HttpClient.put(API_ENDPOINTS.NOTIFICATIONS_V2_MARK_SOURCE_READ(source)),
    dismiss: (notificationId) =>
      HttpClient.put(API_ENDPOINTS.NOTIFICATION_V2_DISMISS(notificationId)),
    delete: (notificationId) =>
      HttpClient.delete(API_ENDPOINTS.NOTIFICATION_V2_DELETE(notificationId)),
    clearAll: () => HttpClient.delete(API_ENDPOINTS.NOTIFICATIONS_V2_CLEAR_ALL),
    create: (payload) =>
      HttpClient.post(API_ENDPOINTS.NOTIFICATIONS_V2_CREATE, payload),
    createNotification: (payload) =>
      HttpClient.post(API_ENDPOINTS.NOTIFICATIONS_V2_CREATE, payload),
    registerDeviceToken: (payload) =>
      HttpClient.post(API_ENDPOINTS.DEVICE_TOKENS, payload),

    // V2 namespace (explicit V2 endpoints)
    v2: {
      list: (params = {}) =>
        HttpClient.get(API_ENDPOINTS.NOTIFICATIONS_V2, params),
      listMinimal: (params = {}) =>
        HttpClient.get(API_ENDPOINTS.NOTIFICATIONS_V2_MINIMAL, params),
      getCounts: () => HttpClient.get(API_ENDPOINTS.NOTIFICATIONS_V2_COUNTS),
      getUnreadCount: () =>
        HttpClient.get(API_ENDPOINTS.NOTIFICATIONS_V2_UNREAD_COUNT),
      markRead: (notificationId) =>
        HttpClient.put(API_ENDPOINTS.NOTIFICATION_V2_READ(notificationId)),
      markAllRead: () =>
        HttpClient.put(API_ENDPOINTS.NOTIFICATIONS_V2_MARK_ALL_READ),
      markSourceRead: (source) =>
        HttpClient.put(API_ENDPOINTS.NOTIFICATIONS_V2_MARK_SOURCE_READ(source)),
      dismiss: (notificationId) =>
        HttpClient.put(API_ENDPOINTS.NOTIFICATION_V2_DISMISS(notificationId)),
      delete: (notificationId) =>
        HttpClient.delete(API_ENDPOINTS.NOTIFICATION_V2_DELETE(notificationId)),
      clearAll: () =>
        HttpClient.delete(API_ENDPOINTS.NOTIFICATIONS_V2_CLEAR_ALL),
      create: (payload) =>
        HttpClient.post(API_ENDPOINTS.NOTIFICATIONS_V2_CREATE, payload),
      registerDeviceToken: (payload) =>
        HttpClient.post(API_ENDPOINTS.DEVICE_TOKENS, payload),
    },

    // Legacy namespace (old V1 endpoints)
    legacy: {
      getUnreadCount: () =>
        HttpClient.get(API_ENDPOINTS.NOTIFICATIONS_UNREAD_COUNT),
      listAll: (unreadOnly = false) =>
        HttpClient.get(API_ENDPOINTS.NOTIFICATIONS, {
          unread: unreadOnly ? 1 : 0,
        }),
      markAsRead: (notificationId) =>
        HttpClient.put(API_ENDPOINTS.NOTIFICATION_READ(notificationId)),
      createNotification: (payload) =>
        HttpClient.post(API_ENDPOINTS.POST_ALL_NOTIFICATIONS, payload),
      creatorGetUnreadCount: () =>
        HttpClient.get(API_ENDPOINTS.CREATOR_NOTIFICATIONS_UNREAD_COUNT),
      creatorListAll: (unreadOnly = false) =>
        HttpClient.get(API_ENDPOINTS.CREATOR_NOTIFICATIONS, {
          unread: unreadOnly ? 1 : 0,
        }),
      creatorMarkAsRead: (notificationId) =>
        HttpClient.put(
          `${API_ENDPOINTS.CREATOR_NOTIFICATIONS}/${notificationId}/read`,
        ),
      creatorCreateNotification: (payload) =>
        HttpClient.post(API_ENDPOINTS.CREATOR_POST_ALL_NOTIFICATIONS, payload),
      creatorGetUnreadCollabCount: () =>
        HttpClient.get(API_ENDPOINTS.CREATOR_NOTIFICATIONS_UNREAD_COLLAB_COUNT),
    },
  };

  bio = {
    // Get all bio pages for the authenticated creator
    list: () => HttpClient.get(API_ENDPOINTS.BIO),

    // Create a new bio page. Expects a JSON object with fields like slug, title, description, theme, and optionally items.
    create: (input) => HttpClient.post(API_ENDPOINTS.BIO, input),

    // Update an existing bio page using its slug. For example, update bio page with slug 'mybio'
    update: (slug, input) =>
      HttpClient.put(`${API_ENDPOINTS.BIO}${slug}`, input),

    getAvailableContacts: () => HttpClient.get(API_ENDPOINTS.BIO_CONTACTS),

    // List all bio pages for a given creator (agency route)
    listAgency: (creator_username) =>
      HttpClient.get(
        `${API_ENDPOINTS.BIO_AGENCY}?creator_username=${encodeURIComponent(creator_username)}`,
      ),

    // Create a bio page for a given creator (agency route)
    createAgency: (input) => HttpClient.post(API_ENDPOINTS.BIO_AGENCY, input),

    // Update a bio page for a given creator (agency route)
    updateAgency: (slug, input) =>
      HttpClient.put(`${API_ENDPOINTS.BIO_AGENCY}${slug}`, input),

    // Public fetch of a bio page. Uses the "@" prefix. For example, if slug is 'mybio', this hits GET /@mybio.
    getPublic: (slug) =>
      HttpClient.get(`${API_ENDPOINTS.PUBLIC_BIO}/${slug}`, undefined, {
        accept404: true,
      }),
  };

  contacts = {
    // existing CRUD
    list: () => HttpClient.get(API_ENDPOINTS.CONTACTS),
    create: (input) => HttpClient.post(API_ENDPOINTS.CONTACTS, input),
    update: (id, data) =>
      HttpClient.put(`${API_ENDPOINTS.CONTACTS}${id}`, data),
    remove: (id) => HttpClient.delete(`${API_ENDPOINTS.CONTACTS}${id}`),

    // ───────── comm-logs ─────────
    logs: {
      // GET /api/contacts/logs?contact_id=123
      list: (contactId) =>
        HttpClient.get(`${API_ENDPOINTS.CONTACTS}logs`, {
          params: { contact_id: contactId },
        }),

      // GET /api/contacts/logs
      listAll: () => HttpClient.get(`${API_ENDPOINTS.CONTACTS}logs`),

      // POST /api/contacts/logs
      create: (input) =>
        HttpClient.post(`${API_ENDPOINTS.CONTACTS}logs`, input),

      // PUT /api/contacts/logs/:id
      update: (id, input) =>
        HttpClient.put(`${API_ENDPOINTS.CONTACTS}logs/${id}`, input),

      // DELETE /api/contacts/logs/:id
      remove: (id) => HttpClient.delete(`${API_ENDPOINTS.CONTACTS}logs/${id}`),

      // GET /api/contacts/logs/summary
      summary: () => HttpClient.get(`${API_ENDPOINTS.CONTACTS}logs/summary`),
    },
  };

  zapfeed = {
    list: (params) => HttpClient.get(API_ENDPOINTS.ZAPFEED_V3, params),
    post: (payload) => HttpClient.post(API_ENDPOINTS.ZAPFEED_POST, payload),

    // Reaction methods
    addReaction: (postId, reactionType = "like") =>
      HttpClient.post(`${API_ENDPOINTS.ZAPFEED_V3}/posts/${postId}/reactions`, {
        reaction_type: reactionType,
      }),

    removeReaction: (postId) =>
      HttpClient.delete(
        `${API_ENDPOINTS.ZAPFEED_V3}/posts/${postId}/reactions`,
      ),

    getReactions: (postId) =>
      HttpClient.get(`${API_ENDPOINTS.ZAPFEED_V3}/posts/${postId}/reactions`),

    getUserPosts: (userId, params = {}) =>
      HttpClient.get(
        `${API_ENDPOINTS.ZAPFEED_V3}/users/${userId}/posts`,
        params,
      ),

    createReply: (postId, text) =>
      HttpClient.post(`${API_ENDPOINTS.ZAPFEED_V3}/posts/${postId}/reply`, {
        text,
      }),

    getReplies: (postId, params = {}) =>
      HttpClient.get(`${API_ENDPOINTS.ZAPFEED_V3}/posts/${postId}/replies`, {
        params,
      }),

    getThread: (postId) =>
      HttpClient.get(`${API_ENDPOINTS.ZAPFEED_V3}/posts/${postId}/thread`),
    getPublicThread: (postId) =>
      HttpClient.get(API_ENDPOINTS.ZAPFEED_PUBLIC_THREAD(postId), null, {
        ignoreAuthError: true,
      }),

    getPublicShareData: (postId) =>
      HttpClient.get(API_ENDPOINTS.ZAPFEED_PUBLIC_SHARE(postId), null, {
        ignoreAuthError: true,
      }),

    // Thread sharing helper - generates shareable URL
    getThreadShareUrl: (postId) => `${window.location.origin}/thread/${postId}`,

    // Social media share URLs
    getTwitterShareUrl: (postId, title, text) => {
      const url = API.zapfeed.getThreadShareUrl(postId);
      return `https://twitter.com/intent/tweet?text=${encodeURIComponent(`${title} - ${text}`)}&url=${encodeURIComponent(url)}`;
    },

    getFacebookShareUrl: (postId) => {
      const url = API.zapfeed.getThreadShareUrl(postId);
      return `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`;
    },

    getLinkedInShareUrl: (postId) => {
      const url = API.zapfeed.getThreadShareUrl(postId);
      return `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`;
    },

    getWhatsAppShareUrl: (postId, title) => {
      const url = API.zapfeed.getThreadShareUrl(postId);
      return `https://wa.me/?text=${encodeURIComponent(`${title} ${url}`)}`;
    },

    // Copy share URL to clipboard
    copyThreadUrl: async (postId) => {
      const url = API.zapfeed.getThreadShareUrl(postId);
      try {
        await navigator.clipboard.writeText(url);
        return true;
      } catch (err) {
        replaceConsoleError("Failed to copy URL", err);
        return false;
      }
    },
  };

  teams = {
    // Basic CRUD operations
    fetchAll: () => HttpClient.get(API_ENDPOINTS.TEAMS),
    create: (input) => HttpClient.post(API_ENDPOINTS.TEAMS, input),
    get: (id) => HttpClient.get(API_ENDPOINTS.TEAM_DETAILS(id)),
    update: (id, input) =>
      HttpClient.put(API_ENDPOINTS.TEAM_DETAILS(id), input),

    // Member management
    assignUser: (teamId, userId) =>
      HttpClient.post(API_ENDPOINTS.TEAM_ASSIGN_USER(teamId), {
        user_id: userId,
      }),
    removeUser: (teamId, userId) =>
      HttpClient.post(API_ENDPOINTS.TEAM_REMOVE_USER(teamId), {
        user_id: userId,
      }),

    // User teams
    getUserTeams: (userId) => HttpClient.get(API_ENDPOINTS.USER_TEAMS(userId)),
    // ── Spending power actions — plug directly into your React mutations
    // --------------------------------------------------------------
    getSpendingSummary: () =>
      HttpClient.get(API_ENDPOINTS.TEAMS_SPENDING_SUMMARY),

    allocateSpending: (teamId, body) =>
      HttpClient.post(API_ENDPOINTS.TEAM_ALLOCATE_SPENDING(teamId), body),
    useSpending: (teamId, body) =>
      HttpClient.post(API_ENDPOINTS.TEAM_USE_SPENDING(teamId), body),
    releaseSpending: (teamId, body) =>
      HttpClient.post(API_ENDPOINTS.TEAM_RELEASE_SPENDING(teamId), body),

    rebalanceSpending: (body) =>
      HttpClient.post(API_ENDPOINTS.TEAMS_REBALANCE_SPENDING, body),

    getMySpendingInfo: () => HttpClient.get(API_ENDPOINTS.MY_SPENDING_INFO),
    checkSpendingLimit: (amount) =>
      HttpClient.post(API_ENDPOINTS.CHECK_SPENDING_LIMIT, {
        amount: amount.toString(),
      }),
    recordSpending: (amount, description, referenceId) =>
      HttpClient.post(API_ENDPOINTS.RECORD_SPENDING, {
        amount: amount.toString(),
        description,
        reference_id: referenceId,
      }),

    // Enhanced team structure fetch
    fetchMyTeam: () => HttpClient.get(API_ENDPOINTS.MY_TEAM),
  };

  contracts = {
    // ───────── Template Management (Admin Only) ─────────
    getTemplates: () => HttpClient.get(API_ENDPOINTS.CONTRACTS_TEMPLATES),
    createTemplate: (input) =>
      HttpClient.post(API_ENDPOINTS.CONTRACTS_TEMPLATES, input),
    getTemplate: (id) => HttpClient.get(API_ENDPOINTS.CONTRACTS_TEMPLATE(id)),
    updateTemplate: (id, input) =>
      HttpClient.put(API_ENDPOINTS.CONTRACTS_TEMPLATE(id), input),
    deleteTemplate: (id) =>
      HttpClient.delete(API_ENDPOINTS.CONTRACTS_TEMPLATE(id)),

    // ───────── Enhanced Assignment Management ─────────
    assignContract: (input) =>
      HttpClient.post(API_ENDPOINTS.CONTRACTS_ASSIGN, input),
    getAssignments: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.CONTRACTS_ASSIGNMENTS, { params }),
    getAssignment: (id) =>
      HttpClient.get(API_ENDPOINTS.CONTRACTS_ASSIGNMENT(id)),
    sendReminders: (id) =>
      HttpClient.post(API_ENDPOINTS.CONTRACTS_ASSIGNMENT_REMIND(id)),

    // ───────── Universal Signature Management (Works for Regular Users + Creators) ─────────
    signContract: (id, body = {}) =>
      HttpClient.post(API_ENDPOINTS.CONTRACTS_SIGN(id), body),
    agreeToContract: (id, body = {}) =>
      HttpClient.post(API_ENDPOINTS.CONTRACTS_AGREE(id), body),
    declineContract: (id, reason) =>
      HttpClient.post(API_ENDPOINTS.CONTRACTS_DECLINE(id), { reason }),
    getMyPendingContracts: () =>
      HttpClient.get(API_ENDPOINTS.CONTRACTS_MY_CONTRACTS),
    getMySignatures: () =>
      HttpClient.get(API_ENDPOINTS.CONTRACTS_MY_SIGNATURES),

    // ───────── Enhanced Contract Details (Universal Access) ─────────
    getContractDetails: (signatureId) =>
      HttpClient.get(API_ENDPOINTS.CONTRACTS_DETAILS(signatureId)),
    getContractFromAssignment: (assignmentId) =>
      HttpClient.get(API_ENDPOINTS.CONTRACTS_ASSIGNMENT_DETAILS(assignmentId)),

    // ───────── Public / Token-based Access (No Auth Required) ─────────
    accessGet: (signatureId, token) =>
      HttpClient.get(
        API_ENDPOINTS.CONTRACTS_ACCESS(signatureId),
        { params: { token } },
        { ignoreAuthError: true },
      ),
    accessSign: (signatureId, token) =>
      HttpClient.post(
        API_ENDPOINTS.CONTRACTS_ACCESS_SIGN(signatureId),
        { token },
        { ignoreAuthError: true },
      ),
    accessAgree: (signatureId, token) =>
      HttpClient.post(
        API_ENDPOINTS.CONTRACTS_ACCESS_AGREE(signatureId),
        { token },
        { ignoreAuthError: true },
      ),
    accessDecline: (signatureId, token, reason) =>
      HttpClient.post(
        API_ENDPOINTS.CONTRACTS_ACCESS_DECLINE(signatureId),
        { token, reason },
        { ignoreAuthError: true },
      ),

    // ───────── External Contract Management ─────────
    createExternalSignature: (input) =>
      HttpClient.post(API_ENDPOINTS.CONTRACTS_EXTERNAL_CREATE, input),
    getExternalSignatures: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.CONTRACTS_EXTERNAL_SIGNATURES, { params }),
    getExternalSignaturesByEmail: (email) =>
      HttpClient.get(API_ENDPOINTS.CONTRACTS_EXTERNAL_BY_EMAIL(email)),
    getExternalSignatureStats: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.CONTRACTS_EXTERNAL_STATS, { params }),
    createBlitzPayExternalSignature: (input) =>
      HttpClient.post(API_ENDPOINTS.CONTRACTS_EXTERNAL_BLITZPAY_CREATE, input),
    bulkCreateBlitzPayExternalSignatures: (input) =>
      HttpClient.post(API_ENDPOINTS.CONTRACTS_EXTERNAL_BLITZPAY_BULK, input),
    checkBlitzPayExternalStatus: (email) =>
      HttpClient.get(API_ENDPOINTS.CONTRACTS_EXTERNAL_BLITZPAY_STATUS(email)),
    verifyExternalContractEmail: (input) =>
      HttpClient.post(API_ENDPOINTS.CONTRACTS_EXTERNAL_VERIFY_EMAIL, input),

    // ───────── External Contract Public Access (No Auth Required) ─────────
    getExternalContract: (signatureId, token) => {
      const base = API_ENDPOINTS.CONTRACTS_EXTERNAL_GET(signatureId);
      const url = `${base}?token=${encodeURIComponent(token)}`;
      return HttpClient.get(url, { ignoreAuthError: true });
    },
    signExternalContract: (signatureId, token) =>
      HttpClient.post(
        API_ENDPOINTS.CONTRACTS_EXTERNAL_SIGN(signatureId),
        { token },
        { ignoreAuthError: true },
      ),
    agreeExternalContract: (signatureId, token) =>
      HttpClient.post(
        API_ENDPOINTS.CONTRACTS_EXTERNAL_AGREE(signatureId),
        { token },
        { ignoreAuthError: true },
      ),
    declineExternalContract: (signatureId, token, reason) =>
      HttpClient.post(
        API_ENDPOINTS.CONTRACTS_EXTERNAL_DECLINE(signatureId),
        { token, reason },
        { ignoreAuthError: true },
      ),

    // ───────── NEW: Creator Management (Admin Only) ─────────
    assignBlitzPayTermsToCreators: (input) =>
      HttpClient.post(API_ENDPOINTS.CONTRACTS_CREATORS_ASSIGN_BLITZPAY, input),
    checkCreatorBlitzPayStatus: (creatorUsername) =>
      HttpClient.get(
        API_ENDPOINTS.CONTRACTS_CREATOR_BLITZPAY_STATUS(creatorUsername),
      ),

    blitzpayRpa: {
      // FIXED: Use POST with body instead of GET with URL param
      checkStatus: (creatorUsername, email, phone = null) =>
        HttpClient.post(
          API_ENDPOINTS.CONTRACTS_BLITZPAY_RPA_CHECK,
          {
            creator_username: creatorUsername,
            email,
            phone,
          },
          { ignoreAuthError: true },
        ),

      // FIXED: Correct endpoint
      getTemplate: () =>
        HttpClient.get(API_ENDPOINTS.CONTRACTS_BLITZPAY_RPA_TEMPLATE, null, {
          ignoreAuthError: true,
        }),

      // FIXED: Correct endpoint, supports both email and phone
      createAndSign: (data) =>
        HttpClient.post(
          API_ENDPOINTS.CONTRACTS_BLITZPAY_RPA_CREATE_AND_SIGN,
          data,
          { ignoreAuthError: true },
        ),

      // Admin method to setup/update RPA template
      setupTemplate: (data) =>
        HttpClient.post(API_ENDPOINTS.CONTRACTS_BLITZPAY_RPA_SETUP, data),

      getSignedRPAs: (params = {}) =>
        HttpClient.get(API_ENDPOINTS.CONTRACTS_BLITZPAY_RPA_SIGNED, { params }),

      // Export signed RPAs
      exportSignedRPAs: (params = {}) =>
        HttpClient.get(API_ENDPOINTS.CONTRACTS_BLITZPAY_RPA_SIGNED_EXPORT, {
          params,
        }),

      // Get signed RPA statistics
      getSignedStats: () =>
        HttpClient.get(API_ENDPOINTS.CONTRACTS_BLITZPAY_RPA_SIGNED_STATS),
    },
    // =================== USER TEMPLATE ROUTES ===================
    user: {
      // Template Management
      getMyTemplates: () =>
        HttpClient.get(API_ENDPOINTS.CONTRACTS_USER_TEMPLATES),
      createTemplate: (input) =>
        HttpClient.post(API_ENDPOINTS.CONTRACTS_USER_TEMPLATES, input),
      updateTemplate: (templateId, input) =>
        HttpClient.put(
          API_ENDPOINTS.CONTRACTS_USER_TEMPLATE_DETAIL(templateId),
          input,
        ),
      deleteTemplate: (templateId) =>
        HttpClient.delete(
          API_ENDPOINTS.CONTRACTS_USER_TEMPLATE_DETAIL(templateId),
        ),
      cloneTemplate: (templateId, data) =>
        HttpClient.post(
          API_ENDPOINTS.CONTRACTS_USER_TEMPLATE_CLONE(templateId),
          data,
        ),
      shareTemplate: (templateId) =>
        HttpClient.post(
          API_ENDPOINTS.CONTRACTS_USER_TEMPLATE_SHARE(templateId),
        ),

      // Assignment Operations
      assignTemplate: (data) =>
        HttpClient.post(API_ENDPOINTS.CONTRACTS_USER_ASSIGN, data),
      validateScope: (data) =>
        HttpClient.post(API_ENDPOINTS.CONTRACTS_USER_VALIDATE_SCOPE, data),
      searchForAssignment: (params) =>
        HttpClient.get(
          API_ENDPOINTS.CONTRACTS_USER_SEARCH_FOR_ASSIGNMENT,
          params,
        ),
      assignTemplateToExternalUsers: (data) =>
        HttpClient.post(API_ENDPOINTS.CONTRACTS_USER_ASSIGN_EXTERNAL, data),

      // Assignment Management
      getMyAssignments: (params = {}) =>
        HttpClient.get(API_ENDPOINTS.CONTRACTS_USER_MY_ASSIGNMENTS, params),
      getMyAssignmentDetail: (assignmentId) =>
        HttpClient.get(
          API_ENDPOINTS.CONTRACTS_USER_MY_ASSIGNMENT_DETAIL(assignmentId),
        ),
      updateMyAssignment: (assignmentId, data) =>
        HttpClient.put(
          API_ENDPOINTS.CONTRACTS_USER_MY_ASSIGNMENT_DETAIL(assignmentId),
          data,
        ),
      cancelMyAssignment: (assignmentId) =>
        HttpClient.delete(
          API_ENDPOINTS.CONTRACTS_USER_MY_ASSIGNMENT_DETAIL(assignmentId),
        ),
      remindAssignment: (assignmentId) =>
        HttpClient.post(
          API_ENDPOINTS.CONTRACTS_USER_MY_ASSIGNMENT_REMIND(assignmentId),
        ),
      getMyAssignmentStats: () =>
        HttpClient.get(API_ENDPOINTS.CONTRACTS_USER_MY_ASSIGNMENT_STATS),
    },

    // Helper methods for user template workflows
    userTemplates: {
      // Quick create personal template
      createPersonal: (name, contractBody, options = {}) =>
        HttpClient.post(API_ENDPOINTS.CONTRACTS_USER_TEMPLATES, {
          name,
          contract_body: contractBody,
          template_scope: "user",
          is_public: false,
          allowed_assignment_scopes: ["user"],
          ...options,
        }),

      // Create team template (for team leads)
      createTeam: (name, contractBody, teamId, options = {}) =>
        HttpClient.post(API_ENDPOINTS.CONTRACTS_USER_TEMPLATES, {
          name,
          contract_body: contractBody,
          template_scope: "team",
          is_public: options.isPublic || false,
          allowed_assignment_scopes: ["team", "user"],
          ...options,
        }),

      // Assign to self
      assignToSelf: (templateId, options = {}) =>
        HttpClient.post(API_ENDPOINTS.CONTRACTS_USER_ASSIGN, {
          template_id: templateId,
          scope: "user",
          target_global_user_id: null, // Backend will use current user
          ...options,
        }),

      // Assign to team (if team lead)
      assignToTeam: (templateId, teamId, options = {}) =>
        HttpClient.post(API_ENDPOINTS.CONTRACTS_USER_ASSIGN, {
          template_id: templateId,
          scope: "team",
          team_id: teamId,
          ...options,
        }),

      // Assign to specific creators
      assignToCreators: (templateId, creatorUsernames, options = {}) =>
        HttpClient.post(API_ENDPOINTS.CONTRACTS_USER_ASSIGN, {
          template_id: templateId,
          scope: "creators_specific",
          metadata: { creator_usernames: creatorUsernames },
          ...options,
        }),

      // Get templates by scope
      getPersonalTemplates: async () => {
        const data = await HttpClient.get(
          API_ENDPOINTS.CONTRACTS_USER_TEMPLATES,
        );
        return data.user_templates.filter((t) => t.template_scope === "user");
      },

      getTeamTemplates: async () => {
        const data = await HttpClient.get(
          API_ENDPOINTS.CONTRACTS_USER_TEMPLATES,
        );
        return data.user_templates.filter((t) => t.template_scope === "team");
      },

      // Check if user can edit template
      canEdit: async (templateId) => {
        try {
          const data = await HttpClient.get(
            API_ENDPOINTS.CONTRACTS_USER_TEMPLATES,
          );
          const template = data.user_templates.find((t) => t.id === templateId);
          return !!template;
        } catch {
          return false;
        }
      },
    },

    // ───────── Utility Functions ─────────
    validateDocument: (file) => {
      const fd = new FormData();
      fd.append("document", file);
      return HttpClient.post(API_ENDPOINTS.CONTRACTS_VALIDATE_DOCUMENT, fd, {
        multipart: true,
      });

      // API Client Extensions for User Contract Templates
    },

    // Enhanced contract URL generator
    contractUrl: (signatureId, token) =>
      token
        ? `${window.location.origin}/external-contract/${signatureId}?token=${encodeURIComponent(token)}`
        : `${window.location.origin}/external-contract/${signatureId}`,
    // Admin utility to generate public links
    getPublicLink: (id) =>
      HttpClient.post(API_ENDPOINTS.CONTRACTS_ACCESS_TOKEN(id)),

    // ───────── Analytics & Overview ─────────
    getCompanyOverview: (companyId) =>
      HttpClient.get(API_ENDPOINTS.CONTRACTS_COMPANY_OVERVIEW(companyId)),
    getTeamOverview: (teamId) =>
      HttpClient.get(API_ENDPOINTS.CONTRACTS_TEAM_OVERVIEW(teamId)),
    getSystemStats: () => HttpClient.get(API_ENDPOINTS.CONTRACTS_STATS),
    getAuditLogs: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.CONTRACTS_AUDIT, { params }),

    // ───────── Helper Methods for Different User Types ─────────
    creator: {
      getPendingContracts: () =>
        HttpClient.get(API_ENDPOINTS.CONTRACTS_MY_CONTRACTS),
      getContractHistory: () =>
        HttpClient.get(API_ENDPOINTS.CONTRACTS_MY_SIGNATURES),
      signContract: (id) => HttpClient.post(API_ENDPOINTS.CONTRACTS_SIGN(id)),
      agreeToContract: (id) =>
        HttpClient.post(API_ENDPOINTS.CONTRACTS_AGREE(id)),
      declineContract: (id, reason) =>
        HttpClient.post(API_ENDPOINTS.CONTRACTS_DECLINE(id), { reason }),
    },

    // Enhanced assignment helper for different scopes
    assignToCreators: (templateId, options = {}) => {
      return HttpClient.post(API_ENDPOINTS.CONTRACTS_ASSIGN, {
        template_id: templateId,
        scope: options.all ? "creators_all" : "creators_specific",
        creator_usernames: options.creator_usernames || [],
        message: options.message || "Please review and accept these terms",
        due_date: options.due_date,
        is_mandatory: options.is_mandatory !== false,
        auto_remind: options.auto_remind !== false,
        reminder_frequency_days: options.reminder_frequency_days || 7,
        metadata: options.metadata || {},
      });
    },

    assignToCompany: (templateId, companyId, options = {}) => {
      return HttpClient.post(API_ENDPOINTS.CONTRACTS_ASSIGN, {
        template_id: templateId,
        scope: "company",
        company_id: companyId,
        ...options,
      });
    },

    assignToTeam: (templateId, teamId, options = {}) => {
      return HttpClient.post(API_ENDPOINTS.CONTRACTS_ASSIGN, {
        template_id: templateId,
        scope: "team",
        team_id: teamId,
        ...options,
      });
    },

    assignToUser: (templateId, userId, options = {}) => {
      return HttpClient.post(API_ENDPOINTS.CONTRACTS_ASSIGN, {
        template_id: templateId,
        scope: "user",
        user_id: userId,
        ...options,
      });
    },

    assignBlitzPayTerms: (templateId, options = {}) => {
      return HttpClient.post(API_ENDPOINTS.CONTRACTS_CREATORS_ASSIGN_BLITZPAY, {
        template_id: templateId,
        creator_usernames: options.creator_usernames,
        message:
          options.message ||
          "Please review and accept the BlitzPay Terms & Conditions",
        due_date: options.due_date,
        auto_remind: options.auto_remind !== false,
        reminder_frequency_days: options.reminder_frequency_days || 7,
      });
    },

    getCreatorRPAStatus: (creatorUsername) =>
      HttpClient.get(
        API_ENDPOINTS.CONTRACTS_BLITZPAY_RPA_STATUS(creatorUsername),
      ),

    assignRPAToCreator: (creatorUsername, data = {}) =>
      HttpClient.post(
        API_ENDPOINTS.CONTRACTS_BLITZPAY_RPA_ASSIGN(creatorUsername),
        data,
      ),

    resendRPANotification: (signatureId) =>
      HttpClient.post(API_ENDPOINTS.CONTRACTS_BLITZPAY_RPA_RESEND(signatureId)),

    // ───────── Migration Utility Routes ─────────
    getMigrationStatus: () => HttpClient.get("/contracts/migration/status"),
    prepareMigration: () => HttpClient.post("/contracts/migration/prepare"),
  };

  // Creator onboarding API client
  creatorOnboarding = {
    submitPaymentInfo: (data) =>
      HttpClient.post(API_ENDPOINTS.CREATOR_PAYMENT_INFO, data),
    getPaymentInfo: () => HttpClient.get(API_ENDPOINTS.CREATOR_PAYMENT_INFO),
    verifyPaymentSetup: () =>
      HttpClient.get(API_ENDPOINTS.CREATOR_VERIFY_PAYMENT_SETUP),
    getProgress: () =>
      HttpClient.get(API_ENDPOINTS.CREATOR_ONBOARDING_PROGRESS),
    getW9Form: () => HttpClient.get(API_ENDPOINTS.CREATOR_W9_FORM),
    validateTaxId: (data) =>
      HttpClient.post(API_ENDPOINTS.CREATOR_VALIDATE_TAX_ID, data),
    validateBusinessType: (data) =>
      HttpClient.post(API_ENDPOINTS.CREATOR_VALIDATE_BUSINESS_TYPE, data),
    validateTaxClassification: (data) =>
      HttpClient.post(API_ENDPOINTS.CREATOR_VALIDATE_TAX_CLASSIFICATION, data),
  };

  // Spending power API client
  spendingPower = {
    fundInvoice: (id, data) =>
      HttpClient.post(API_ENDPOINTS.SPENDING_POWER_FUND_INVOICE(id), data),
    checkWithdrawalEligibility: (id) =>
      HttpClient.get(API_ENDPOINTS.SPENDING_POWER_WITHDRAWAL_ELIGIBILITY(id)),
    createWithdrawal: (id, data) =>
      HttpClient.post(API_ENDPOINTS.SPENDING_POWER_CREATE_WITHDRAWAL(id), data),
    getSummary: () => HttpClient.get(API_ENDPOINTS.SPENDING_POWER_SUMMARY),
    getFundedInvoices: (params) =>
      HttpClient.get(API_ENDPOINTS.SPENDING_POWER_FUNDED_INVOICES, { params }),
    getAnalytics: () => HttpClient.get(API_ENDPOINTS.SPENDING_POWER_ANALYTICS),

    // Team-based Spending Power Methods
    allocateTeamSpending: (teamId, data) =>
      HttpClient.post(API_ENDPOINTS.SPENDING_POWER_TEAM_ALLOCATE(teamId), data),
    getTeamSummary: (teamId) =>
      HttpClient.get(API_ENDPOINTS.SPENDING_POWER_TEAM_SUMMARY(teamId)),
    getTeamFundedInvoices: (teamId) =>
      HttpClient.get(API_ENDPOINTS.SPENDING_POWER_TEAM_FUNDED_INVOICES(teamId)),

    // Enhanced Withdrawal Linkage
    getInvoiceWithdrawalStatus: (invoiceId) =>
      HttpClient.get(
        API_ENDPOINTS.SPENDING_POWER_INVOICE_WITHDRAWAL_STATUS(invoiceId),
      ),
    processWithdrawal: (invoiceId) =>
      HttpClient.post(
        API_ENDPOINTS.SPENDING_POWER_PROCESS_WITHDRAWAL(invoiceId),
      ),
  };

  // Admin API client
  admin = {
    updateAiNegotiationsValueForCreator: (creatorUserName, booleanValue) =>
      HttpClient.put(API_ENDPOINTS.ADMIN_CREATOR_UPDATE_AI(creatorUserName), {
        booleanValue: booleanValue,
      }),
    listWithdrawals: (params) =>
      HttpClient.get(API_ENDPOINTS.ADMIN_WITHDRAWALS, { params }),
    cancelWithdrawal: (id) =>
      HttpClient.post(API_ENDPOINTS.ADMIN_CANCEL_WITHDRAWAL(id)),
    getCreatorOnboarding: (id) =>
      HttpClient.get(API_ENDPOINTS.ADMIN_CREATOR_ONBOARDING(id)),
    verifyCreator: (id) =>
      HttpClient.post(API_ENDPOINTS.ADMIN_VERIFY_CREATOR(id)),
    getSpendingPowerSummary: (id) =>
      HttpClient.get(API_ENDPOINTS.ADMIN_SPENDING_POWER_SUMMARY(id)),
    fundInvoice: (id, data) =>
      HttpClient.post(API_ENDPOINTS.ADMIN_FUND_INVOICE(id), data),
    getCreatorPaymentInfo: (creatorId) =>
      HttpClient.get(API_ENDPOINTS.ADMIN_CREATOR_PAYMENT_INFO(creatorId)),

    // Company Bank Accounts (for ACH reimbursements via Plaid)
    createCompanyBankLinkToken: (data) =>
      HttpClient.post(API_ENDPOINTS.ADMIN_COMPANY_BANK_LINK_TOKEN, data),
    createCompanyBankAccount: (data) =>
      HttpClient.post(API_ENDPOINTS.ADMIN_COMPANY_BANK_ACCOUNTS, data),
    getCompanyBankAccounts: (companyId) =>
      HttpClient.get(
        API_ENDPOINTS.ADMIN_COMPANY_BANK_ACCOUNTS_BY_COMPANY(companyId),
      ),
    updateCompanyBankAccount: (accountId, data) =>
      HttpClient.put(API_ENDPOINTS.ADMIN_COMPANY_BANK_ACCOUNT(accountId), data),
    deactivateCompanyBankAccount: (accountId) =>
      HttpClient.delete(API_ENDPOINTS.ADMIN_COMPANY_BANK_ACCOUNT(accountId)),
    deleteCompanyBankAccount: (accountId) =>
      HttpClient.delete(
        `${API_ENDPOINTS.ADMIN_COMPANY_BANK_ACCOUNT(accountId)}?permanent=true`,
      ),
    verifyCompanyBankAccount: (accountId, data) =>
      HttpClient.post(
        API_ENDPOINTS.ADMIN_COMPANY_BANK_ACCOUNT_VERIFY(accountId),
        data,
      ),
    // Dwolla Integration
    getCompanyDwollaStatus: (companyId) =>
      HttpClient.get(API_ENDPOINTS.ADMIN_COMPANY_DWOLLA_STATUS(companyId)),
    onboardCompanyDwolla: (companyId, data) =>
      HttpClient.post(
        API_ENDPOINTS.ADMIN_COMPANY_DWOLLA_ONBOARD(companyId),
        data,
      ),

    // Platform Payment Methods (for Stripe payout operations)
    listPlatformPaymentMethods: () =>
      HttpClient.get(API_ENDPOINTS.ADMIN_PLATFORM_PAYMENT_METHODS),
    createPlatformPaymentMethod: (data) =>
      HttpClient.post(API_ENDPOINTS.ADMIN_PLATFORM_PAYMENT_METHODS, data),
    updatePlatformPaymentMethod: (methodId, data) =>
      HttpClient.put(
        API_ENDPOINTS.ADMIN_PLATFORM_PAYMENT_METHOD(methodId),
        data,
      ),
    deactivatePlatformPaymentMethod: (methodId) =>
      HttpClient.delete(API_ENDPOINTS.ADMIN_PLATFORM_PAYMENT_METHOD(methodId)),

    // Plaid Link for Platform Payment Methods
    createPlaidLinkToken: () =>
      HttpClient.post(
        API_ENDPOINTS.ADMIN_PLATFORM_PAYMENT_METHODS_PLAID_LINK_TOKEN,
      ),
    createPlaidPaymentMethod: (data) =>
      HttpClient.post(API_ENDPOINTS.ADMIN_PLATFORM_PAYMENT_METHODS_PLAID, data),
  };

  systemConfig = {
    getConfig: () => HttpClient.get(API_ENDPOINTS.ADMIN_SYSTEM_CONFIG),
    setAutoPayments: (enabled) =>
      HttpClient.put(API_ENDPOINTS.ADMIN_SYSTEM_CONFIG_AUTO_PAYMENTS, {
        enabled,
      }),
    setClaimPayoutAutomation: (enabled) =>
      HttpClient.put(
        API_ENDPOINTS.ADMIN_SYSTEM_CONFIG_CLAIM_PAYOUT_AUTOMATION,
        { enabled },
      ),
  };

  stripe = {
    createCheckoutSession: (data) =>
      HttpClient.post(API_ENDPOINTS.STRIPE_CREATE_CHECKOUT_SESSION, data),
    handleSuccess: (data) =>
      HttpClient.post(API_ENDPOINTS.STRIPE_HANDLE_SUCCESS, data),
    webhook: (data) => HttpClient.post(API_ENDPOINTS.STRIPE_WEBHOOK, data),
    processWithdrawal: (data) =>
      HttpClient.post(API_ENDPOINTS.STRIPE_PROCESS_WITHDRAWAL, data),
  };

  paypal = {
    createOrder: (data) =>
      HttpClient.post(API_ENDPOINTS.PAYPAL_CREATE_ORDER, data),
    captureOrder: (data) =>
      HttpClient.post(API_ENDPOINTS.PAYPAL_CAPTURE_ORDER, data),
    getOrderStatus: (orderId) =>
      HttpClient.get(API_ENDPOINTS.PAYPAL_ORDER_STATUS(orderId)),
    createPayout: (data) =>
      HttpClient.post(API_ENDPOINTS.PAYPAL_CREATE_PAYOUT, data),
    getPayoutStatus: (batchId) =>
      HttpClient.get(API_ENDPOINTS.PAYPAL_PAYOUT_STATUS(batchId)),
    getWithdrawals: () => HttpClient.get(API_ENDPOINTS.PAYPAL_WITHDRAWALS),
    processWithdrawal: (withdrawalId) =>
      HttpClient.post(API_ENDPOINTS.PAYPAL_PROCESS_WITHDRAWAL, {
        withdrawal_id: withdrawalId,
      }),
  };

  globalUsers = {
    // Get public info for referral display
    getPublicInfo: (globalUserId) =>
      HttpClient.get(API_ENDPOINTS.GLOBAL_USER_PUBLIC(globalUserId)),

    // Claim invite and create account
    claimInvite: (input) =>
      HttpClient.post(API_ENDPOINTS.GLOBAL_USER_INVITE_CLAIM, input),
    claimCreatorInvite: (input) =>
      HttpClient.post(API_ENDPOINTS.GLOBAL_USER_CREATOR_INVITE_CLAIM, input),

    // Generate invite link for current user (dialog)
    generateInvite: (input) =>
      HttpClient.post(API_ENDPOINTS.GLOBAL_USER_GENERATE_INVITE, input),

    // Quick invite generation (for quick buttons)
    quickInvite: (accountType) =>
      HttpClient.post(API_ENDPOINTS.GLOBAL_USER_QUICK_INVITE(accountType)),

    // Copy invite link (for copy functionality)
    copyInvite: (accountType) =>
      HttpClient.post(API_ENDPOINTS.GLOBAL_USER_COPY_INVITE(accountType)),

    generateInviteGen: (input) =>
      HttpClient.post(API_ENDPOINTS.GLOBAL_USER_GENERATE_INVITE_GEN, input),

    // Get referral statistics
    getReferrals: (params) =>
      HttpClient.get(API_ENDPOINTS.GLOBAL_USER_REFERRALS, params),

    // Get user profile
    getProfile: () => HttpClient.get(API_ENDPOINTS.GLOBAL_USER_PROFILE),

    // Get activity summary
    getActivitySummary: () =>
      HttpClient.get(API_ENDPOINTS.GLOBAL_USER_ACTIVITY_SUMMARY),

    // Get referral activity with optional params
    getReferralActivity: (params) =>
      HttpClient.get(API_ENDPOINTS.GLOBAL_USER_REFERRALS_ACTIVITY, params),

    // Single referral detail by referral id
    getReferralDetailById: (referralId, params) =>
      HttpClient.get(
        API_ENDPOINTS.GLOBAL_USER_REFERRAL_DETAIL_BY_ID(referralId),
        params,
      ),
    grantReferralAccess: (input) =>
      HttpClient.post(
        API_ENDPOINTS.GLOBAL_USER_REFERRAL_ACCESS_GRANT_V2,
        input,
      ),
    revokeReferralAccess: (params) =>
      HttpClient.delete(
        API_ENDPOINTS.GLOBAL_USER_REFERRAL_ACCESS_REVOKE_V2,
        params,
      ),
    listAccessGrants: (params) =>
      HttpClient.get(API_ENDPOINTS.GLOBAL_USER_REFERRAL_ACCESS_LIST, params),

    // Revoke commission access from referral
    revokeReferralAccess: (referralId, params) =>
      HttpClient.delete(
        API_ENDPOINTS.GLOBAL_USER_REFERRAL_ACCESS_REVOKE(referralId),
        params,
      ),

    // Get all referrals current user has commission access to
    getMyReferralAccess: () =>
      HttpClient.get(API_ENDPOINTS.GLOBAL_USER_REFERRAL_MY_ACCESS),

    // Search for global users (for internal transfers, autocomplete, etc.)
    search: (params) =>
      HttpClient.get(API_ENDPOINTS.GLOBAL_USER_SEARCH, params),
    getByUserId: (userId) =>
      HttpClient.get(API_ENDPOINTS.GLOBAL_USER_BY_USER_ID(userId)),
    
    getByCreatorUsername: (username) =>
      HttpClient.get(API_ENDPOINTS.GLOBAL_USER_BY_CREATOR_USERNAME(username)),
  };

  projects = {
    // Archive both campaigns and partnerships
    archive: (input) => HttpClient.post(API_ENDPOINTS.PROJECT_ARCHIVE, input),

    // Commissions
    commissions: () => HttpClient.get(API_ENDPOINTS.COMMISSIONS),
    getAllCommissions: () => HttpClient.get(API_ENDPOINTS.COMMISSIONS_All),

    // Misc Projects
    miscProjects: {
      list: () => HttpClient.get(API_ENDPOINTS.MISC_PROJECTS),
      create: (input) =>
        HttpClient.post(API_ENDPOINTS.MISC_PROJECTS_CREATE, input),
    },
  };

  accountLinking = {
    // Get current account status and linking options
    getStatus: () => HttpClient.get(API_ENDPOINTS.ACCOUNT_STATUS),

    // Link existing creator account to current user
    linkCreator: (input) =>
      HttpClient.post(API_ENDPOINTS.ACCOUNT_LINK_CREATOR, input),

    // Create new creator account for current user
    createCreator: (input) =>
      HttpClient.post(API_ENDPOINTS.ACCOUNT_CREATE_CREATOR, input),

    // Create user account for current creator
    createUser: (input) =>
      HttpClient.post(API_ENDPOINTS.ACCOUNT_CREATE_USER, input),

    // Switch between user and creator contexts
    switchContext: (contextType) =>
      HttpClient.post(API_ENDPOINTS.ACCOUNT_SWITCH_CONTEXT(contextType)),

    // Check if creator username is available
    checkUsername: (username) =>
      HttpClient.get(API_ENDPOINTS.ACCOUNT_CHECK_USERNAME(username)),

    // Get list of companies for user account creation
    getCompanies: () => HttpClient.get(API_ENDPOINTS.ACCOUNT_COMPANIES),
  };

  // Add these methods to your existing noa object in API/index.js

  noa = {
    // Existing admin methods
    list: () => HttpClient.get(API_ENDPOINTS.NOA),
    create: (input) => HttpClient.post(API_ENDPOINTS.NOA, input),
    pending: (creatorId) =>
      HttpClient.get(API_ENDPOINTS.NOA_PENDING(creatorId)),
    acknowledge: (input) =>
      HttpClient.post(API_ENDPOINTS.NOA_ACKNOWLEDGE, input),
    acknowledged: (creatorId) =>
      HttpClient.get(API_ENDPOINTS.NOA_ACKNOWLEDGED(creatorId)),
    send: (noaId, input = {}) =>
      HttpClient.post(API_ENDPOINTS.NOA_SEND(noaId), input),
    revoke: (noaId) => HttpClient.post(API_ENDPOINTS.NOA_REVOKE(noaId)),
    checkExists: (params) =>
      HttpClient.get(API_ENDPOINTS.NOA_CHECK, { params }),
    linkInvoice: (input) =>
      HttpClient.post(API_ENDPOINTS.NOA_LINK_INVOICE, input),

    // NEW: Public access methods (no auth required)
    viewPublic: (token, email) =>
      HttpClient.get(
        API_ENDPOINTS.NOA_PUBLIC_VIEW(token),
        { email }, // ✅ Direct query param
        { ignoreAuthError: true },
      ),

    acknowledgePublic: (token, email) =>
      HttpClient.post(
        API_ENDPOINTS.NOA_PUBLIC_ACKNOWLEDGE(token),
        { email },
        { ignoreAuthError: true },
      ),
  };

  // Add this to your main Client class (API/index.js)

  // Article API client - Add this section to your existing Client class
  articles = {
    // Public Routes (no auth required)
    list: (params) => HttpClient.get(API_ENDPOINTS.ARTICLES_LIST, params),
    getBySlug: (slug) => HttpClient.get(API_ENDPOINTS.ARTICLES_DETAIL(slug)),
    featured: (params) =>
      HttpClient.get(API_ENDPOINTS.ARTICLES_FEATURED, params),
    categories: () => HttpClient.get(API_ENDPOINTS.ARTICLES_CATEGORIES),
    tags: () => HttpClient.get(API_ENDPOINTS.ARTICLES_TAGS),

    // Admin Routes (require ADMIN_LEVEL auth)
    admin: {
      list: (params) =>
        HttpClient.get(API_ENDPOINTS.ADMIN_ARTICLES_LIST, params),
      getById: (id) => HttpClient.get(API_ENDPOINTS.ADMIN_ARTICLES_DETAIL(id)),
      create: (input) =>
        HttpClient.post(API_ENDPOINTS.ADMIN_ARTICLES_CREATE, input),
      update: (id, input) =>
        HttpClient.put(API_ENDPOINTS.ADMIN_ARTICLES_UPDATE(id), input),
      delete: (id) =>
        HttpClient.delete(API_ENDPOINTS.ADMIN_ARTICLES_DELETE(id)),
      publish: (id) =>
        HttpClient.post(API_ENDPOINTS.ADMIN_ARTICLES_PUBLISH(id)),
      unpublish: (id) =>
        HttpClient.post(API_ENDPOINTS.ADMIN_ARTICLES_UNPUBLISH(id)),
      duplicate: (id) =>
        HttpClient.post(API_ENDPOINTS.ADMIN_ARTICLES_DUPLICATE(id)),

      // Category management
      listCategories: (params) =>
        HttpClient.get(API_ENDPOINTS.ADMIN_CATEGORIES_LIST, params),
      createCategory: (input) =>
        HttpClient.post(API_ENDPOINTS.ADMIN_CATEGORIES_CREATE, input),
      updateCategory: (id, input) =>
        HttpClient.put(API_ENDPOINTS.ADMIN_CATEGORIES_UPDATE(id), input),
      deleteCategory: (id) =>
        HttpClient.delete(API_ENDPOINTS.ADMIN_CATEGORIES_DELETE(id)),
    },
  };

  blitzpayCustomInfo = {
    // =================== PUBLIC INVOICE ROUTES ===================

    // Get a single BlitzPay invoice with full custom info (public, no auth required)
    getInvoice: (invoiceId) =>
      HttpClient.get(API_ENDPOINTS.BLITZPAY_INVOICE(invoiceId)),

    // Get all BlitzPay invoices for a user (requires auth)
    getUserInvoices: (userId, params) =>
      HttpClient.get(API_ENDPOINTS.BLITZPAY_USER_INVOICES(userId), params),

    // =================== USER CUSTOM INFO ROUTES ===================

    // Get current user's BlitzPay custom info
    getMyCustomInfo: () => HttpClient.get(API_ENDPOINTS.BLITZPAY_CUSTOM_INFO),

    // Create or update current user's BlitzPay custom info
    createOrUpdateMyCustomInfo: (input) =>
      HttpClient.post(API_ENDPOINTS.BLITZPAY_CUSTOM_INFO, input),

    // Update current user's BlitzPay custom info (PUT method)
    updateMyCustomInfo: (input) =>
      HttpClient.put(API_ENDPOINTS.BLITZPAY_CUSTOM_INFO, input),

    // =================== ADMIN ROUTES ===================

    admin: {
      // List all custom info records with pagination and search
      list: (params) =>
        HttpClient.get(API_ENDPOINTS.ADMIN_BLITZPAY_CUSTOM_INFO, params),

      // Get specific custom info by ID
      getById: (customInfoId) =>
        HttpClient.get(
          API_ENDPOINTS.ADMIN_BLITZPAY_CUSTOM_INFO_DETAIL(customInfoId),
        ),

      // Get custom info by user ID
      getByUserId: (userId) =>
        HttpClient.get(
          API_ENDPOINTS.ADMIN_BLITZPAY_CUSTOM_INFO_BY_USER(userId),
        ),

      // Create custom info for a user
      create: (input) =>
        HttpClient.post(API_ENDPOINTS.ADMIN_BLITZPAY_CUSTOM_INFO, input),

      // Update custom info
      update: (customInfoId, input) =>
        HttpClient.put(
          API_ENDPOINTS.ADMIN_BLITZPAY_CUSTOM_INFO_UPDATE(customInfoId),
          input,
        ),

      // Delete custom info
      delete: (customInfoId) =>
        HttpClient.delete(
          API_ENDPOINTS.ADMIN_BLITZPAY_CUSTOM_INFO_DELETE(customInfoId),
        ),

      // Bulk import multiple custom info records
      bulkImport: (input) =>
        HttpClient.post(API_ENDPOINTS.ADMIN_BLITZPAY_CUSTOM_INFO_BULK, input),

      // Get statistics about custom info records
      stats: () =>
        HttpClient.get(API_ENDPOINTS.ADMIN_BLITZPAY_CUSTOM_INFO_STATS),
    },
  };

  amTracking = {
    // Get performance metrics for all account managers
    getMetrics: (params) =>
      HttpClient.get(API_ENDPOINTS.ADMIN_AM_METRICS, params),

    // Get detailed breakdown for a specific account manager
    getDetails: (userId, params) =>
      HttpClient.get(API_ENDPOINTS.ADMIN_AM_METRICS_SPEC(userId), params),
  };

  invoiceCollections = {
    // =================== COLLECTIONS OVERVIEW ===================

    // Get summary of all active collections
    getSummary: () => HttpClient.get(API_ENDPOINTS.COLLECTIONS_SUMMARY),

    // Get active collections with optional filters
    getActive: (params) =>
      HttpClient.get(API_ENDPOINTS.COLLECTIONS_ACTIVE, params),

    // Get collections for a specific company
    getByCompany: (companyId) =>
      HttpClient.get(API_ENDPOINTS.COLLECTIONS_COMPANY(companyId)),

    // Get detailed information about a specific collection
    getDetail: (ledgerId) =>
      HttpClient.get(API_ENDPOINTS.COLLECTIONS_DETAIL(ledgerId)),

    // Get collection by invoice ID
    getByInvoice: (invoiceId) =>
      HttpClient.get(API_ENDPOINTS.COLLECTIONS_BY_INVOICE(invoiceId)),

    // =================== COLLECTIONS ACTIONS ===================

    // Resolve a collection
    resolve: (ledgerId, data) =>
      HttpClient.post(API_ENDPOINTS.COLLECTIONS_RESOLVE(ledgerId), data),

    // Add contact note to a collection
    addContactNote: (ledgerId, data) =>
      HttpClient.post(API_ENDPOINTS.COLLECTIONS_CONTACT(ledgerId), data),

    // Bulk write-off collections
    bulkWriteOff: (data) =>
      HttpClient.post(API_ENDPOINTS.COLLECTIONS_BULK_WRITEOFF, data),

    // =================== COLLECTIONS REPORTS ===================

    // Get aging report
    getAgingReport: (params) =>
      HttpClient.get(API_ENDPOINTS.COLLECTIONS_AGING_REPORT, params),

    // Get top overdue collections
    getTopOverdue: (params) =>
      HttpClient.get(API_ENDPOINTS.COLLECTIONS_TOP_OVERDUE, params),

    // Get oldest overdue collections
    getOldestOverdue: (params) =>
      HttpClient.get(API_ENDPOINTS.COLLECTIONS_OLDEST_OVERDUE, params),

    // Get recent resolutions
    getRecentResolutions: (params) =>
      HttpClient.get(API_ENDPOINTS.COLLECTIONS_RECENT_RESOLUTIONS, params),

    manualSweep: () => HttpClient.post(API_ENDPOINTS.COLLECTIONS_MANUAL_SWEEP),

    // System Overview Metrics (cached for 2 hours on backend)
    systemOverview: {
      getMetrics: () => HttpClient.get(API_ENDPOINTS.SYSTEM_OVERVIEW_METRICS),
    },
  };

  userWallets = {
    // Get all wallets for authenticated user
    getWallets: () => HttpClient.get(API_ENDPOINTS.USER_WALLETS),

    // Create new wallet
    createWallet: (input) => HttpClient.post(API_ENDPOINTS.USER_WALLETS, input),

    // Update existing wallet
    updateWallet: (walletId, input) =>
      HttpClient.put(API_ENDPOINTS.USER_WALLET_DETAIL(walletId), input),

    // Delete wallet (soft delete)
    deleteWallet: (walletId) =>
      HttpClient.delete(API_ENDPOINTS.USER_WALLET_DETAIL(walletId)),

    // Set primary payment method
    setPrimaryWallet: (walletId) =>
      HttpClient.post(API_ENDPOINTS.USER_WALLET_SET_PRIMARY(walletId)),

    // Get tax information
    getTaxInfo: () => HttpClient.get(API_ENDPOINTS.USER_TAX_INFO),

    // Create or update tax information
    updateTaxInfo: (input) =>
      HttpClient.post(API_ENDPOINTS.USER_TAX_INFO, input),

    // PayPal OAuth - Login with PayPal for wallet verification
    paypalOAuth: {
      // Initiate PayPal OAuth flow - returns authorization URL
      initiate: (redirectPath) =>
        HttpClient.post(API_ENDPOINTS.PAYPAL_OAUTH_INITIATE, {
          redirect_path: redirectPath,
        }),

      // Check if user has verified PayPal wallet
      getStatus: () => HttpClient.get(API_ENDPOINTS.PAYPAL_OAUTH_STATUS),

      // Disconnect PayPal wallet
      disconnect: () => HttpClient.post(API_ENDPOINTS.PAYPAL_OAUTH_DISCONNECT),
    },

    // Stripe OAuth - Stripe Connect for wallet verification
    stripeOAuth: {
      // Initiate Stripe OAuth flow - returns authorization URL
      initiate: (redirectPath) =>
        HttpClient.post(API_ENDPOINTS.STRIPE_OAUTH_INITIATE, {
          redirect_path: redirectPath,
        }),

      // Check if user has verified Stripe wallet
      getStatus: () => HttpClient.get(API_ENDPOINTS.STRIPE_OAUTH_STATUS),

      // Disconnect Stripe wallet
      disconnect: () => HttpClient.post(API_ENDPOINTS.STRIPE_OAUTH_DISCONNECT),
    },
  };

  blitzAI = {
    // =================== PARTNER EMAIL MANAGEMENT ===================

    /**
     * Get comprehensive status of all Partner creators
     * @param {boolean} includeEmails - Include recent email counts (slower)
     * @returns {Promise<Object>} Partner status overview
     */
    getPartnerStatus: (includeEmails = false) =>
      HttpClient.get(API_ENDPOINTS.BLITZAI_PARTNER_STATUS, {
        include_emails: includeEmails,
      }),

    /**
     * Manually check emails without sending replies (Step 1)
     * @param {Object} params - Check parameters
     * @param {string[]} params.creator_usernames - Specific creators to check
     * @param {boolean} params.check_all - Check all partners
     * @param {number} params.days_back - How many days to scan (default 21)
     * @returns {Promise<Object>} Check results
     */
    checkEmails: (params = {}) =>
      HttpClient.post(API_ENDPOINTS.BLITZAI_CHECK_EMAILS, params),

    /**
     * Process emails and send auto-replies (Step 2)
     * @param {Object} params - Process parameters
     * @param {string[]} params.creator_usernames - Specific creators to process
     * @param {boolean} params.process_all - Process all partners
     * @param {boolean} params.dry_run - Test mode (no actual emails sent)
     * @returns {Promise<Object>} Process results
     */
    processEmails: (params = {}) =>
      HttpClient.post(API_ENDPOINTS.BLITZAI_PROCESS_EMAILS, params),

    /**
     * Process follow-up emails for existing threads
     * @param {Object} params - Followup parameters
     * @param {string[]} params.creator_usernames - Specific creators
     * @param {boolean} params.process_all - Process all partners
     * @param {boolean} params.dry_run - Test mode
     * @returns {Promise<Object>} Followup results
     */
    processFollowups: (params = {}) =>
      HttpClient.post(API_ENDPOINTS.BLITZAI_PROCESS_FOLLOWUPS, params),

    /**
     * Process forwarded emails from partnerships@ inbox
     * @param {Object} params - Forwarded email parameters
     * @param {boolean} params.dry_run - Test mode
     * @param {number} params.hours_back - How far back to scan (default 24)
     * @returns {Promise<Object>} Forwarded processing results
     */
    processForwarded: (params = {}) =>
      HttpClient.post(API_ENDPOINTS.BLITZAI_PROCESS_FORWARDED, params),

    // Helper methods for common use cases

    /**
     * Quick check for a single creator
     */
    checkSingleCreator: (username, daysBack = 21) =>
      HttpClient.post(API_ENDPOINTS.BLITZAI_CHECK_EMAILS, {
        creator_usernames: [username],
        days_back: daysBack,
      }),

    /**
     * Process all partners with dry run for testing
     */
    testProcessAll: () =>
      HttpClient.post(API_ENDPOINTS.BLITZAI_PROCESS_EMAILS, {
        process_all: true,
        dry_run: true,
      }),

    /**
     * Get partner health summary (lightweight)
     */
    getHealthSummary: () =>
      HttpClient.get(API_ENDPOINTS.BLITZAI_PARTNER_STATUS, {
        include_emails: false,
      }),

    getCreatorEmails: (username, params = {}) =>
      HttpClient.get(API_ENDPOINTS.BLITZAI_CREATOR_EMAILS(username), params),
    getClassificationStats: (hours = 24) =>
      HttpClient.get(API_ENDPOINTS.BLITZAI_CLASSIFICATION_STATS, { hours }),

    /**
     * Test email classification on arbitrary text
     * @param {Object} data - Test data
     * @param {string} data.email_content - Email body to classify
     * @param {string} data.subject - Optional subject line
     * @param {string} data.sender - Optional sender email
     * @returns {Promise<Object>} Classification result
     */
    testClassification: (data) =>
      HttpClient.post(API_ENDPOINTS.BLITZAI_TEST_CLASSIFY, data),

    /**
     * Manually reclassify a single email
     * @param {string} emailId - Gmail message ID
     * @param {Object} data - Reclassification data
     * @param {string} data.new_category - New category
     * @param {string} data.reason - Optional reason for change
     * @returns {Promise<Object>} Updated email info
     */
    reclassifyEmail: (emailId, data) =>
      HttpClient.post(API_ENDPOINTS.BLITZAI_RECLASSIFY_EMAIL(emailId), data),

    /**
     * Bulk reclassify emails matching filters
     * @param {Object} data - Bulk reclassification data
     * @param {Object} data.filters - Filter criteria
     * @param {string} data.new_category - New category for matches
     * @param {string} data.reason - Optional reason
     * @param {boolean} data.dry_run - Test mode (default true)
     * @returns {Promise<Object>} Bulk operation results
     */
    bulkReclassify: (data) =>
      HttpClient.post(API_ENDPOINTS.BLITZAI_BULK_RECLASSIFY, data),

    /**
     * Get classifier configuration
     * @returns {Promise<Object>} Current configuration
     */
    getClassifierConfig: () =>
      HttpClient.get(API_ENDPOINTS.BLITZAI_CLASSIFIER_CONFIG),

    /**
     * Update classifier configuration
     * @param {Object} config - New configuration values
     * @param {number} config.confidence_threshold - Threshold (0.0-1.0)
     * @param {boolean} config.enable_hybrid_classifier - Enable/disable hybrid
     * @param {number} config.timeout_seconds - API timeout
     * @returns {Promise<Object>} Updated configuration
     */
    updateClassifierConfig: (config) =>
      HttpClient.post(API_ENDPOINTS.BLITZAI_CLASSIFIER_CONFIG, config),
    debugEmailCheck: (username, daysBack = 7) =>
      HttpClient.get(API_ENDPOINTS.BLITZAI_EMAIL_DEBUG(username), {
        days_back: daysBack,
      }),
    toggleEmailHidden: (emailId, hidden = undefined) => {
      const body = hidden !== undefined ? { hidden } : {};
      return HttpClient.post(
        API_ENDPOINTS.BLITZAI_TOGGLE_EMAIL_HIDDEN(emailId),
        body,
      );
    },

    /**
     * Bulk hide/unhide emails based on filters
     * @param {Object} data - Bulk hide parameters
     * @param {Object} data.filters - Filter criteria
     * @param {string} data.filters.creator_id - Optional: Filter by creator
     * @param {string} data.filters.category - Optional: Filter by category
     * @param {string} data.filters.sender_pattern - Optional: SQL LIKE pattern for sender
     * @param {string} data.filters.subject_pattern - Optional: SQL LIKE pattern for subject
     * @param {string} data.filters.date_from - Optional: ISO datetime string
     * @param {string} data.filters.date_to - Optional: ISO datetime string
     * @param {boolean} data.hidden - Set hidden state (true/false)
     * @param {boolean} data.dry_run - Optional: Test mode (default true)
     * @returns {Promise<Object>} Bulk operation results
     */
    bulkHideEmails: (data) =>
      HttpClient.post(API_ENDPOINTS.BLITZAI_BULK_HIDE_EMAILS, data),

    // Helper method for quick single email hide
    hideEmail: (emailId) =>
      HttpClient.post(API_ENDPOINTS.BLITZAI_TOGGLE_EMAIL_HIDDEN(emailId), {
        hidden: true,
      }),

    // Helper method for quick single email unhide
    unhideEmail: (emailId) =>
      HttpClient.post(API_ENDPOINTS.BLITZAI_TOGGLE_EMAIL_HIDDEN(emailId), {
        hidden: false,
      }),

    // Helper method for bulk hide by category
    bulkHideByCategory: (category, creatorId = null, dryRun = true) =>
      HttpClient.post(API_ENDPOINTS.BLITZAI_BULK_HIDE_EMAILS, {
        filters: {
          category,
          ...(creatorId && { creator_id: creatorId }),
        },
        hidden: true,
        dry_run: dryRun,
      }),

    // Helper method for bulk unhide by category
    bulkUnhideByCategory: (category, creatorId = null, dryRun = true) =>
      HttpClient.post(API_ENDPOINTS.BLITZAI_BULK_HIDE_EMAILS, {
        filters: {
          category,
          ...(creatorId && { creator_id: creatorId }),
        },
        hidden: false,
        dry_run: dryRun,
      }),

    getEmailThread: (emailId, includeGmail = true) =>
      HttpClient.get(API_ENDPOINTS.BLITZAI_EMAIL_THREAD(emailId), {
        include_gmail: includeGmail,
      }),

    /**
     * Get all email threads for a creator
     * @param {string} username - Creator username
     * @param {Object} params - Query parameters
     * @param {number} params.page - Page number (default 0)
     * @param {number} params.rows - Rows per page (default 20)
     * @param {string} params.category - Filter by category
     * @param {string} params.has_replies - Filter by reply status ('true'/'false')
     * @param {string} params.reply_type - Filter by reply type
     * @returns {Promise<Object>} Threads list with pagination
     */
    getCreatorThreads: (username, params = {}) =>
      HttpClient.get(API_ENDPOINTS.BLITZAI_CREATOR_THREADS(username), params),

    /**
     * Get reply statistics
     * @param {string} creatorUsername - Optional: Filter to specific creator
     * @param {number} days - Time window (default 7)
     * @returns {Promise<Object>} Reply statistics and breakdowns
     */
    getReplyStats: (creatorUsername = null, days = 7) =>
      HttpClient.get(API_ENDPOINTS.BLITZAI_REPLY_STATS, {
        creator_username: creatorUsername,
        days,
      }),

    manuallyReclassifyEmail: (emailId, data) =>
      HttpClient.post(API_ENDPOINTS.BLITZAI_RECLASSIFY_EMAIL_V2(emailId), data),

    getReclassificationHistory: (emailId) =>
      HttpClient.get(API_ENDPOINTS.BLITZAI_RECLASSIFICATION_HISTORY(emailId)),
  };

  // =================== BUBBLES (COMMUNITIES) ===================
  bubbles = {
    // =================== CORE OPERATIONS ===================

    // List all bubbles user is a member of
    list: () => HttpClient.get(API_ENDPOINTS.BUBBLES_LIST),

    // Create new bubble with conversation and organizer
    create: (input) => HttpClient.post(API_ENDPOINTS.BUBBLES_CREATE, input),

    // Get bubble details with organizers
    get: (id) => HttpClient.get(API_ENDPOINTS.BUBBLE_DETAIL(id)),

    // Update bubble basic info
    update: (id, input) =>
      HttpClient.put(API_ENDPOINTS.BUBBLE_UPDATE(id), input),

    // Delete bubble (soft delete)
    delete: (id) => HttpClient.delete(API_ENDPOINTS.BUBBLE_DELETE(id)),

    // =================== MEMBERSHIP MANAGEMENT ===================

    // Join a public bubble
    join: (id) => HttpClient.post(API_ENDPOINTS.BUBBLE_JOIN(id)),

    // Leave a bubble
    leave: (id) => HttpClient.post(API_ENDPOINTS.BUBBLE_LEAVE(id)),

    // Add member to bubble (admin/host only)
    addMember: (id, input) =>
      HttpClient.post(API_ENDPOINTS.BUBBLE_ADD_MEMBER(id), input),

    // Remove member from bubble (admin/host only)
    removeMember: (id, memberGlobalUserId) =>
      HttpClient.delete(
        API_ENDPOINTS.BUBBLE_REMOVE_MEMBER(id, memberGlobalUserId),
      ),

    // Check detailed membership status (debug/admin)
    getMembershipStatus: (id) =>
      HttpClient.get(API_ENDPOINTS.BUBBLE_MEMBERSHIP_STATUS(id)),

    // =================== ADMIN MANAGEMENT ===================

    // Promote member to admin (host only)
    addAdmin: (id, adminGlobalUserId) =>
      HttpClient.post(API_ENDPOINTS.BUBBLE_ADD_ADMIN(id, adminGlobalUserId)),

    // Remove admin privileges (host only)
    removeAdmin: (id, adminGlobalUserId) =>
      HttpClient.delete(
        API_ENDPOINTS.BUBBLE_REMOVE_ADMIN(id, adminGlobalUserId),
      ),

    // =================== SETTINGS & CONFIGURATION ===================

    // Update bubble settings (admin/host only)
    updateSettings: (id, data) =>
      HttpClient.put(API_ENDPOINTS.BUBBLE_SETTINGS(id), data),

    // =================== FEED & CONTENT ===================

    // Get bubble feed (posts + casting calls)
    getFeed: (id, params) =>
      HttpClient.get(API_ENDPOINTS.BUBBLE_FEED(id), params),

    // Create post in bubble
    createPost: (id, input) =>
      HttpClient.post(API_ENDPOINTS.BUBBLE_CREATE_POST(id), input),

    // =================== DISCOVERY ===================

    // Discover public bubbles
    discover: (params) =>
      HttpClient.get(API_ENDPOINTS.BUBBLES_DISCOVER, params),

    // Get user's bubble summary
    getUserSummary: (userId) =>
      HttpClient.get(API_ENDPOINTS.BUBBLE_USER_SUMMARY(userId)),

    // =================== CONVERSATION ===================

    // Get bubble conversation
    getConversation: (id) =>
      HttpClient.get(API_ENDPOINTS.BUBBLE_CONVERSATION(id)),

    // Create conversation for bubble (if doesn't exist)
    createConversation: (id) =>
      HttpClient.post(API_ENDPOINTS.BUBBLE_CONVERSATION(id)),

    // Get conversation participants
    getConversationParticipants: (id) =>
      HttpClient.get(API_ENDPOINTS.BUBBLE_CONVERSATION_PARTICIPANTS(id)),

    // =================== ORGANIZERS (FILE MANAGEMENT) ===================

    // List organizers for bubble
    getOrganizers: (bubbleId) =>
      HttpClient.get(API_ENDPOINTS.BUBBLE_ORGANIZERS_LIST(bubbleId)),
    // Create new organizer in bubble (admin only)
    createOrganizer: (id, input) =>
      HttpClient.post(API_ENDPOINTS.BUBBLE_ORGANIZERS_CREATE(id), input),

    // Update organizer link settings (admin only)
    updateOrganizerSettings: (id, organizerId, data) =>
      HttpClient.put(
        API_ENDPOINTS.BUBBLE_ORGANIZER_SETTINGS(id, organizerId),
        data,
      ),

    // Remove organizer from bubble (admin only)
    removeOrganizer: (id, organizerId) =>
      HttpClient.delete(API_ENDPOINTS.BUBBLE_ORGANIZER_REMOVE(id, organizerId)),

    // =================== PUBLIC ACCESS (NO AUTH) ===================

    // Get public bubble details
    getPublic: (id) =>
      HttpClient.get(API_ENDPOINTS.BUBBLE_PUBLIC_DETAIL(id), null, {
        ignoreAuthError: true,
      }),

    // Get public bubble feed preview
    getFeedPublic: (id, params) =>
      HttpClient.get(API_ENDPOINTS.BUBBLE_FEED_PUBLIC(id), params, {
        ignoreAuthError: true,
      }),

    // Get public bubble assets preview
    getAssetsPublic: (id) =>
      HttpClient.get(API_ENDPOINTS.BUBBLE_PUBLIC_ASSETS(id), null, {
        ignoreAuthError: true,
      }),

    shareOrganizerWithMembers: (bubbleId, organizerId, data) =>
      HttpClient.post(
        API_ENDPOINTS.BUBBLE_ORGANIZER_SHARE(bubbleId, organizerId),
        data,
      ),

    getOrganizerShares: (bubbleId, organizerId) =>
      HttpClient.get(
        API_ENDPOINTS.BUBBLE_ORGANIZER_SHARES(bubbleId, organizerId),
      ),
  };

  // Add this section to your Client class in API/index.js
  // In your client object
  spendingPowerLedger = {
    getHistory: () => HttpClient.get(API_ENDPOINTS.BLITZ_SPENDINGPOWER_HISTORY),
  };

  blitzpayRepayments = {
    // =================== BLITZPAY INVOICE REPAYMENT SYSTEM ===================

    /**
     * Process single BlitzPay invoice repayment
     * @param {Object} data - Repayment data
     * @param {number} data.invoice_id - Invoice ID to process
     * @param {number} data.payment_amount - Amount being repaid
     * @param {string} data.external_transaction_id - Optional external transaction ID
     * @param {string} data.payment_method - Payment method (stripe, ach, wire, check, manual)
     * @param {string} data.notes - Optional notes about the repayment
     * @returns {Promise<Object>} Repayment result with spending status
     */
    processRepayment: (data) =>
      HttpClient.post(API_ENDPOINTS.BLITZPAY_REPAYMENTS_PROCESS, data),

    /**
     * Process multiple BlitzPay repayments in batch
     * @param {Object} data - Batch repayment data
     * @param {Array} data.repayments - Array of repayment objects
     * @returns {Promise<Object>} Batch processing results
     */
    processBatch: (data) =>
      HttpClient.post(API_ENDPOINTS.BLITZPAY_REPAYMENTS_BATCH, data),

    /**
     * Validate if invoice is eligible for repayment
     * @param {number} invoiceId - Invoice ID to validate
     * @returns {Promise<Object>} Validation result with eligibility info
     */
    validateRepayment: (invoiceId) =>
      HttpClient.get(API_ENDPOINTS.BLITZPAY_REPAYMENTS_VALIDATE(invoiceId)),

    /**
     * Get all pending BlitzPay repayments
     * @param {number} companyId - Optional company ID filter
     * @returns {Promise<Object>} Pending repayments grouped by company/team
     */
    getPending: (companyId = null) => {
      const params = companyId ? { company_id: companyId } : {};
      return HttpClient.get(API_ENDPOINTS.BLITZPAY_REPAYMENTS_PENDING, params);
    },

    /**
     * Helper: Check if invoice can be repaid
     * @param {Object} invoice - Invoice object
     * @returns {boolean} True if eligible for repayment
     */
    isEligible: (invoice) => {
      return (
        invoice.invoice_type === "blitzpay" &&
        invoice.status === "funded" &&
        invoice.spending_ledger_id != null
      );
    },

    /**
     * Helper: Get payment method options
     * @returns {Array<Object>} Payment method options for dropdowns
     */
    getPaymentMethods: () => [
      { value: "stripe", label: "Stripe" },
      { value: "ach", label: "ACH Transfer" },
      { value: "wire", label: "Wire Transfer" },
      { value: "check", label: "Check" },
      { value: "manual", label: "Manual" },
      { value: "unknown", label: "Other" },
    ],
  };

  blitzpayProfiles = {
    // =================== ADMIN BLITZPAY PROFILE MANAGEMENT ===================

    /**
     * List all BlitzPay company profiles
     * @param {Object} params - Query parameters
     * @param {string} params.search - Search by company name or email
     * @param {number} params.limit - Results per page (default 50)
     * @param {number} params.offset - Pagination offset (default 0)
     * @param {boolean} params.active_only - Filter to active profiles only (default true)
     */
    list: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.ADMIN_BLITZPAY_PROFILES, params),

    /**
     * Get BlitzPay profile for specific user
     * @param {number} userId - User ID
     */
    get: (userId) =>
      HttpClient.get(API_ENDPOINTS.ADMIN_BLITZPAY_PROFILE_DETAIL(userId)),

    /**
     * Update BlitzPay profile (creates if doesn't exist)
     * @param {number} userId - User ID
     * @param {Object} data - Profile data
     */
    update: (userId, data) =>
      HttpClient.put(API_ENDPOINTS.ADMIN_BLITZPAY_PROFILE_DETAIL(userId), data),

    /**
     * Deactivate BlitzPay profile
     * @param {number} userId - User ID
     */
    delete: (userId) =>
      HttpClient.delete(API_ENDPOINTS.ADMIN_BLITZPAY_PROFILE_DETAIL(userId)),

    /**
     * Create default BlitzPay profile for user
     * @param {Object} data - { user_id, company_name?, email? }
     */
    createDefault: (data) =>
      HttpClient.post(
        API_ENDPOINTS.ADMIN_BLITZPAY_PROFILE_CREATE_DEFAULT,
        data,
      ),

    // Helper methods

    /**
     * Get profile for current invoice's user
     * @param {Object} invoice - Invoice object
     */
    getForInvoice: (invoice) => {
      if (!invoice.user_id) {
        throw new Error("Invoice has no user_id");
      }
      return HttpClient.get(
        API_ENDPOINTS.ADMIN_BLITZPAY_PROFILE_DETAIL(invoice.user_id),
      );
    },

    /**
     * Update profile with partial data
     * @param {number} userId - User ID
     * @param {Object} updates - Partial profile data
     */
    updatePartial: async (userId, updates) => {
      // Get existing profile first
      const response = await HttpClient.get(
        API_ENDPOINTS.ADMIN_BLITZPAY_PROFILE_DETAIL(userId),
      );
      const existing = response.profile || {};

      // Merge with updates
      const merged = { ...existing, ...updates };

      // Update
      return HttpClient.put(
        API_ENDPOINTS.ADMIN_BLITZPAY_PROFILE_DETAIL(userId),
        merged,
      );
    },
  };

  // =================== USDC SETTLEMENT ===================
  usdc = {
    getMyDepositStatus: () => HttpClient.get("/api/usdc/my-deposit-status"),
  };

  clientGroups = {
    // ── Groups ──────────────────────────────────────────────────────────────────

    /** List all non-archived groups owned by or shared with the current user.
     *  @param {{ include_archived?: boolean, include_members?: boolean }} params */
    list: (params = {}) => HttpClient.get(API_ENDPOINTS.CLIENT_GROUPS, params),

    /** Full nested tree of accessible groups (no archived). */
    tree: () => HttpClient.get(API_ENDPOINTS.CLIENT_GROUPS_TREE),

    /** Create a new group.
     *  @param {{ name: string, description?: string, color?: string, icon?: string, parent_id?: number }} data */
    create: (data) => HttpClient.post(API_ENDPOINTS.CLIENT_GROUPS, data),

    /** Get one group with immediate children + members. */
    get: (groupId) => HttpClient.get(API_ENDPOINTS.CLIENT_GROUP(groupId)),

    /** Update name / description / color / icon / parent_id / archived.
     *  @param {number} groupId
     *  @param {object} data */
    update: (groupId, data) =>
      HttpClient.put(API_ENDPOINTS.CLIENT_GROUP(groupId), data),

    /** Soft-archive (default) or hard-delete (?hard=true) a group.
     *  @param {number} groupId
     *  @param {boolean} [hard=false] permanent delete */
    delete: (groupId, hard = false) =>
      HttpClient.delete(
        hard
          ? `${API_ENDPOINTS.CLIENT_GROUP(groupId)}?hard=true`
          : API_ENDPOINTS.CLIENT_GROUP(groupId),
      ),

    // ── Sharing ─────────────────────────────────────────────────────────────────

    /** Share a group with other supervisors in the same company.
     *  @param {number} groupId
     *  @param {{ user_ids: number[] }} data */
    share: (groupId, data) =>
      HttpClient.post(API_ENDPOINTS.CLIENT_GROUP_SHARE(groupId), data),

    /** Revoke shared access for specified users.
     *  @param {number} groupId
     *  @param {{ user_ids: number[] }} data */
    unshare: (groupId, data) =>
      HttpClient.post(API_ENDPOINTS.CLIENT_GROUP_UNSHARE(groupId), data),

    // ── Members ─────────────────────────────────────────────────────────────────

    /** Add clients to a group.
     *  @param {number} groupId
     *  @param {{ client_ids: number[] }} data */
    addMembers: (groupId, data) =>
      HttpClient.post(API_ENDPOINTS.CLIENT_GROUP_MEMBERS(groupId), data),

    /** Remove a single client from a group. */
    removeMember: (groupId, clientId) =>
      HttpClient.delete(API_ENDPOINTS.CLIENT_GROUP_MEMBER(groupId, clientId)),

    getShareableTeamMembers: () =>
      HttpClient.get(API_ENDPOINTS.SUPERVISOR_TEAM_MEMBERS),

    bulkShareClients: (payload) =>
      HttpClient.post(API_ENDPOINTS.BULK_SHARE_CLIENTS, payload),
  };

  clientReminders = {
    // ── CRUD ────────────────────────────────────────────────────────────────────

    /** List reminders for the current user.
     *  @param {{ status?: string, client_id?: number, creator_id?: string, include_assigned?: boolean }} params */
    list: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.CLIENT_REMINDERS, params),

    /** Reminders due right now (remind_at <= now, status = pending).
     *  Useful for polling / badge counts. */
    due: () => HttpClient.get(API_ENDPOINTS.CLIENT_REMINDERS_DUE),

    /** Create a reminder.
     *  Required: title, remind_at, and exactly one of client_id | creator_id.
     *  @param {{ title: string, remind_at: string, client_id?: number, creator_id?: string,
     *             body?: string, repeat?: string, repeat_interval?: number,
     *             repeat_until?: string, assigned_to?: number, notify_via?: string[] }} data */
    create: (data) => HttpClient.post(API_ENDPOINTS.CLIENT_REMINDERS, data),

    /** Update any mutable field of a reminder. */
    update: (reminderId, data) =>
      HttpClient.put(API_ENDPOINTS.CLIENT_REMINDER(reminderId), data),

    /** Permanently delete a reminder (author only). */
    delete: (reminderId) =>
      HttpClient.delete(API_ENDPOINTS.CLIENT_REMINDER(reminderId)),

    // ── Actions ─────────────────────────────────────────────────────────────────

    /** Snooze a reminder until a given datetime.
     *  @param {number} reminderId
     *  @param {{ until: string }} data  ISO-8601 datetime */
    snooze: (reminderId, data) =>
      HttpClient.post(API_ENDPOINTS.CLIENT_REMINDER_SNOOZE(reminderId), data),

    /** Mark a reminder as done.
     *  If the reminder repeats, a new one is auto-spawned.
     *  Returns { completed: Reminder, next: Reminder | null } */
    complete: (reminderId) =>
      HttpClient.post(API_ENDPOINTS.CLIENT_REMINDER_COMPLETE(reminderId)),
  };
  todos = {
    // ── Per-client todo lists ─────────────────────────────────────────────────

    /**
     * List todos for a client.
     * @param {number} clientId
     * @param {{ completed?: 'true'|'false', priority?: string, overdue?: 'true' }} params
     */
    list: (clientId, params = {}) =>
      HttpClient.get(API_ENDPOINTS.CLIENT_TODOS(clientId), params),

    /**
     * Create a todo attached to a client.
     * @param {number} clientId
     * @param {{ title: string, priority?: string, due_date?: string,
     *            body?: string, assigned_to?: number, note_id?: number,
     *            position?: number }} data
     */
    create: (clientId, data) =>
      HttpClient.post(API_ENDPOINTS.CLIENT_TODOS(clientId), data),

    /**
     * Update any mutable field of a todo.
     * @param {number} todoId
     * @param {object} data
     */
    update: (todoId, data) =>
      HttpClient.put(API_ENDPOINTS.CLIENT_TODO(todoId), data),

    /**
     * Toggle completed status of a todo.
     * Returns the updated todo with new completed / status fields.
     * @param {number} todoId
     */
    toggle: (todoId) =>
      HttpClient.post(API_ENDPOINTS.CLIENT_TODO_TOGGLE(todoId)),

    /**
     * Permanently delete a todo (author only).
     * @param {number} todoId
     */
    delete: (todoId) => HttpClient.delete(API_ENDPOINTS.CLIENT_TODO(todoId)),

    /**
     * Batch reorder todos via drag-and-drop.
     * @param {number} clientId
     * @param {{ order: number[] }} data  IDs in desired display order
     */
    reorder: (clientId, data) =>
      HttpClient.post(API_ENDPOINTS.CLIENT_TODO_REORDER(clientId), data),

    /**
     * Compact stats for a client's todo list — used on cards.
     * Returns { total, open, done, overdue, high_priority }
     * @param {number} clientId
     */
    stats: (clientId) =>
      HttpClient.get(API_ENDPOINTS.CLIENT_TODO_STATS(clientId)),
  };

  amRanking = {
    // ── Personal AM priority ranking ──────────────────────────────────────────

    /**
     * Fetch the current user's ranked client list in rank_position order.
     * Each entry includes a lightweight client snapshot + todo_stats.
     * Returns { ranked: RankedClient[] }
     */
    get: () => HttpClient.get(API_ENDPOINTS.AM_RANKING),

    /**
     * Add a client to the current user's ranked list.
     * Idempotent — if the client is already ranked, returns the existing entry.
     * @param {{ client_id: number, position?: number }} data
     *   position is optional; omit to append at the end.
     */
    add: (data) => HttpClient.post(API_ENDPOINTS.AM_RANKING_ADD, data),

    /**
     * Remove a client from the current user's ranked list.
     * Positions above the removed entry are compacted automatically.
     * @param {number} clientId
     */
    remove: (clientId) =>
      HttpClient.delete(API_ENDPOINTS.AM_RANKING_REMOVE(clientId)),

    /**
     * Full reorder after a drag-and-drop interaction.
     * Clients not present in the order array stay at the tail of the list.
     * @param {{ order: number[] }} data  Client IDs in desired priority order
     */
    reorder: (data) => HttpClient.post(API_ENDPOINTS.AM_RANKING_REORDER, data),

     setBAPType: (clientId, bapType) =>
      HttpClient.put(API_ENDPOINTS.CLIENT_BAP(clientId), { bap_type: bapType }),
 
    /**
     * Fetch all BAP-enrolled clients visible to the caller.
     * @param {{ bap_type?: string, include_stats?: boolean }} params
     * @returns {Promise<{ clients: ClientLightweight[], counts: BAPCounts }>}
     */
    getBAPClients: (params = {}) =>
      HttpClient.get(API_ENDPOINTS.SUPERVISOR_BAP_CLIENTS, params),
 
    /**
     * Lightweight tier-count summary — used to badge the ⚡ BAP tab.
     * @returns {Promise<{ Creator: number, Agency: number, Brand: number, total: number }>}
     */
    getBAPSummary: () =>
      HttpClient.get(API_ENDPOINTS.SUPERVISOR_BAP_SUMMARY),

        // inside amRanking = { ... }
    bapLinks: {
      list:   (bapClientId) =>
        HttpClient.get(API_ENDPOINTS.BAP_CLIENT_LINKS(bapClientId)),

      add:    (bapClientId, payload) =>
        HttpClient.post(API_ENDPOINTS.BAP_CLIENT_LINKS(bapClientId), payload),
      // payload: { client_ids: number[], relationship_type?: string, notes?: string }

      remove: (bapClientId, linkedClientId) =>
        HttpClient.delete(API_ENDPOINTS.BAP_CLIENT_LINK_REMOVE(bapClientId, linkedClientId)),

      update: (linkId, payload) =>
        HttpClient.put(API_ENDPOINTS.BAP_LINK_UPDATE(linkId), payload),
      // payload: { relationship_type?: string | null, notes?: string | null }

      move:   (destBapClientId, linkedClientId) =>
        HttpClient.post(API_ENDPOINTS.BAP_CLIENT_LINK_MOVE(destBapClientId), {
          linked_client_id: linkedClientId,
        }),

      bulkCounts: () =>
        HttpClient.get(API_ENDPOINTS.BAP_LINKED_COUNTS),
    },
  };

 
bbc = {
  // ── Create ────────────────────────────────────────────────────────────
  create: (data) => HttpClient.post(API_ENDPOINTS.BBC_CREATE, data),
 
  // ── Creator incoming ──────────────────────────────────────────────────
  getIncoming: () => HttpClient.get(API_ENDPOINTS.BBC_INCOMING),
 
  // ── Decision (creator-elected OR sme-elected based on who calls it) ──
  decide: (paymentId, decision) =>
    HttpClient.post(API_ENDPOINTS.BBC_PAYMENT_DECISION(paymentId), { decision }),
  claimInstant: (paymentId) =>
    HttpClient.post(API_ENDPOINTS.BBC_PAYMENT_DECISION(paymentId), {
      decision: 'instant',
    }),
  electWait: (paymentId) =>
    HttpClient.post(API_ENDPOINTS.BBC_PAYMENT_DECISION(paymentId), {
      decision: 'wait',
    }),
 
  // ── Public claim page — token recipients, no auth ────────────────────
  getByToken: (token) =>
    HttpClient.get(API_ENDPOINTS.BBC_PAYMENT_BY_TOKEN(token), null, {
      ignoreAuthError: true,
    }),
 
  // ── Admin: enterprise money arrived ───────────────────────────────────
  markFunded: (bbcId, externalTransactionId) =>
    HttpClient.post(API_ENDPOINTS.BBC_MARK_FUNDED(bbcId), {
      external_transaction_id: externalTransactionId,
    }),
 
  // ── Split directory ───────────────────────────────────────────────────
  searchCompanies: (search, limit = 20) =>
    HttpClient.get(API_ENDPOINTS.BBC_COMPANIES, { search, limit }),
  companyContacts: (companyId) =>
    HttpClient.get(API_ENDPOINTS.BBC_COMPANY_CONTACTS(companyId)),
 
  // ── Bulk instant conversion (all promised rows, fee from caller) ──────
  convertToBlitzPay: (bbcId) =>
    HttpClient.post(API_ENDPOINTS.BBC_CONVERT_TO_BLITZPAY(bbcId)),
 
  // ─────────────────────────────────────────────────────────────────────
  // NEW — roster management (BlitzSplitManageDialog)
  // ─────────────────────────────────────────────────────────────────────
 
  /**
   * Save all roster edits from the manage dialog in one call.
   * Handles: amount changes, pre-accept toggles, new contractors (local_ ids),
   * and removed contractors (absent from the array).
   *
   * @param {number} bbcId
   * @param {{ payments: Array }} payload
   *   payments[].id           — existing DB id or "local_xxx" for new rows
   *   payments[].amount       — updated gross amount
   *   payments[].preapproved  — instant pre-election flag
   *   payments[].recipient_email
   *   payments[].recipient_name
   *   payments[].creator_id
   */
  updatePayments: (bbcId, payload) =>
    HttpClient.put(API_ENDPOINTS.BBC_UPDATE_PAYMENTS(bbcId), payload),
 
  /**
   * SME draws some or all of their margin to their Blitz balance.
   * Only available once invoice.status in ('paid', 'funded').
   *
   * @param {number} bbcId
   * @param {{ amount: number }} payload
   */
  withdrawMargin: (bbcId, payload) =>
    HttpClient.post(API_ENDPOINTS.BBC_WITHDRAW_MARGIN(bbcId), payload),
 
  /**
   * Cancel a single 'promised' contractor row.
   * Called by RemoveConfirmDialog when the invoice is still pending/approved.
   * On approved invoices this is irreversible — the UI shows the warning.
   *
   * @param {number} paymentId
   */
  cancelPayment: (paymentId) =>
    HttpClient.post(API_ENDPOINTS.BBC_CANCEL_PAYMENT(paymentId)),
 
  /**
   * Lightweight remaining-due balance for the manage dialog's ledger guard.
   * Returns { bbc_id, remaining, remaining_after }.
   * Uses the existing /remaining endpoint; the frontend only needs
   * `remaining_after` from the response.
   *
   * @param {number} bbcId
   */
  getLedgerRemaining: (bbcId) =>
    HttpClient.get(API_ENDPOINTS.BBC_REMAINING(bbcId)),
};

  commerce = {
    listProducts: () => HttpClient.get(API_ENDPOINTS.COMMERCE_PRODUCTS),
    createProduct: (payload) =>
      HttpClient.post(API_ENDPOINTS.COMMERCE_PRODUCTS, payload),
    fetchMetrics: () => HttpClient.get(API_ENDPOINTS.COMMERCE_METRICS),
    fetchPurchasers: () => HttpClient.get(API_ENDPOINTS.COMMERCE_PURCHASERS),
    fetchReturns: () => HttpClient.get(API_ENDPOINTS.COMMERCE_RETURNS),
    // Bulk-imports historical orders from an external store (e.g. a
    // WooCommerce sales export) so they show up in Blitz Commerce metrics
    // and CRM purchase history. Must NOT trigger live ledger crediting —
    // see docs/cummerce/crm-commerce-mapping.md.
    importSalesHistory: (payload) =>
      HttpClient.post(API_ENDPOINTS.COMMERCE_ORDERS_IMPORT, payload),

    // Public (no auth) — used by the hosted /checkout/:slug checkout page.
    fetchPublicProduct: (slug) =>
      HttpClient.get(API_ENDPOINTS.COMMERCE_PRODUCT_PUBLIC(slug), null, {
        ignoreAuthError: true,
      }),
    // Mints a fresh single-use T2A pay-request for this product's price on
    // the merchant's behalf (server resolves owner_global_user_id, buyer
    // never sees or supplies it) and returns { token } synchronously so we
    // can redirect straight to /pay-request/:token. NOT a passthrough of
    // the authenticated /gu-balance/request-transfer route — see the
    // EXPECTED API SHAPES note in ProductManagement.js for why.
    requestProductPayment: (slug, payload) =>
      HttpClient.post(
        API_ENDPOINTS.COMMERCE_PRODUCT_CHECKOUT(slug),
        payload,
        { ignoreAuthError: true },
      ),

    // ── Third-party integration: hosted embedded-checkout page (public) ────
    // Powers /commerce/pay/:sessionId/:token — a third-party platform's
    // customer, redirected here after their backend called
    // POST /thirdparty/commerce/checkout. Authorization is the token itself
    // (never a JWT, never the merchant's API key) — see
    // blitz-backend/docs/commerce/README.md §2a.
    getCheckoutSession: (sessionId, token) =>
      HttpClient.get(API_ENDPOINTS.COMMERCE_CHECKOUT_SESSION(sessionId, token), null, {
        ignoreAuthError: true,
      }),
    // Lazily creates the Stripe embedded Checkout Session and returns
    // { client_secret } for <EmbeddedCheckout /> to mount.
    startEmbeddedCheckout: (sessionId, token) =>
      HttpClient.post(
        API_ENDPOINTS.COMMERCE_CHECKOUT_EMBED(sessionId, token),
        {},
        { ignoreAuthError: true },
      ),

    // ── Developer portal ────────────────────────────────────────────────────
    // Merchant resolves their integration first; keys/webhooks are scoped to it.
    listIntegrations: () => HttpClient.get(API_ENDPOINTS.COMMERCE_INTEGRATIONS),
    // Returns the single active key in full ({ api_key, key }) for copy/paste.
    revealApiKey: (integrationId) =>
      HttpClient.get(API_ENDPOINTS.COMMERCE_KEY_REVEAL, {
        integration_id: integrationId,
      }),
    // Issues a new key and revokes the previous one (single-key semantics).
    rerollApiKey: (integrationId) =>
      HttpClient.post(API_ENDPOINTS.COMMERCE_KEY_ROTATE, {
        integration_id: integrationId,
      }),

    // Webhooks
    getWebhook: (integrationId) =>
      HttpClient.get(API_ENDPOINTS.COMMERCE_WEBHOOK, {
        integration_id: integrationId,
      }),
    // payload: { integration_id, url } — url must be HTTPS (backend enforces).
    setWebhook: (payload) =>
      HttpClient.put(API_ENDPOINTS.COMMERCE_WEBHOOK, payload),
    enableWebhook: (integrationId) =>
      HttpClient.post(API_ENDPOINTS.COMMERCE_WEBHOOK_ENABLE, {
        integration_id: integrationId,
      }),
    disableWebhook: (integrationId) =>
      HttpClient.post(API_ENDPOINTS.COMMERCE_WEBHOOK_DISABLE, {
        integration_id: integrationId,
      }),
    rotateWebhookSecret: (integrationId) =>
      HttpClient.post(API_ENDPOINTS.COMMERCE_WEBHOOK_ROTATE_SECRET, {
        integration_id: integrationId,
      }),
    // Re-fetch the current signing secret in full (owner-only) so it stays retrievable.
    revealWebhookSecret: (integrationId) =>
      HttpClient.get(API_ENDPOINTS.COMMERCE_WEBHOOK_REVEAL_SECRET, {
        integration_id: integrationId,
      }),

    // Admin-only: onboard a business user to commerce by email.
    // payload: { email, name?, fee_percentage? }
    adminInitialize: (payload) =>
      HttpClient.post(API_ENDPOINTS.COMMERCE_ADMIN_INIT, payload),
  };


}

export default new Client();
